'use strict';

var CREATOR_COACH = 1;
var CREATOR_ATHLETE = 2;
var CREATOR_GROUP = 3;

import React from 'react'
import TimezoneSelect from "react-timezone-select"
import DatePicker, { registerLocale } from 'react-datepicker'
import enGB from 'date-fns/locale/en-GB';
import moment from 'moment'
import { Transition, TransitionGroup } from 'react-transition-group';
import get_text_as_jsx from '../common/get_text_as_jsx.jsx'
import activateConfirmationBox from './confirmation-box.jsx'
import { StructuredTrainingButton, StructuredTrainingSection, StructuredWorkoutEditor } from './structured-workout.jsx'
import { ResizableTextareaDraftJS } from './resizable-textarea.jsx'
import PropTypes from 'prop-types'
import { handleAjaxErrors, handleLoggedOutError } from '../common/ajax-error-handlers.jsx'
import { logError, logMessage } from '../common/logger.js'
import { can_edit_training_plans_on_date, DISCIPLINE_DIARY_ENTRY } from '../models/training-sessions.js'
import { COLOURS } from '../common/constants.js'
import { RaceStage, RACE_STAGE_DISCIPLINES } from '../models/races.js'
import {
  hasBetaAccess,
  newDateFromDatestring,
  removeURLParameter,
  kilometersToMiles,
  decimalPlaces,
  metersPerSecondAsSecondsPerMile,
  metersPerSecondAsSecondsPerKilometer,
  meanWeightedByDuration,
  formatTime,
  formatDistance,
  addToArrayIfNotExists,
  removeFromArrayIfExists,
} from '../common/helpers.js'
import { store } from '../common/redux_store.js'
import { saveRaces } from '../persistence/calendar-data.js'
import { ajaxRequest } from '../common/ajax.js'
import L from 'leaflet';
import { v1 as uuidv1 } from 'uuid'

import 'react-datepicker/dist/react-datepicker.css'
registerLocale('en-GB', enGB);

const SlideTransition = ({ children: child, ...props }) => {

  const duration = 400;

  const handleEntering = (element, is_appearing) => {
    $(element).stop(true).hide().slideDown(duration);
  }

  const handleExiting = (element, is_appearing) => {
    $(element).stop(true).slideUp(duration);
  }

  return (
    <Transition {...props} timeout={duration} onEnter={handleEntering} onExit={handleExiting}>
      {child}
    </Transition>
  )

};

const FadeTransition = ({ children: child, ...props }) => {

  const duration = 200;

  const handleEntering = (element, is_appearing) => {
    $(element).stop(true).hide().fadeIn(duration);
  }

  const handleExiting = (element, is_appearing) => {
    $(element).stop(true).fadeOut(duration);
  }

  return (
    <Transition {...props} timeout={duration} onEnter={handleEntering} onExit={handleExiting}>
      {child}
    </Transition>
  )

};

const TableRowTransition = ({ children: child, ...props }) => {

  const duration = 600;
  const fade_duration = duration / 2;
  const slide_duration = duration / 2;

  const handleEntering = (element, is_appearing) => {

    // Calculate what height the element needs to be.
    var height = element.scrollHeight;

    // Hide the td items which will stop the table row from changing height
    // since overflow hidden doesn't work.
    $('td', element).hide();

    // Animate the reveal of the row.
    $(element).stop(true).height(0).animate({height:height}, slide_duration, function () {
      // Now that the row is the correct height, display the td elements again.
      $('td:not(.hidden-items)', element).fadeIn(fade_duration);
    });
  }

  const handleExiting = (element, is_appearing) => {

    // Calculate what height the element needs to be.
    var height = element.scrollHeight;

    // Fade out the row contents.
    $.when($('td:not(.hidden-items)', element).fadeOut(fade_duration)).then(function () {
      // Hide the actual row.
      $('td', element).hide();
      $(element).height(height).animate({height:0}, slide_duration)
    });
  }

  return (
    <Transition {...props} timeout={duration} onEnter={handleEntering} onExit={handleExiting}>
      {child}
    </Transition>
  )

}

/**
 * Does the user have permission to edit this session.
 */
function hasSessionPlanEditPermission(training_session, athlete_data, group_data, coaching_companies) {
  const { has_edit_permission, edit_permission_reason } = sessionPlanEditPermission(training_session, athlete_data, group_data, coaching_companies);
  return has_edit_permission;
}

/**
 * Does the user have permission to edit this session.
 * Also returns a reason for giving or not giving access.
 */
export function sessionPlanEditPermission(training_session, athlete_data, group_data, coaching_companies) {

  let has_edit_permission;
  let edit_permission_reason;

  if (!training_session.has_permanent_id()) {
    // Session has not been saved at all yet, so must have been created by the
    // current user.
    has_edit_permission = true;
    edit_permission_reason = 'Has not yet been saved';
  }
  else if (typeof(group_data) != 'undefined' && group_data && typeof(group_data.current_user_is_prescriber) != 'undefined' && group_data.current_user_is_prescriber === true) {
    // We are viewing this as a group, so allow editing of the group sessions
    // by anyone else with prescriber access.
    has_edit_permission = true;
    edit_permission_reason = 'Group prescribers can edit any group session.';
  }
  else if (training_session.using_group_parent_session()) {
    // Group training can only be edited when it has been overridden.
    has_edit_permission = false;
    edit_permission_reason = 'Group session must be overridden before editing.';
  }
  else if (training_session.overridden_group_parent_session() && can_edit_training_plans_on_date(training_session.date)) {
    // This is a group session that has been overridden. Such sessions can be
    // edited only if the athlete can prescribe sessions in the future.
    has_edit_permission = true;
    edit_permission_reason = 'User cannot edit session on this date';
  }
  else if (training_session.creator.creator_id == xhale_meta_data.user_id) {
    // Can edit training sessions created by themselves.
    has_edit_permission = true;
    edit_permission_reason = 'User can edit any session created by themselves.';
  }
  else if (training_session.template) {
    if (coaching_companies && training_session.creator.creator_company_id in coaching_companies && coaching_companies[training_session.creator.creator_company_id].shared_template_editing === true) {
      has_edit_permission = true;
      edit_permission_reason = 'Company has shared editing permission.';
    }
    else {
      has_edit_permission = false;
      edit_permission_reason = 'template_created_by_another_user';
    }
  }
  else if (typeof(training_session.creator.creator_company_id) != 'undefined' && training_session.creator.creator_company_id == xhale_meta_data.coaching_company_id) {
    // Can edit training sessions created by others in their company.
    has_edit_permission = true;
    edit_permission_reason = 'This template was created by another user but within the same company.';
  }
  else {
    has_edit_permission = false;
    edit_permission_reason = 'No conditions for allowing access exist';
  }

  return {
    has_edit_permission: has_edit_permission,
    edit_permission_reason: edit_permission_reason,
  }

}

function hasGroupSessionOverridePermission(training_session) {
  if (!training_session.has_permanent_id()) {
    // Session has not been saved at all yet, so obviously cannot be deleted yet.
    return false;
  }
  else if (training_session.creator.creator_id == xhale_meta_data.user_id) {
    // Can override training sessions created by themselves.
    return true;
  }
  else if (training_session.creator.creator_company_id == xhale_meta_data.coaching_company_id) {
    // Can override training sessions created by others in a coach's company.
    return true;
  }
  else if (training_session.athlete_id == xhale_meta_data.user_id && can_edit_training_plans_on_date(training_session.date)) {
    // Can override their own training sessions if they are created by others
    // and they are allowed to edit their own training.
    return true;
  }
  else {
    return false;
  }
}

function hasSessionReschedulePermission(training_session, athlete_data, group_data) {
  if (!training_session) {
    // No training session provided.
    return false;
  }
  else if (training_session.template) {
    // Templates do not have a date to set.
    return false;
  }
  else if (!training_session.has_permanent_id()) {
    // Session has not been saved at all yet, so obviously the date can be set.
    return true;
  }
  else if (training_session.athlete_id == xhale_meta_data.user_id) {
    // Athletes can can reschedule, although there may be further restrictions
    // on whether they can reschedule to a date in the future.
    return true;
  }
  else if (training_session.creator.creator_id == xhale_meta_data.user_id) {
    // Users can reschedule any session they have created.
    return true;
  }
  else if (training_session.creator.creator_company_id == xhale_meta_data.coaching_company_id) {
    // Can reschedule any sessions created by someone else in the company.
    return true;
  }
  else if (typeof(group_data) != 'undefined' && group_data && typeof(group_data.current_user_is_prescriber) != 'undefined' && group_data.current_user_is_prescriber === true) {
    // We are viewing this as a group, so allow rescheduling of the group sessions
    // by anyone else with prescriber access.
    return true;
  }
  else if (training_session.athlete_id != xhale_meta_data.user_id && typeof(athlete_data) != 'undefined' && athlete_data && typeof(athlete_data.coaching_start_datetime) == 'object') {
    // When a coach is editing this athlete's data, they can reschedule training
    // if the current date is after coaching started.
    var training_session_date = newDateFromDatestring(training_session.date);
    var coaching_start_date = athlete_data.coaching_start_datetime;
    coaching_start_date.setHours(0, 0, 0, 0, 0);
    if (coaching_start_date <= training_session_date) {
      return true;
    }
    else {
      return false;
    }
  }
  else {
    return false;
  }
}

var RACE_TYPE_OPTIONS = {
  'sprint triathlon': 4,
  'standard triathlon':  3,
  'middle triathlon': 2,
  'long triathlon': 1,
  'other distance triathlon': 5,
  'sprint duathlon': 41,
  'standard duathlon': 42,
  'other distance duathlon': 43,
  'aquabike': 60,
  'sprint aquathlon': 51,
  'standard aquathlon': 52,
  'other distance aquathlon': 53,
  'gala': 31,
  'open water swim': 32,
  'road race bike': 24,
  'time trial': 23,
  'criterium': 25,
  'sportive': 26,
  'gravel': 28,
  'cyclocross': 21,
  'mountain bike': 22,
  'track': 27,
  'road race run': 11,
  'cross country': 12,
  'trail run': 13,
  'other': 0
}

function getDefaultStages(race_type) {

  race_type = parseInt(race_type);

  switch (race_type) {
    case RACE_TYPE_OPTIONS['long triathlon']:
    case RACE_TYPE_OPTIONS['middle triathlon']:
    case RACE_TYPE_OPTIONS['standard triathlon']:
    case RACE_TYPE_OPTIONS['sprint triathlon']:
    case RACE_TYPE_OPTIONS['other distance triathlon']:
      // Triathlons.
      return [RaceStage.DISCIPLINE_OPTIONS['other swim'], RaceStage.DISCIPLINE_OPTIONS['transition'], RaceStage.DISCIPLINE_OPTIONS['other bike'], RaceStage.DISCIPLINE_OPTIONS['transition'], RaceStage.DISCIPLINE_OPTIONS['other run']];
    case RACE_TYPE_OPTIONS['sprint duathlon']:
    case RACE_TYPE_OPTIONS['standard duathlon']:
    case RACE_TYPE_OPTIONS['other distance duathlon']:
      // Duathlon.
      return [RaceStage.DISCIPLINE_OPTIONS['other run'], RaceStage.DISCIPLINE_OPTIONS['other bike'], RaceStage.DISCIPLINE_OPTIONS['other run']];
    case RACE_TYPE_OPTIONS['sprint aquathlon']:
    case RACE_TYPE_OPTIONS['standard aquathlon']:
    case RACE_TYPE_OPTIONS['other distance aquathlon']:
      // Aquabike.
    case RACE_TYPE_OPTIONS['aquabike']:
      // Aquathlon.
      return [RaceStage.DISCIPLINE_OPTIONS['other swim'], RaceStage.DISCIPLINE_OPTIONS['other bike']];
    case RACE_TYPE_OPTIONS['gala']:
      return [RaceStage.DISCIPLINE_OPTIONS['pool swim']];
    case RACE_TYPE_OPTIONS['open water swim']:
      return [RaceStage.DISCIPLINE_OPTIONS['other swim']];
    case RACE_TYPE_OPTIONS['road race bike']:
    case RACE_TYPE_OPTIONS['time trial']:
    case RACE_TYPE_OPTIONS['criterium']:
    case RACE_TYPE_OPTIONS['sportive']:
      return [RaceStage.DISCIPLINE_OPTIONS['road bike']];
    case RACE_TYPE_OPTIONS['gravel']:
    case RACE_TYPE_OPTIONS['cyclocross']:
    case RACE_TYPE_OPTIONS['mountain bike']:
      return [RaceStage.DISCIPLINE_OPTIONS['off-road bike']];
    case RACE_TYPE_OPTIONS['track']:
      return [RaceStage.DISCIPLINE_OPTIONS['other bike']];
    case RACE_TYPE_OPTIONS['road race run']:
      return [RaceStage.DISCIPLINE_OPTIONS['road run']];
    case RACE_TYPE_OPTIONS['cross country']:
      return [RaceStage.DISCIPLINE_OPTIONS['cross country run']];
    case RACE_TYPE_OPTIONS['trail run']:
      return [RaceStage.DISCIPLINE_OPTIONS['trail run']];
    case RACE_TYPE_OPTIONS['other']:
      return [RaceStage.DISCIPLINE_OPTIONS['other']];
  }
}

class AdditionalDisciplineData extends React.Component {

  add_personal_bests = (discipline_category) => {
    var pb_data = [];
    if (this.props.athlete_data.personal_bests[discipline_category].length > 0) {
      pb_data.push(<h4 key={'heading-pbs-' + discipline_category}>{'Personal ' + discipline_category + ' bests'}</h4>);
      $.each(this.props.athlete_data.personal_bests[discipline_category], function (index, personal_best) {
        var date_info = (personal_best.date) ? <span> on {personal_best.date}</span> : null;
        pb_data.push(<div key={'pb-' + discipline_category + '-' + index} className="additional-discipline-data personal-best">{decimalPlaces(personal_best.distance, 5, 'zero', false)}<span className="unit">{personal_best.unit}</span> in {personal_best.duration}{date_info}</div>);
      });
    }
    return pb_data;
  }

  render = () => {

    var pb_data = [];

    // Add swim PB data.
    if ((this.props.discipline_category == 'swim' || this.props.discipline_category == 'brick') && this.props.athlete_data.personal_bests.swim.length > 0) {
      pb_data = pb_data.concat(this.add_personal_bests('swim'));
    }

    // Add bike data.
    if ((this.props.discipline_category == 'bike' || this.props.discipline_category == 'brick') && (this.props.athlete_data.personal_bests.bike.length > 0 || this.props.athlete_data.target_powers.length > 0)) {

      if (this.props.athlete_data.target_powers.length > 0) {
        pb_data.push(<h4 key="heading-target-bike-power">Target powers</h4>);
      }

      for (var i = 0; i < this.props.athlete_data.target_powers.length; i++) {
        pb_data.push(
          <div key={'target-bike-power-' + i} className={'additional-discipline-data power ' + this.props.athlete_data.target_powers[i].id}>
            {this.props.athlete_data.target_powers[i].description}: {this.props.athlete_data.target_powers[i].watts}<span className="unit">watts</span>
          </div>
        );
      }

      pb_data = pb_data.concat(this.add_personal_bests('bike'));
    }

    // Add run data.
    if ((this.props.discipline_category == 'run' || this.props.discipline_category == 'brick') && (this.props.athlete_data.personal_bests.run.length > 0 || this.props.athlete_data.target_run_paces.length > 0)) {

      if (this.props.athlete_data.target_run_paces.length > 0) {
        pb_data.push(<h4 key="heading-target-run-paces">Target run paces</h4>);
      }

      for (var i = 0; i < this.props.athlete_data.target_run_paces.length; i++) {
        var min_miles = xhale.helper.format_minutes_seconds(xhale.helper.meters_per_second_as_seconds_per_mile(this.props.athlete_data.target_run_paces[i].meters_per_second));
        var min_km = xhale.helper.format_minutes_seconds(xhale.helper.meters_per_second_as_seconds_per_km(this.props.athlete_data.target_run_paces[i].meters_per_second));
        pb_data.push(
          <div key={'target-run-paces-' + i} className={'additional-discipline-data run-pace' + this.props.athlete_data.target_run_paces[i].id}>
            {athlete_data.target_run_paces[i].description}: <span className="the-pace min-km">{min_km}<span className="unit">min/km</span></span> <span className="the-pace min-mile">{min_miles}<span className="unit">min/mile</span></span>
          </div>
        );
      }

      pb_data = pb_data.concat(this.add_personal_bests('run'));
    }

    if (pb_data.length == 0) {
      return null;
    }
    else {
      return (
        <div id="additional-discipline-data">
          {pb_data}
        </div>
      );
    }
  }

}

class TitleSection extends React.Component {

  state = {
    display_additional_discipline_data: false
  }

  handleToggleAdditionalDisciplineData = (event) => {
    if (this.state.display_additional_discipline_data) {
      this.setState({display_additional_discipline_data: false});
    }
    else {
      this.setState({display_additional_discipline_data: true});
    }
  }

  change_discipline = (event) => {
    this.props.training_session.discipline_id = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_other_discipline = (event) => {
    this.props.training_session.other_discipline = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_date = (date) => {
    if (this.props.mode == 'training_session') {
      // The extra check to compare dates here is required incase we
      // had to use the onblur event instead of onChange. This is a
      // workaround for a problem with Firefox on Android not recognising
      // the change event.
      if (this.props.training_session.date != date) {
        this.props.training_session.date = date;
        this.props.updateTrainingSession(this.props.training_session, true);
      }
    }
    else {
      if (this.props.training_session.date != date) {
        this.props.race.date = date;
        this.props.updateRace(this.props.race, true);
      }
    }
  }

  handle_discipline_title_click = (event) => {
    if (this.allow_altering_discipline_and_intensity()) {
      if (this.props.training_session_form.state.discipline_widget_open == true) {
        this.props.training_session_form.setState({discipline_widget_open: false});
      }
      else {
        this.props.training_session_form.setState({discipline_widget_open: true});
      }
    }
  }

  handleSponsorChange = (event) => {
    this.props.training_session.sponsor = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  handleSubtitleChange = (event) => {
    this.props.training_session.subtitle = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  handleKeySessionChange = (event) => {
    this.props.training_session.key_session = event.target.checked;
    this.props.updateTrainingSession(this.props.training_session);
  }

  handle_schedule_title_click = (event) => {
    if (this.props.training_session_form.state.schedule_widget_open == true) {
      if (this.props.columns == 3) {
        this.props.training_session_form.setState({schedule_widget_open: false});
      }
    }
    else if (hasSessionReschedulePermission(this.props.training_session, this.props.athlete_data, this.props.group_data)) {
      this.props.training_session_form.setState({schedule_widget_open: true});
    }
  }

  handle_intensity_title_click = (event) => {
    if (this.allow_altering_discipline_and_intensity()) {
      if (this.props.training_session_form.state.intensity_widget_open == true) {
        if (this.props.columns == 3) {
          this.props.training_session_form.setState({intensity_widget_open: false});
        }
      }
      else {
        this.props.training_session_form.setState({intensity_widget_open: true});
      }
    }
  }

  native_date_change = (event) => {
    // Keeping the native version at least for now because it likely works
    // better on mobile.
    this.change_date(event.target.value);
  }

  datepicker_date_change = (date) => {
    this.change_date(date.format('YYYY-MM-DD'));
  }

  allow_altering_discipline_and_intensity = () => {
    // Allow altering discipline and intensity if the session was created by
    // the athlete or it is an unsaved race. We allow changing unsaved races
    // in case the wrong link was clicked, but changing a saved race would
    // mean deleting a race and then creating a new training session in the
    // database. Since it is unexpected behaviour from a user as well, we
    // do not allow this. Coaches can also change the session type.
    return (
      (this.props.mode == 'training_session' && this.props.training_session.creator && this.props.training_session.creator.type == CREATOR_ATHLETE) ||
      (this.props.mode == 'training_session' && this.props.perspective == 'coach') ||
      (this.props.mode == 'race' && !this.props.race.id)
    );
  }

  render() {

    if (this.props.mode == 'race' || (this.props.mode == 'training_session' && hasSessionPlanEditPermission(this.props.training_session, this.props.athlete_data, this.props.group_data, this.props.coaching_companies))) {
      var readonly = false;
    }
    else {
      var readonly = true;
    }

    var discipline_title_classes = 'session-form__title session-form__discipline-title';
    var intensity_title_classes = 'session-form__title session-form__intensity-title';
    var schedule_title_classes = 'session-form__title session-form__schedule-title';

    if (this.allow_altering_discipline_and_intensity()) {
      discipline_title_classes += ' active';
      intensity_title_classes += ' active';
    }

    if (hasSessionReschedulePermission(this.props.training_session, this.props.athlete_data, this.props.group_data)) {
      schedule_title_classes += ' active';
    }

    if (this.props.mode == 'training_session') {

      var discipline_category = this.props.training_session.getDisciplineCategory();

      if (!this.props.training_session.discipline_id) {
        var discipline_title_text = 'Select an activity';
      }
      else if (discipline_category == 'other') {
        if (!this.props.training_session.other_discipline || this.props.training_session.other_discipline == '') {
          var discipline_title_text = 'Other';
        }
        else {
          var discipline_title_text = this.props.training_session.other_discipline;
        }
      }
      else if (discipline_category == 'diary') {
        if (!this.props.training_session.diary_summary || this.props.training_session.diary_summary == '') {
          var discipline_title_text = 'Diary';
        }
        else {
          var discipline_title_text = this.props.training_session.diary_summary;
        }
      }
      else {
        var discipline_title_text = this.props.training_session.getDisciplineName();
      }
    }
    else {
      var discipline_title_text = (this.props.race.race_name) ? this.props.race.race_name : 'Race';
    }

    var discipline_title_display = (this.props.columns == 1) ? 'none' : 'block';
    var discipline_title = <h2 className={discipline_title_classes} style={{display: discipline_title_display}} onClick={this.handle_discipline_title_click} >{discipline_title_text}</h2>;

    var zone_data;
    if (this.props.mode == 'training_session' && discipline_category != 'diary' && discipline_category != 'rest' && this.props.columns == 3) {
      var intensity_title_text = (this.props.training_session.intensity || this.props.training_session.intensity == 0) ? 'Intensity ' + this.props.training_session.intensity : 'Choose intensity';
      var intensity_title_display = 'block';

      if (this.props.training_session.intensity && this.props.training_session.intensity > 0) {

        var bike_zone_data;
        var run_zone_data;

        if ((discipline_category == 'bike' || discipline_category == 'brick') && this.props.athlete_data.training_zones.bike.zones) {
          var min_heart_rate_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].heart_rate_min;
          var max_heart_rate_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].heart_rate_max;

          var min_power_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].power_min;
          var max_power_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].power_max;
        }

        if ((discipline_category == 'run' || discipline_category == 'brick') && this.props.athlete_data.training_zones.run.zones) {

          var min_heart_rate_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].heart_rate_min;
          var max_heart_rate_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].heart_rate_max;

          var min_power_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].power_min;
          var max_power_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].power_max;

        }

        if (min_heart_rate_bike || min_power_bike) {
          var heart_rate_bike = null;
          var power_bike = null;
          if (min_heart_rate_bike) {
            heart_rate_bike = <span>{min_heart_rate_bike + '-' + max_heart_rate_bike}<span className="unit">bpm</span></span>;
          }
          if (min_power_bike) {
            power_bike = <span>{min_power_bike + '-' + max_power_bike}<span className="unit">watts</span></span>;
          }

          var bike_prefix = (discipline_category == 'brick') ? 'bike ' : '';
        }

        if (min_heart_rate_run || min_power_run) {
          var heart_rate_run = null;
          var power_run = null;
          if (min_heart_rate_run) {
            heart_rate_run = <span>{min_heart_rate_run + '-' + max_heart_rate_run}<span className="unit">bpm</span></span>;
          }
          if (min_power_run) {
            power_run = <span>{min_power_run + '-' + max_power_run}<span className="unit">watts</span></span>;
          }

          var run_prefix = (discipline_category == 'brick') ? 'run ' : '';
        }

        if (min_heart_rate_bike || min_power_bike || min_heart_rate_run || min_power_run || slowest_pace_run) {
          if ((min_heart_rate_bike || min_power_bike ) && (min_heart_rate_run || min_power_run || slowest_pace_run)) {
            bike_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal', display: 'block'}} className="zones">{bike_prefix}{heart_rate_bike} {power_bike}</span>);
            run_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal'}} className="zones">{run_prefix}{heart_rate_run} {power_run}</span>);
            zone_data = <span style={{ position: 'absolute', paddingLeft: '6px', marginTop: '-4px'}} >{bike_zone_data} {run_zone_data}</span> }
          else {
            bike_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal'}} className="zones">{bike_prefix}{heart_rate_bike} {power_bike}</span>);
            run_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal'}} className="zones">{run_prefix}{heart_rate_run} {power_run}</span>);
            zone_data = <span>{bike_zone_data}{run_zone_data}</span>
          }
        }

      }
    }
    else {
      // Just hiding this for now while we transition away from binding click event with jQuery.
      var intensity_title_visibility = 'hidden';
    }

    var intensity_title = <h2 style={{visibility: intensity_title_visibility}} className={intensity_title_classes} onClick={this.handle_intensity_title_click}>{intensity_title_text}{zone_data}</h2>

    var schedule_title;
    let date;
    if (this.props.mode == 'training_session' && this.props.training_session.date) {
      date = newDateFromDatestring(this.props.training_session.date);
    }
    else if (this.props.mode == 'race' && this.props.race.date) {
      date = this.props.race.date;
    }
    if (typeof(date) != 'undefined') {
      // There may not be a date on the first load, in which case we have seen
      // Safari crash at this point.
      schedule_title = <h2 className={schedule_title_classes} onClick={this.handle_schedule_title_click} data-date={date.format('Y-m-d')}>{date.format('D jS M Y')}</h2>
    }

    var session_is_template = this.props.mode == 'training_session' && this.props.training_session.template ? true : false;

    // Swapping between a race and a training session after it has saved is
    // going to require creating a whole new database entry and deleting the
    // existing one. This is unlikely to occur, so we don't support doing this.
    // There's actually a great simplification that can occur if we don't
    // allow moving back and forth between races and training sessions even
    // before it is saved since we don't have to keep a training session in
    // memory in case the user makes a mistake and selects race instead of,
    // for example, changing a bike interval to a bike endurance.
    var race_option;
    if (!session_is_template && this.props.training_session && !this.props.training_session.has_permanent_id()) {
      const race_option_disabled = (this.props.training_session.discipline_id) ? true : false;
      race_option = (<option value="16" disabled={race_option_disabled}>Race</option>);
    }

    let discipline_select_input;
    if (this.props.columns == 1 && (this.props.training_session && this.props.training_session.creator.type == CREATOR_ATHLETE || this.props.perspective == 'coach') && this.allow_altering_discipline_and_intensity()) {

      // Other discipline field.
      var mobile_other_discipline;
      if (this.props.training_session.discipline_id == 15) {
        var mobile_other_discipline = (
          <div id="other-discipline" className="field-wrapper inline">
            <label htmlFor="id_other_discipline">Brief description: </label>
            <input type="text" name="other_discipline" id="id_other_discipline" maxLength="50" onChange={this.change_other_discipline} value={this.props.training_session.other_discipline} />
          </div>
        );
      }

      // Additional Discipline Data.
      var show_pb_button;
      var additional_discipline_data;
      if (this.props.perspective == 'coach' && this.props.athlete_data && this.props.athlete_data.personal_bests && (discipline_category == 'swim' || discipline_category == 'bike' || discipline_category == 'run' || discipline_category == 'brick')) {

        var has_pb_data = false;

        if ((discipline_category == 'swim' || discipline_category == 'brick') && (this.props.athlete_data.personal_bests.swim.length > 0)) {
          has_pb_data = true;
        }
        else if ((discipline_category == 'bike' || discipline_category == 'brick') && (this.props.athlete_data.personal_bests.bike.length > 0 || this.props.athlete_data.target_powers.length > 0)) {
          has_pb_data = true;
        }
        else if ((discipline_category == 'run' || discipline_category == 'brick') && (this.props.athlete_data.personal_bests.run.length > 0 || this.props.athlete_data.target_run_paces.length > 0)) {
          has_pb_data = true;
        }

        if (has_pb_data) {
          show_pb_button = <div id="show-pb-button" onClick={this.handleToggleAdditionalDisciplineData}>{discipline_category + ' PBs'}</div>;
        }
        else {
          show_pb_button = <div id="show-pb-button" className="inactive">No PBs</div>;
        }

        if (this.state.display_additional_discipline_data) {
          additional_discipline_data = <SlideTransition><AdditionalDisciplineData discipline_category={discipline_category} athlete_data={this.props.athlete_data} /></SlideTransition>;
        }

      }

      // Only give the not selected option when the user hasn't already selected
      // a discipline. Like with disabling the race option it makes it easier to
      // avoid losing data if once it is a training session it remains a training
      // session and a non-training session option cannot be selected.
      let please_select_option;
      if (!this.props.training_session.discipline_id) {
        please_select_option = <option value="">Please select</option>;
      }

      discipline_select_input = (
        <select name="discipline_select" id="id_discipline_select" value={this.props.training_session.discipline_id || ""} onChange={this.change_discipline} style={{width: '148px', borderRadius: '3px', padding: '3px 0px'}}>
        {please_select_option}
        <optgroup label="Swim">
          <option value="1">Swim Tech</option>
          <option value="2">Swim Intervals</option>
          <option value="3">Swim Endurance</option>
        </optgroup>
        <optgroup label="Bike">
          <option value="4">Bike Intervals</option>
          <option value="5">Turbo Intervals</option>
          <option value="6">Bike Endurance</option>
        </optgroup>
        <optgroup label="Run">
          <option value="7">Run Intervals</option>
          <option value="8">Run Endurance</option>
          <option value="9">Run Tempo</option>
        </optgroup>
        <optgroup label="Brick">
          <option value="10">Brick Intervals</option>
          <option value="11">Brick Endurance</option>
        </optgroup>
        <optgroup label="Gym">
          <option value="12">Gym Core/Specifics</option>
          <option value="13">Gym Lifting</option>
        </optgroup>
        <option value="15">Other training</option>
        {race_option}
        <option value="14">Rest day</option>
        <option value="17">Diary entry</option>
        </select>
      );

    }
    else if (this.props.mode == 'race') {
      discipline_select_input = <span style={{ fontSize: 13, lineHeight: '20px' }}>Race</span>
    }
    else {
      discipline_select_input = <span style={{ fontSize: 13, lineHeight: '20px' }}>{this.props.training_session.discipline_display}</span>
    }

    let discipline_select;
    if (this.props.mode == 'training_session' || (this.props.mode == 'race' && !this.props.race.has_permanent_id())) {
      discipline_select = (
        <div className="discipline_select field-wrapper inline">
          {show_pb_button}
          {'discipline_select' in this.props.form_errors && <Errors errors={this.props.form_errors.discipline} />}
          <label htmlFor="id_discipline_select">Discipline:</label>
          {discipline_select_input}
          {mobile_other_discipline}
        </div>
      );
    }

    var subtitle;
    // Note we still show subtitle field if it has been filled in for legacy reasons. One day we may wish to get rid of it completely.
    if (this.props.columns == 1 && this.props.mode == 'training_session' && (this.props.training_session.is_training_session() || this.props.training_session.subtitle)) {
      subtitle = (
        <div className="field-wrapper inline training-session">
          {'subtitle' in this.props.form_errors && <Errors errors={this.props.form_errors.subtitle} />}
          <label htmlFor="id_subtitle">Subtitle:</label><input type="text" name="subtitle" id="id_subtitle" maxLength="100" readOnly={!this.allow_altering_discipline_and_intensity()} onChange={this.handleSubtitleChange} value={this.props.training_session.subtitle || ""} />
        </div>
      );
    }

    let key_session;
    let favourite;
    if (this.props.columns == 1 && this.props.perspective == 'coach' && this.props.mode == 'training_session') {

      // Templates don't have the key session option.
      if (!this.props.training_session.template && this.props.training_session.discipline_id) {

        if (!readonly) {
          var key_session_field = <input type="checkbox" name="key_session" id="id_key_session" checked={this.props.training_session.key_session ? true : false} onChange={this.handleKeySessionChange} />;
        }
        else {
          var key_session_field = <span>{this.props.training_session.key_session ? 'yes': 'no'}</span>;
        }

        key_session = (
          <div className="field-wrapper inline training-session">
            {'key_session' in this.props.form_errors && <Errors errors={this.props.form_errors.key_session} />}
            <label htmlFor="id_key_session">Key session:</label>
            {key_session_field}
          </div>
        );

        favourite = (
          <div className="field-wrapper inline training-session">
            {'favourite' in this.props.form_errors && <Errors errors={this.props.form_errors.favourite} />}
            <label htmlFor="id_key_session">Favourite:</label>
            {key_session_field}
          </div>
        );

      }

    }

    var sponsor;
    if (this.props.perspective == 'coach' && this.props.mode == 'training_session' && this.props.group_data && typeof(this.props.group_data.sponsors) != 'undefined' && this.props.group_data.sponsors.length > 0) {

      var sponsor_users = [];
      var sponsor_companies = [];
      for (var i = 0; i < this.props.group_data.sponsors.length; i++) {
        var sponsor = this.props.group_data.sponsors[i];
        if (sponsor.type == 'user') {
          sponsor_users.push(<option key={sponsor.id} value={'user-' + sponsor.id}>{sponsor.name}</option>)
        }
        else if (sponsor.type == 'company') {
          sponsor_companies.push(<option key={sponsor.id} value={'company-' + sponsor.id}>{sponsor.name}</option>)
        }
      }

      var sponsor = (
        <div className="sponsor field-wrapper">
          {'sponsor' in this.props.form_errors && <Errors errors={this.props.form_errors.sponsor} />}
          <label htmlFor="id_sponsor">Brough to you by:</label>
          <select name="sponsor" id="id_sponsor" value={this.props.training_session.sponsor || ""} onChange={this.handleSponsorChange}>
            <option value="">Please select</option>
            <optgroup label="Users">
              {sponsor_users}
            </optgroup>
            <optgroup label="Companies">
              {sponsor_companies}
            </optgroup>
          </select>
        </div>
      );

    }

    if (this.props.columns == 1) {
      return (
        <div className="title-section">
          {'discipline' in this.props.form_errors && <Errors errors={this.props.form_errors.discipline} />}
          {discipline_title}
          {discipline_select}
          <TransitionGroup>{additional_discipline_data ? additional_discipline_data : null}</TransitionGroup>
          {subtitle}
          {key_session}
          {sponsor}
        </div>
      )
    }
    else {
      return (
        <div className="title-section">
          {'discipline' in this.props.form_errors && <Errors errors={this.props.form_errors.discipline} />}
          {discipline_title}
          {'intensity' in this.props.form_errors && <Errors errors={this.props.form_errors.intensity} />}
          {intensity_title}
          {schedule_title}
          {discipline_select}
          {subtitle}
          {key_session}
          {sponsor}
        </div>
      )
    }
  }
}

class TitleSectionV2 extends React.Component {

  state = {
    display_additional_discipline_data: false
  }

  handleToggleAdditionalDisciplineData = (event) => {
    if (this.state.display_additional_discipline_data) {
      this.setState({display_additional_discipline_data: false});
    }
    else {
      this.setState({display_additional_discipline_data: true});
    }
  }

  change_discipline = (event) => {
    this.props.training_session.discipline_id = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_other_discipline = (event) => {
    this.props.training_session.other_discipline = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_date = (date) => {
    if (this.props.mode == 'training_session') {
      // The extra check to compare dates here is required incase we
      // had to use the onblur event instead of onChange. This is a
      // workaround for a problem with Firefox on Android not recognising
      // the change event.
      if (this.props.training_session.date != date) {
        this.props.training_session.date = date;
        this.props.updateTrainingSession(this.props.training_session, true);
      }
    }
    else {
      if (this.props.training_session.date != date) {
        this.props.race.date = date;
        this.props.updateRace(this.props.race, true);
      }
    }
  }

  handle_discipline_title_click = (event) => {
    if (this.allow_altering_discipline_and_intensity()) {
      if (this.props.training_session_form.state.discipline_widget_open == true) {
        this.props.training_session_form.setState({discipline_widget_open: false});
      }
      else {
        this.props.training_session_form.setState({discipline_widget_open: true});
      }
    }
  }

  handleSponsorChange = (event) => {
    this.props.training_session.sponsor = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  handleSubtitleChange = (event) => {
    this.props.training_session.subtitle = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  handleKeySessionChange = (event) => {
    this.props.training_session.key_session = event.target.checked;
    this.props.updateTrainingSession(this.props.training_session);
  }

  handle_schedule_title_click = (event) => {
    if (this.props.training_session_form.state.schedule_widget_open == true) {
      if (this.props.columns == 3) {
        this.props.training_session_form.setState({schedule_widget_open: false});
      }
    }
    else if (hasSessionReschedulePermission(this.props.training_session, this.props.athlete_data, this.props.group_data)) {
      this.props.training_session_form.setState({schedule_widget_open: true});
    }
  }

  handle_intensity_title_click = (event) => {
    if (this.allow_altering_discipline_and_intensity()) {
      if (this.props.training_session_form.state.intensity_widget_open == true) {
        if (this.props.columns == 3) {
          this.props.training_session_form.setState({intensity_widget_open: false});
        }
      }
      else {
        this.props.training_session_form.setState({intensity_widget_open: true});
      }
    }
  }

  native_date_change = (event) => {
    // Keeping the native version at least for now because it likely works
    // better on mobile.
    this.change_date(event.target.value);
  }

  datepicker_date_change = (date) => {
    this.change_date(date.format('YYYY-MM-DD'));
  }

  allow_altering_discipline_and_intensity = () => {
    // Allow altering discipline and intensity if the session was created by
    // the athlete or it is an unsaved race. We allow changing unsaved races
    // in case the wrong link was clicked, but changing a saved race would
    // mean deleting a race and then creating a new training session in the
    // database. Since it is unexpected behaviour from a user as well, we
    // do not allow this. Coaches can also change the session type.
    return (
      (this.props.mode == 'training_session' && this.props.training_session.creator && this.props.training_session.creator.type == CREATOR_ATHLETE) ||
      (this.props.mode == 'training_session' && this.props.training_session.creator && this.props.training_session.creator.type == CREATOR_COACH && this.props.perspective == 'coach') ||
      (this.props.mode == 'training_session' && this.props.training_session.creator && this.props.training_session.creator.type == CREATOR_GROUP && this.props.perspective == 'coach') ||
      (this.props.mode == 'race' && !this.props.race.id)
    );
  }

  render() {

    var discipline_category = (this.props.mode == 'race') ? 'race' : this.props.training_session.getDisciplineCategory();
    const distance_unit = this.props.training_session.getDistanceUnit();

    if (this.props.mode == 'race' || (this.props.mode == 'training_session' && hasSessionPlanEditPermission(this.props.training_session, this.props.athlete_data, this.props.group_data, this.props.coaching_companies))) {
      var readonly = false;
    }
    else {
      var readonly = true;
    }

    var discipline_title_classes = 'session-form__title session-form__discipline-title';
    var intensity_title_classes = 'session-form__title session-form__intensity-title';
    var schedule_title_classes = 'session-form__title session-form__schedule-title';

    if (this.allow_altering_discipline_and_intensity()) {
      discipline_title_classes += ' active';
      intensity_title_classes += ' active';
    }

    if (hasSessionReschedulePermission(this.props.training_session, this.props.athlete_data, this.props.group_data)) {
      schedule_title_classes += ' active';
    }

    if (this.props.mode == 'training_session') {
      if (!this.props.training_session.discipline_id) {
        var discipline_title_text = 'Select an activity';
      }
      else if (discipline_category == 'other') {
        if (!this.props.training_session.other_discipline || this.props.training_session.other_discipline == '') {
          var discipline_title_text = 'Other';
        }
        else {
          var discipline_title_text = this.props.training_session.other_discipline;
        }
      }
      else if (discipline_category == 'diary') {
        if (!this.props.training_session.diary_summary || this.props.training_session.diary_summary == '') {
          var discipline_title_text = 'Diary';
        }
        else {
          var discipline_title_text = this.props.training_session.diary_summary;
        }
      }
      else {
        var discipline_title_text = this.props.training_session.getDisciplineName();
      }
    }
    else {
      var discipline_title_text = (this.props.race.race_name) ? this.props.race.race_name : 'Race';
    }

    var discipline_title_display = (this.props.columns == 1) ? 'none' : 'block';
    var discipline_title = <h2 className={discipline_title_classes} style={{display: discipline_title_display, float: 'left', margin: 0, background: 'none', border: 'none'}}><span className="title" onClick={this.handle_discipline_title_click}>{discipline_title_text}</span></h2>;

    var zone_data;
    if (this.props.mode == 'training_session' && discipline_category != 'diary' && discipline_category != 'rest' && this.props.columns == 3) {
      var intensity_title_text = (this.props.training_session.intensity || this.props.training_session.intensity == 0) ? 'Intensity ' + this.props.training_session.intensity : 'Choose intensity';
      var intensity_title_display = 'block';

      if (this.props.training_session.intensity && this.props.training_session.intensity > 0) {

        var bike_zone_data;
        var run_zone_data;

        if ((discipline_category == 'bike' || discipline_category == 'brick') && this.props.athlete_data.training_zones.bike.zones) {
          var min_heart_rate_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].heart_rate_min;
          var max_heart_rate_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].heart_rate_max;

          var min_power_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].power_min;
          var max_power_bike = this.props.athlete_data.training_zones.bike.zones[this.props.training_session.intensity - 1].power_max;
        }

        if ((discipline_category == 'run' || discipline_category == 'brick') && this.props.athlete_data.training_zones.run.zones) {

          var min_heart_rate_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].heart_rate_min;
          var max_heart_rate_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].heart_rate_max;

          var min_power_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].power_min;
          var max_power_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].power_max;

          var slowest_pace_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].meters_per_second_slowest;
          var fastest_pace_run = this.props.athlete_data.training_zones.run.zones[this.props.training_session.intensity - 1].meters_per_second_fastest;

        }

        if (min_heart_rate_bike || min_power_bike) {
          var heart_rate_bike = null;
          var power_bike = null;
          if (min_heart_rate_bike) {
            heart_rate_bike = <span>{min_heart_rate_bike + '-' + max_heart_rate_bike}<span className="unit">bpm</span></span>;
          }
          if (min_power_bike) {
            power_bike = <span>{min_power_bike + '-' + max_power_bike}<span className="unit">watts</span></span>;
          }

          var bike_prefix = (discipline_category == 'brick') ? 'bike ' : '';
        }

        if (min_heart_rate_run || min_power_run || slowest_pace_run) {
          var heart_rate_run = null;
          var power_run = null;
          var pace_run = null;
          if (min_heart_rate_run) {
            heart_rate_run = <span>{min_heart_rate_run + '-' + max_heart_rate_run}<span className="unit">bpm</span></span>;
          }
          if (min_power_run) {
            power_run = <span>{min_power_run + '-' + max_power_run}<span className="unit">watts</span></span>;
          }

          if (slowest_pace_run) {
            if (distance_unit == 'miles') {
              pace_run = <span>{formatTime(metersPerSecondAsSecondsPerMile(slowest_pace_run)) + '-' + formatTime(metersPerSecondAsSecondsPerMile(fastest_pace_run))}<span className="unit">min/mile</span></span>;
            }
            else {
              pace_run = <span>{formatTime(metersPerSecondAsSecondsPerKilometer(slowest_pace_run)) + '-' + formatTime(metersPerSecondAsSecondsPerKilometer(fastest_pace_run))}<span className="unit">min/km</span></span>;
            }
          }

          var run_prefix = (discipline_category == 'brick') ? 'run ' : '';
        }

        if (min_heart_rate_bike || min_power_bike || min_heart_rate_run || min_power_run || slowest_pace_run) {
          if ((min_heart_rate_bike || min_power_bike) && (min_heart_rate_run || min_power_run)) {
            bike_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal', display: 'block'}} className="zones">{bike_prefix}{heart_rate_bike} {power_bike}</span>);
            run_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal'}} className="zones">{run_prefix}{heart_rate_run} {power_run}</span>);
            zone_data = <span style={{ position: 'absolute', paddingLeft: '6px', marginTop: '-4px'}} >{bike_zone_data} {run_zone_data}</span> }
          else {
            bike_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal'}} className="zones">{bike_prefix}{heart_rate_bike} {power_bike}</span>);
            run_zone_data = (<span style={{fontSize: '12px', fontWeight: 'normal'}} className="zones">{run_prefix}{heart_rate_run} {power_run} {pace_run}</span>);
            zone_data = <span>{bike_zone_data}{run_zone_data}</span>
          }
        }

      }

    }
    else {
      // Just hiding this for now while we transition away from binding click event with jQuery.
      var intensity_title_visibility = 'hidden';
    }

    var intensity_title = <h2 style={{visibility: intensity_title_visibility, float: 'left', margin: 0, background: 'none', border: 'none'}} className={intensity_title_classes}><span className="title" onClick={this.handle_intensity_title_click}>{intensity_title_text}</span> {zone_data}</h2>

    var schedule_title;
    var session_order_buttons;
    let date;
    if (this.props.mode == 'training_session' && this.props.training_session.date) {
      date = newDateFromDatestring(this.props.training_session.date);
    }
    else if (this.props.mode == 'race' && this.props.race.date) {
      date = this.props.race.date;
    }
    if (typeof(date) != 'undefined') {
      // There may not be a date on the first load, in which case we have seen
      // Safari crash at this point.

      if (hasBetaAccess('athlete_up_down_buttons') && this.props.mode == 'training_session' && typeof(this.props.updateSessionOrder) != 'undefined') {
        session_order_buttons = (
          <SessionOrderButtons
            perspective={this.props.perspective}
            training_session={this.props.training_session}
            markers_on_this_date={this.props.markers_on_this_date}
            updateSessionOrder={this.props.updateSessionOrder}
          />
        );
      }

      schedule_title = (
        <h2 className={schedule_title_classes} style={{float: 'left', margin: 0, background: 'none'}} data-date={date.format('Y-m-d')}>
          <span className="title" onClick={this.handle_schedule_title_click}>{date.format('D jS M Y')}</span> {session_order_buttons}
        </h2>
      );
    }

    var session_is_template = this.props.mode == 'training_session' && this.props.training_session.template ? true : false;

    // Swapping between a race and a training session after it has saved is
    // going to require creating a whole new database entry and deleting the
    // existing one. This is unlikely to occur, so we don't support doing this.
    var race_option;
    if (!session_is_template && !this.props.training_session.has_permanent_id()) {
      race_option = (<option value="16">Race</option>);
    }

    var discipline_select;
    if (this.props.columns == 1 && (this.props.training_session.creator.type == CREATOR_ATHLETE || this.props.perspective == 'coach') && this.allow_altering_discipline_and_intensity()) {

      // Other discipline field.
      var mobile_other_discipline;
      if (this.props.training_session.discipline_id == 15) {
        var mobile_other_discipline = (
          <div id="other-discipline" className="field-wrapper inline">
            <label htmlFor="id_other_discipline">Brief description: </label>
            <input type="text" name="other_discipline" id="id_other_discipline" readOnly={readonly} maxLength="50" onChange={this.change_other_discipline} value={this.props.training_session.other_discipline} />
          </div>
        );
      }

      // Additional Discipline Data.
      var show_pb_button;
      var additional_discipline_data;
      if (this.props.perspective == 'coach' && this.props.athlete_data && this.props.athlete_data.personal_bests && (discipline_category == 'swim' || discipline_category == 'bike' || discipline_category == 'run' || discipline_category == 'brick')) {

        var pb_data_box;
        var has_pb_data = false;

        if ((discipline_category == 'swim' || discipline_category == 'brick') && (this.props.athlete_data.personal_bests.swim.length > 0)) {
          has_pb_data = true;
        }
        else if ((discipline_category == 'bike' || discipline_category == 'brick') && (this.props.athlete_data.personal_bests.bike.length > 0 || this.props.athlete_data.target_powers.length > 0)) {
          has_pb_data = true;
        }
        else if ((discipline_category == 'run' || discipline_category == 'brick') && (this.props.athlete_data.personal_bests.run.length > 0 || this.props.athlete_data.target_run_paces.length > 0)) {
          has_pb_data = true;
        }

        if (has_pb_data) {
          show_pb_button = <div id="show-pb-button" onClick={this.handleToggleAdditionalDisciplineData}>{discipline_category + ' PBs'}</div>;
        }
        else {
          show_pb_button = <div id="show-pb-button" className="inactive">No PBs</div>;
        }

        if (this.state.display_additional_discipline_data) {
          additional_discipline_data = <SlideTransition><AdditionalDisciplineData discipline_category={discipline_category} athlete_data={this.props.athlete_data} /></SlideTransition>;
        }

      }

      if (!readonly) {
        var discipline_select_input = (
          <select name="discipline_select" id="id_discipline_select" value={this.props.training_session.discipline_id || ""} onChange={this.change_discipline} style={{width: '148px', borderRadius: '3px', padding: '3px 0px'}}>
          <option value="">Please select</option>
          <optgroup label="Swim">
            <option value="1">Swim Tech</option>
            <option value="2">Swim Intervals</option>
            <option value="3">Swim Endurance</option>
          </optgroup>
          <optgroup label="Bike">
            <option value="4">Bike Intervals</option>
            <option value="5">Turbo Intervals</option>
            <option value="6">Bike Endurance</option>
          </optgroup>
          <optgroup label="Run">
            <option value="7">Run Intervals</option>
            <option value="8">Run Endurance</option>
            <option value="9">Run Tempo</option>
          </optgroup>
          <optgroup label="Brick">
            <option value="10">Brick Intervals</option>
            <option value="11">Brick Endurance</option>
          </optgroup>
          <optgroup label="Gym">
            <option value="12">Gym Core/Specifics</option>
            <option value="13">Gym Lifting</option>
          </optgroup>
          <option value="15">Other training</option>
          {race_option}
          <option value="14">Rest day</option>
          <option value="17">Diary entry</option>
          </select>
        );
      }
      else {
        var discipline_select_input = <span style={{ fontSize: 13, lineHeight: '20px' }}>{this.props.training_session.discipline_display}</span>
      }

      var discipline_select = (
        <div className="discipline_select field-wrapper">
        {show_pb_button}
        {'discipline_select' in this.props.form_errors && <Errors errors={this.props.form_errors.discipline} />}
        <label htmlFor="id_discipline_select">Discipline:</label>
        {discipline_select_input}
        {mobile_other_discipline}
        </div>
      );

    }

    var subtitle;
    var key_session;
    if (this.props.columns == 1 && this.props.perspective == 'coach' && this.props.mode == 'training_session') {

      var readonly = this.allow_altering_discipline_and_intensity() ? false : true;

      subtitle = (
        <div className="field-wrapper inline training-session">
          {'subtitle' in this.props.form_errors && <Errors errors={this.props.form_errors.subtitle} />}
          <label htmlFor="id_subtitle">Subtitle:</label><input type="text" name="subtitle" id="id_subtitle" maxLength="100" readOnly={readonly} onChange={this.handleSubtitleChange} value={this.props.training_session.subtitle || ""} />
        </div>
      );

      // Templates don't have the key session option.
      if (!this.props.training_session.template) {

        if (!readonly) {
          var key_session_field = <input type="checkbox" name="key_session" id="id_key_session" checked={this.props.training_session.key_session ? true : false} onChange={this.handleKeySessionChange} />;
        }
        else {
          var key_session_field = <span>{this.props.training_session.key_session ? 'yes': 'no'}</span>;
        }

        key_session = (
          <div className="field-wrapper inline training-session">
            {'key_session' in this.props.form_errors && <Errors errors={this.props.form_errors.key_session} />}
            <label htmlFor="id_key_session">Key session:</label>
            {key_session_field}
          </div>
        );

      }

    }

    var sponsor;
    if (this.props.perspective == 'coach' && this.props.mode == 'training_session' && this.props.group_data && typeof(this.props.group_data.sponsors) != 'undefined' && this.props.group_data.sponsors.length > 0) {

      var sponsor_users = [];
      var sponsor_companies = [];
      for (var i = 0; i < this.props.group_data.sponsors.length; i++) {
        var sponsor = this.props.group_data.sponsors[i];
        if (sponsor.type == 'user') {
          sponsor_users.push(<option key={sponsor.id} value={'user-' + sponsor.id}>{sponsor.name}</option>)
        }
        else if (sponsor.type == 'company') {
          sponsor_companies.push(<option key={sponsor.id} value={'company-' + sponsor.id}>{sponsor.name}</option>)
        }
      }

      var sponsor = (
        <div className="sponsor field-wrapper">
          {'sponsor' in this.props.form_errors && <Errors errors={this.props.form_errors.sponsor} />}
          <label htmlFor="id_sponsor">Brough to you by:</label>
          <select name="sponsor" id="id_sponsor" value={this.props.training_session.sponsor || ""} onChange={this.handleSponsorChange}>
            <option value="">Please select</option>
            <optgroup label="Users">
              {sponsor_users}
            </optgroup>
            <optgroup label="Companies">
              {sponsor_companies}
            </optgroup>
          </select>
        </div>
      );

    }

    if (this.props.columns == 1) {
      return (
        <div className="title-section">
          {'discipline' in this.props.form_errors && <Errors errors={this.props.form_errors.discipline} />}
          {discipline_title}
          {discipline_select}
          <TransitionGroup>{additional_discipline_data ? additional_discipline_data : null}</TransitionGroup>
          {subtitle}
          {key_session}
          {sponsor}
        </div>
      )
    }
    else {

      let border_color = (this.props.mode == 'training_session' && this.props.training_session.intensity) ? COLOURS.intensity[this.props.training_session.intensity] : '#000000';

      return (
        <div style={{overflow: 'auto', borderTop: "3px solid", borderColor: border_color}}>
          {'discipline' in this.props.form_errors && <Errors errors={this.props.form_errors.discipline} />}
          {discipline_title}
          {'intensity' in this.props.form_errors && <Errors errors={this.props.form_errors.intensity} />}
          {intensity_title}
          {schedule_title}
          {discipline_select}
          {subtitle}
          {key_session}
          {sponsor}
        </div>
      )
    }
  }
}


class DisciplineWidget extends React.Component {

  change_discipline = (event) => {
    this.props.training_session.discipline_id = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);

    // Close the intensity and discipline widgets if an intensity has been
    // selected already, it is a race, diary or rest day so intensity is
    // irrelevant, it is not in mobile mode and we are not selecting
    // 'other discipline' (which requires further input).
    var discipline_category = this.props.training_session.getDisciplineCategory();
    if ((!this.props.intensity_widget_open || this.props.training_session.intensity || discipline_category == 'race' || discipline_category == 'diary' || discipline_category == 'rest') && this.props.columns == 3&& event.target.value != 15) {
      this.props.training_session_form.setState({discipline_widget_open: false, intensity_widget_open: false});
    }

    if (event.target.value == 15) {
      $('#id_other_discipline').focus();
    }
  }

  closeWidget = (event) => {
    this.props.training_session_form.setState({discipline_widget_open: false, intensity_widget_open: false});
  }

  change_other_discipline = (event) => {
    this.props.training_session.other_discipline = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  other_discipline_clicked = (event) => {
    this.props.training_session.discipline_id = 15;
    this.props.updateTrainingSession(this.props.training_session);
  }

  other_discipline_key_pressed = (event) => {
    // When the enter key (code 13) is pressed while editing other discipline,
    // close the widget rather than submitting the form.
    var code = (event.keyCode ? event.keyCode : event.which);
    if (code == 13) {
      event.preventDefault();
      this.props.training_session_form.setState({discipline_widget_open: false, intensity_widget_open: false});
    }
  }

  render() {

    // Work out what type of file we are importing (if any).
    var disciplines = [];
    if (this.props.related_training_logs) {
      for (var i = 0; i < this.props.related_training_logs.length; i++) {
        var training_log = this.props.related_training_logs[i];
        var importing = (this.props.file_ids_marked_to_import.indexOf(training_log.file_data.id) >= 0) ? true : false;
        if (importing) {
          disciplines.push(training_log.file_data.activity_type);
        }
      }
    }

    disciplines = disciplines.getUniqueValues();

    if (disciplines.length < 1) {
      var importing_discipline = 'none';
    }
    else if (disciplines.length > 1) {
      var importing_discipline = 'brick';
    }
    else if (disciplines[0] == 'swimming') {
      var importing_discipline = 'swim';
    }
    else if (disciplines[0] == 'running') {
      var importing_discipline = 'run';
    }
    else if (disciplines[0] == 'cycling') {
      var importing_discipline = 'bike';
    }
    else {
      var importing_discipline = 'unknown';
    }

    // Swapping between a race and a training session after it has saved is
    // going to require creating a whole new database entry and deleting the
    // existing one. This is unlikely to occur, so we don't support doing this.
    var race_option;
    if (!this.props.training_session.id || (typeof(this.props.training_session.id) == 'string' && this.props.training_session.id.substring(0, 4) == 'temp')) {
      race_option = (
        <div className="race">
          <h5>Race</h5>
          <ul>
            <li className="discipline-16"><label htmlFor="id_discipline_radio_16"><input type="radio" id="id_discipline_radio_16" value="16" checked={'16' == this.props.training_session.discipline_id || this.props.mode == 'race'} onChange={this.change_discipline} name="discipline_radio" /> Race</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
          </ul>
        </div>
      );
    }

    // Hide training disciplines if the user is not allowed to create them.
    var full_coaching_message, swim, bike, run, brick, gym, other, desktop_other_discipline;
    if (can_edit_training_plans_on_date(this.props.training_session.date)) {

      var swim;
      if (importing_discipline == 'none' || importing_discipline == 'swim' || importing_discipline == 'unknown') {
        swim = (
          <div className="swim">
            <h5>Swim</h5>
            <ul>
              <li className="discipline-1"><label htmlFor="id_discipline_radio_1"><input type="radio" id="id_discipline_radio_1" value="1" checked={'1' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Swim Tech</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
              <li className="discipline-2"><label htmlFor="id_discipline_radio_2"><input type="radio" id="id_discipline_radio_2" value="2" checked={'2' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Swim Intervals</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
              <li className="discipline-3"><label htmlFor="id_discipline_radio_3"><input type="radio" id="id_discipline_radio_3" value="3" checked={'3' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Swim Endurance</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
            </ul>
          </div>
        );
      }
      var bike;
      if (importing_discipline == 'none' || importing_discipline == 'bike' || importing_discipline == 'unknown') {
        bike = (
          <div className="bike">
            <h5>Bike</h5>
            <ul>
              <li className="discipline-4"><label htmlFor="id_discipline_radio_4"><input type="radio" id="id_discipline_radio_4" value="4" checked={'4' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Bike Intervals</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
              <li className="discipline-5"><label htmlFor="id_discipline_radio_5"><input type="radio" id="id_discipline_radio_5" value="5" checked={'5' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Turbo Intervals</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
              <li className="discipline-6"><label htmlFor="id_discipline_radio_6"><input type="radio" id="id_discipline_radio_6" value="6" checked={'6' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Bike Endurance</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
            </ul>
          </div>
        );
      }
      var run;
      if (importing_discipline == 'none' || importing_discipline == 'run' || importing_discipline == 'unknown') {
        run = (
          <div className="run">
            <h5>Run</h5>
            <ul>
              <li className="discipline-7"><label htmlFor="id_discipline_radio_7"><input type="radio" id="id_discipline_radio_7" value="7" checked={'7' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Run Intervals</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
              <li className="discipline-8"><label htmlFor="id_discipline_radio_8"><input type="radio" id="id_discipline_radio_8" value="8" checked={'8' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Run Endurance</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
              <li className="discipline-9"><label htmlFor="id_discipline_radio_9"><input type="radio" id="id_discipline_radio_9" value="9" checked={'9' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Run Tempo</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
            </ul>
          </div>
        );
      }

      // Brick is always an option no matter what we are importing.
      var brick = (
        <div className="brick">
          <h5>Brick</h5>
          <ul>
            <li className="discipline-10"><label htmlFor="id_discipline_radio_10"><input type="radio" id="id_discipline_radio_10" value="10" checked={'10' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Brick Intervals</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
            <li className="discipline-11"><label htmlFor="id_discipline_radio_11"><input type="radio" id="id_discipline_radio_11" value="11" checked={'11' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Brick Endurance</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
          </ul>
        </div>
      );
      var gym;
      if (importing_discipline == 'none' || importing_discipline == 'unknown') {
        gym = (
          <div className="gym">
            <h5>Gym</h5>
            <ul>
              <li className="discipline-12"><label htmlFor="id_discipline_radio_12"><input type="radio" id="id_discipline_radio_12" value="12" checked={'12' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Gym Core/Specifics</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
              <li className="discipline-13"><label htmlFor="id_discipline_radio_13"><input type="radio" id="id_discipline_radio_13" value="13" checked={'13' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Gym Lifting</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
            </ul>
          </div>
        );
      }

      var other;
      if (importing_discipline == 'none' || importing_discipline == 'unknown') {
        if (this.props.columns == 3) {
          desktop_other_discipline = (
            <div id="other-discipline" className="field-wrapper ">
              <label htmlFor="id_other_discipline">brief description</label>
              <input type="text" name="other_discipline" id="id_other_discipline" tabIndex="-1" maxLength="50" onKeyPress={this.other_discipline_key_pressed} onClick={this.other_discipline_clicked} value={this.props.training_session.other_discipline || ''} onChange={this.change_other_discipline} style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0'}} />
              <div className="continue" onClick={this.closeWidget}>&#8618;</div>
            </div>
          );
        }

        var other = (
          <div className="other">
            <h5>Other training</h5>
            <ul>
              <li className="discipline-15"><label htmlFor="id_discipline_radio_15"><input type="radio" id="id_discipline_radio_15" value="15" checked={'15' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Other training</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
            </ul>
            {desktop_other_discipline}
          </div>
        );
      }
    }
    else {
      full_coaching_message = (
        <div className="full-coaching-message">
          <p>Future training sessions<br /> are scheduled by your coach.</p>
          <p>You can copy diary entries by dragging and dropping them from one day to another. Or move them by holding down the shift key while dragging. On an iPad, press and hold down on a diary entry for a second to start dragging.</p>
        </div>
      );
    }

    var rest;
    if (importing_discipline == 'none') {
      rest = (
        <div className="rest">
          <h5>Rest</h5>
          <ul>
            <li className="discipline-14"><label htmlFor="id_discipline_radio_14"><input type="radio" id="id_discipline_radio_14" value="14" checked={'14' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Rest day</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
          </ul>
        </div>
      );
    }

    var diary;
    if (importing_discipline == 'none') {
      diary = (
        <div className="diary">
          <h5>Diary entry</h5>
          <ul>
            <li className="discipline-17"><label htmlFor="id_discipline_radio_17"><input type="radio" id="id_discipline_radio_17" value="17" checked={'17' == this.props.training_session.discipline_id} onChange={this.change_discipline} name="discipline_radio" /> Diary entry</label><span className="previous-session" style={{float: 'right', margin: '1px 0'}}></span></li>
          </ul>
        </div>
      );
    }

    return (
      <div className="discipline-widget">
        <div className="discipline-widget-inner">
          {swim}
          {bike}
          {run}
          {brick}
          {gym}
          {other}
          {race_option}
          {rest}
          {diary}
          {full_coaching_message}
        </div>
      </div>
    )
  }
}

class DisciplineWidgetMainCategory extends React.Component {

  handleClick = () => {
    this.props.changeDiscipline(this.props.discipline_id);
  }

  render () {
    return (
      <div className={"discipline-dropdown__main-category discipline-dropdown__main-category-" + this.props.machine_name} onClick={this.handleClick}>
        <h5>{this.props.title}</h5>
      </div>
    )
  }
}

class DisciplineSubCategory extends React.Component {

  handleClick = () => {
    this.props.changeDiscipline(this.props.id);
  }

  render() {
    return <span className="small-button" onClick={this.handleClick}>{this.props.name}</span>
  }

}

class DropDown extends React.Component {

  changeDiscipline = (discipline_id) => {
    this.props.training_session.discipline_id = discipline_id;
    this.props.updateTrainingSession(this.props.training_session);

    // Close the intensity and discipline widgets if an intensity has been
    // selected already, it is a race, diary or rest day so intensity is
    // irrelevant, it is not in mobile mode and we are not selecting
    // 'other discipline' (which requires further input).
    if (this.props.training_session.intensity && subcategoryChosen(this.props.training_session.discipline_id)) {
      this.props.training_session_form.setState({discipline_widget_open: false, intensity_widget_open: false});
    }

    if (discipline_id == 15) {
      $('#id_other_discipline').focus();
    }

  }

  closeWidget = (event) => {
    this.props.training_session_form.setState({discipline_widget_open: false, intensity_widget_open: false, schedule_widget_open: false});
  }

  change_other_discipline = (event) => {
    this.props.training_session.other_discipline = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  other_discipline_clicked = (event) => {
    this.props.training_session.discipline_id = 15;
    this.props.updateTrainingSession(this.props.training_session);
  }

  other_discipline_key_pressed = (event) => {
    // When the enter key (code 13) is pressed while editing other discipline,
    // close the widget rather than submitting the form.
    var code = (event.keyCode ? event.keyCode : event.which);
    if (code == 13) {
      event.preventDefault();
      this.props.training_session_form.setState({discipline_widget_open: false, intensity_widget_open: false});
    }
  }

  getZoneName = (intensity) => {

    // Bike and run zones should be the same so we just grab it from the run.
    if (this.props.training_zones && this.props.training_zones.run.zones && this.props.training_zones.run.zones[intensity - 1].name) {
      return this.props.training_zones.run.zones[intensity - 1].name;
    }

    switch (intensity) {
      case 1:
        return 'Easy'
      case 2:
        return 'Steady'
      case 3:
        return 'Moderate'
      case 4:
        return 'Hard'
      case 5:
        return 'Max'
    }
  }

  zonesContain = (training_zones, discipline, zone_type) => {

    if (!training_zones || !training_zones[discipline] || !training_zones[discipline].zones) {
      return false;
    }

    for (var i = 0; i < training_zones[discipline].zones.length; i++) {
      if (training_zones[discipline].zones[i][zone_type + '_min'] || training_zones[discipline].zones[i][zone_type + '_max']) {
        return true;
      }
    }
    return false;
  }

  render() {

    // Work out what type of file we are importing (if any).
    var disciplines = [];
    if (this.props.related_training_logs) {
      for (var i = 0; i < this.props.related_training_logs.length; i++) {
        var training_log = this.props.related_training_logs[i];
        var importing = (this.props.file_ids_marked_to_import.indexOf(training_log.file_data.id) >= 0) ? true : false;
        if (importing) {
          disciplines.push(training_log.file_data.activity_type);
        }
      }
    }

    disciplines = disciplines.getUniqueValues();

    if (disciplines.length < 1) {
      var importing_discipline = 'none';
    }
    else if (disciplines.length > 1) {
      var importing_discipline = 'brick';
    }
    else if (disciplines[0] == 'swimming') {
      var importing_discipline = 'swim';
    }
    else if (disciplines[0] == 'running') {
      var importing_discipline = 'run';
    }
    else if (disciplines[0] == 'cycling') {
      var importing_discipline = 'bike';
    }
    else {
      var importing_discipline = 'unknown';
    }

    // Swapping between a race and a training session after it has saved is
    // going to require creating a whole new database entry and deleting the
    // existing one. This is unlikely to occur, so we don't support doing this.
    var race_option;
    if (!this.props.training_session.id || (typeof(this.props.training_session.id) == 'string' && this.props.training_session.id.substring(0, 4) == 'temp')) {
      race_option = <DisciplineWidgetMainCategory title="Race" machine_name="race" discipline_id="16" changeDiscipline={this.changeDiscipline} />
    }

    // Hide training disciplines if the user is not allowed to create them.
    var full_coaching_message, swim, bike, run, brick, gym, other, desktop_other_discipline;
    if (can_edit_training_plans_on_date(this.props.training_session.date)) {

      var swim;
      if (importing_discipline == 'none' || importing_discipline == 'swim' || importing_discipline == 'unknown') {
        swim = <DisciplineWidgetMainCategory title="Swim" machine_name="swim" discipline_id="18" changeDiscipline={this.changeDiscipline} />
      }
      var bike;
      if (importing_discipline == 'none' || importing_discipline == 'bike' || importing_discipline == 'unknown') {
        bike = <DisciplineWidgetMainCategory title="Bike" machine_name="bike" discipline_id="19" changeDiscipline={this.changeDiscipline} />
      }
      var run;
      if (importing_discipline == 'none' || importing_discipline == 'run' || importing_discipline == 'unknown') {
        run = <DisciplineWidgetMainCategory title="Run" machine_name="run" discipline_id="20" changeDiscipline={this.changeDiscipline} />
      }

      // Brick is always an option no matter what we are importing.
      var brick = <DisciplineWidgetMainCategory title="Brick" machine_name="brick" discipline_id="21" changeDiscipline={this.changeDiscipline} />
      var gym = <DisciplineWidgetMainCategory title="Gym" machine_name="gym" discipline_id="22" changeDiscipline={this.changeDiscipline} />

      var other;
      if (importing_discipline == 'none' || importing_discipline == 'unknown') {

        //if (this.props.mobile_mode == false) {
        //  desktop_other_discipline = (
        //    <div id="other-discipline" className="field-wrapper ">
        //      <label htmlFor="id_other_discipline">brief description</label>
        //      <input type="text" name="other_discipline" id="id_other_discipline" tabIndex="-1" maxLength="50" onKeyPress={this.other_discipline_key_pressed} onClick={this.other_discipline_clicked} value={this.props.training_session.other_discipline || ''} onChange={this.change_other_discipline} style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0'}} />
        //      <div className="continue" onClick={this.closeWidget}>&#8618;</div>
        //    </div>
        //  );
        //}

        other = <DisciplineWidgetMainCategory title="Other" machine_name="other" discipline_id="15" changeDiscipline={this.changeDiscipline} />
      }
    }
    else {
      full_coaching_message = (
        <div className="full-coaching-message">
          <p>Future training sessions<br /> are scheduled by your coach.</p>
          <p>You can copy diary entries by dragging and dropping them from one day to another. Or move them by holding down the shift key while dragging. On an iPad, press and hold down on a diary entry for a second to start dragging.</p>
        </div>
      );
    }

    var rest;
    if (importing_discipline == 'none') {
      rest = <DisciplineWidgetMainCategory title="Rest" machine_name="rest" discipline_id="14" changeDiscipline={this.changeDiscipline} />
    }

    var diary;
    if (importing_discipline == 'none') {
      diary = <DisciplineWidgetMainCategory title="Diary" machine_name="diary" discipline_id="17" changeDiscipline={this.changeDiscipline} />
    }

    var discipline_category = this.props.training_session.getDisciplineCategory()
    var discipline_sub_categories = [];
    switch (discipline_category) {
      case 'swim':
        discipline_sub_categories.push(<DisciplineSubCategory key={1} id={1} name='Tech' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={2} id={2} name='Intervals' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={3} id={3} name='Endurance' changeDiscipline={this.changeDiscipline} />)
        break;
      case 'bike':
        discipline_sub_categories.push(<DisciplineSubCategory key={4} id={4} name='Intervals' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={5} id={5} name='Turbo' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={6} id={6} name='Endurance' changeDiscipline={this.changeDiscipline} />)
        break;
      case 'run':
        discipline_sub_categories.push(<DisciplineSubCategory key={7} id={7} name='Intervals' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={8} id={8} name='Endurance' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={9} id={9} name='Tempo' changeDiscipline={this.changeDiscipline} />)
        break;
      case 'brick':
        discipline_sub_categories.push(<DisciplineSubCategory key={10} id={10} name='Intervals' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={11} id={11} name='Endurance' changeDiscipline={this.changeDiscipline} />)
        break;
      case 'gym':
        discipline_sub_categories.push(<DisciplineSubCategory key={12} id={12} name='Core/Specifics' changeDiscipline={this.changeDiscipline} />)
        discipline_sub_categories.push(<DisciplineSubCategory key={13} id={13} name='Lifting' changeDiscipline={this.changeDiscipline} />)
        break;
    }

    var dropdown_style = {
      position: 'absolute',
      display: 'block',
      left: '0',
      zIndex: '10',
      width: '33%',
    }

    var dropdown_inner_style = {
      borderBottomLeftRadius: '5px',
      borderBottomRightRadius: '5px',
      background: 'white',
      boxShadow: 'rgba(0, 0, 0, 0.2) 1px 1px 15px',
      margin: '0 13px',
      overflow: 'auto',
    }

    var subcategory_section;
    if (discipline_sub_categories.length > 0) {
      subcategory_section = (
        <div className="sub-categories" style={{padding: '0 10px 10px', clear: 'both'}}>
          <h3>Subcategory</h3>
          {discipline_sub_categories}
        </div>
      );
    }

    return (
      <div className="session-dropdown" style={dropdown_style}>
        <div className="session-dropdown__inner" style={dropdown_inner_style}>
          <div>
            {swim}
            {bike}
            {run}
            {brick}
            {gym}
            {other}
            {race_option}
            {rest}
            {diary}
            {full_coaching_message}
          </div>
          {subcategory_section}
        </div>
      </div>
    )
  }
}

function subcategoryChosen(discipline_id) {
  switch (parseInt(discipline_id)) {
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
    case 9:
    case 10:
    case 11:
    case 12:
    case 13:
    case 14:
    case 15:
    case 16:
    case 17:
    case 18:
    case 19:
    case 20:
    case 21:
    case 22:
      return true;
    default:
      return false;
  }
}

class IntensityOption extends React.Component {

  change_intensity = (event) => {

    if (!hasSessionPlanEditPermission(this.props.training_session, this.props.athlete_data, this.props.group_data, this.props.coaching_companies)) {
      return;
    }

    if (subcategoryChosen(this.props.training_session.discipline_id) && this.props.columns == 3) {
      this.props.training_session_form.setState({discipline_widget_open: false, intensity_widget_open: false});
    }
    this.props.training_session.intensity = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  render() {

    var discipline_category = this.props.training_session.getDisciplineCategory();
    let read_only = !hasSessionPlanEditPermission(this.props.training_session, this.props.athlete_data, this.props.group_data, this.props.coaching_companies);

    if (this.props.columns == 1) {

      var small_guide_text = [];

      // Add heart rate zone data for run.
      if (discipline_category == 'run' || discipline_category == 'brick') {
        if (this.props.level_number == 0) {
          if (this.props.has_run_heart_rate) {
            small_guide_text.push(<div key='small-guide-text-run-hr' className="run-heart-rate">Run HR</div>);
          }
          if (this.props.has_run_power) {
            small_guide_text.push(<div key='small-guide-text-run-power' className="run-power">Run Watts</div>);
          }
          if (this.props.has_run_pace) {
            if (this.props.pace_unit == 'miles') {
              small_guide_text.push(<div key='small-guide-text-run-pace' className="run-pace">Run min/mi</div>);
            }
            else {
              small_guide_text.push(<div key='small-guide-text-run-pace' className="run-pace">Run min/km</div>);
            }
          }
        }
        else {
          if (this.props.has_run_heart_rate) {
            small_guide_text.push(<div key='small-guide-text-run-hr' className="run-heart-rate">{this.props.run_zone.heart_rate_min}-{this.props.run_zone.heart_rate_max}</div>);
          }
          if (this.props.has_run_power) {
            small_guide_text.push(<div key='small-guide-text-run-power' className="run-power">{this.props.run_zone.power_min}-{this.props.run_zone.power_max}</div>);
          }
          if (this.props.has_run_pace) {
            if (this.props.pace_unit == 'miles') {
              small_guide_text.push(<div key='small-guide-text-run-pace' className="run-pace">{formatTime(metersPerSecondAsSecondsPerMile(this.props.run_zone.meters_per_second_slowest)) + '-' + formatTime(metersPerSecondAsSecondsPerMile(this.props.run_zone.meters_per_second_fastest))}</div>);
            }
            else {
              small_guide_text.push(<div key='small-guide-text-run-pace' className="run-pace">{formatTime(metersPerSecondAsSecondsPerKilometer(this.props.run_zone.meters_per_second_slowest)) + '-' + formatTime(metersPerSecondAsSecondsPerKilometer(this.props.run_zone.meters_per_second_fastest))}</div>);
            }
          }
        }
      }

      // Add heart rate zone data for bike.
      if (discipline_category == 'bike' || discipline_category == 'brick') {
        if (this.props.level_number == 0) {
          if (this.props.has_bike_heart_rate) {
            small_guide_text.push(<div key='small-guide-text-bike-hr' className="bike-heart-rate">Bike HR</div>);
          }
          if (this.props.has_bike_power) {
            small_guide_text.push(<div key='small-guide-text-bike-power' className="bike-heart-rate">Bike Watts</div>);
          }
        }
        else {
          if (this.props.has_bike_heart_rate) {
            small_guide_text.push(<div key='small-guide-text-bike-hr' className="bike-heart-rate">{this.props.bike_zone.heart_rate_min}-{this.props.bike_zone.heart_rate_max}</div>);
          }
          if (this.props.has_bike_power) {
            small_guide_text.push(<div key='small-guide-text-bike-power' className="bike-power">{this.props.bike_zone.power_min}-{this.props.bike_zone.power_max}</div>);
          }
        }
      }

      let label_class_name = this.props.level_number == this.props.training_session.intensity ? 'active' : 'inactive';
      label_class_name += ' intensity-widget-option-label intensity-widget-option-label-' + (read_only ? 'disabled' : 'enabled');

      return (
        <li className={"level-" + this.props.level_number}>
          <label htmlFor={this.props.prefix + "_id_intensity_" + this.props.level_number} className={label_class_name}>
            <input type="radio" id={this.props.prefix + '_id_intensity_' + this.props.level_number} disabled={read_only} value={this.props.level_number} name="intensity" checked={this.props.level_number == this.props.training_session.intensity} onChange={this.change_intensity} />
            <span className="title">{"Level " + this.props.level_number}</span>
          </label>
          <div className="small-guide">
            {small_guide_text}
          </div>
        </li>
      );
    }
    else {

      var large_guide_text = [];

      // Add heart zone data for run.
      if (this.props.run_zone && (discipline_category == 'run' || discipline_category == 'brick' || discipline_category == 'none')) {
        if (this.props.run_zone && (this.props.run_zone.heart_rate_min || this.props.run_zone.power_min || this.props.run_zone.meters_per_second_slowest)) {
          var heart_rate = null;
          var power = null;
          var pace = null;
          if (this.props.run_zone.heart_rate_min) {
            heart_rate = <span>{this.props.run_zone.heart_rate_min}-{this.props.run_zone.heart_rate_max}<span className="unit">bpm</span></span>
          }
          if (this.props.run_zone.power_min) {
            power = <span>{this.props.run_zone.power_min}-{this.props.run_zone.power_max}<span className="unit">watts</span></span>
          }
          if (this.props.run_zone.meters_per_second_slowest) {
            if (this.props.pace_unit == 'miles') {
              pace = <span>{formatTime(metersPerSecondAsSecondsPerMile(this.props.run_zone.meters_per_second_slowest)) + '-' + formatTime(metersPerSecondAsSecondsPerMile(this.props.run_zone.meters_per_second_fastest))}<span className="unit">min/mile</span></span>
            }
            else {
              pace = <span>{formatTime(metersPerSecondAsSecondsPerKilometer(this.props.run_zone.meters_per_second_slowest)) + '-' + formatTime(metersPerSecondAsSecondsPerKilometer(this.props.run_zone.meters_per_second_fastest))}<span className="unit">min/km</span></span>
            }
          }
          large_guide_text.push(<span key='large-guide-text-run' className="run-heart-rate">Run {heart_rate} {power} {pace}</span>);
        }
      }

      // Add zone data for bike.
      if (this.props.bike_zone && (discipline_category == 'bike' || discipline_category == 'brick' || discipline_category == 'none')) {

        if (this.props.bike_zone && (this.props.bike_zone.heart_rate_min || this.props.bike_zone.power_min)) {
          var heart_rate = null;
          var power = null;
          if (this.props.bike_zone.heart_rate_min) {
            heart_rate = <span>{this.props.bike_zone.heart_rate_min}-{this.props.bike_zone.heart_rate_max}<span className="unit">bpm</span></span>
          }
          if (this.props.bike_zone.power_min) {
            power = <span>{this.props.bike_zone.power_min}-{this.props.bike_zone.power_max}<span className="unit">watts</span></span>
          }
          large_guide_text.push(<span key='large-guide-text-bike' className="bike-heart-rate">Bike {heart_rate} {power}</span>);
        }
      }

      return (
        <li className={"level-" + this.props.level_number}>
          <label htmlFor={this.props.prefix + "_id_intensity_" + this.props.level_number} className={this.props.level_number == this.props.training_session.intensity ? 'active' : 'inactive'}>
            <span className="description">
              <span className="guide">{this.props.guide_text}</span>
              {large_guide_text}
            </span>
            <input type="radio" id={this.props.prefix + '_id_intensity_' + this.props.level_number} value={this.props.level_number} name="intensity" checked={this.props.level_number == this.props.training_session.intensity} onChange={this.change_intensity} />
            <span className="title">{"Level " + this.props.level_number}</span>
          </label>
        </li>
      );
    }
  }
}

class SessionOrderButtons extends React.Component {

  handleSessionOrderUpClicked = () => {
    this.props.updateSessionOrder('up');
  }

  handleSessionOrderDownClicked = () => {
    this.props.updateSessionOrder('down');
  }

  render() {

    // Collect a list of training session ids in the order that they
    // are displayed as well as the index in that list of the current
    // session.
    var ordered_training_session_ids = [];
    var current_session_index = null;
    for (var i = 0; i < this.props.markers_on_this_date.length; i++) {
      if (this.props.markers_on_this_date[i].type == 'training session') {
        ordered_training_session_ids.push(this.props.markers_on_this_date[i].key);
        if (this.props.markers_on_this_date[i].key == String(this.props.training_session.uuid)) {
          current_session_index = ordered_training_session_ids.length - 1;
        }
      }
    }

    // Create appropriate buttons depending on where the session is in the
    // list.
    var up_button, down_button;

    var button_style = {
      width: '26px',
      textAlign: 'center',
      display: 'inline-block'
    }

    if (current_session_index == 0 || current_session_index == null) {
      // First training session in the list so the up button is disabled.
      up_button = <span className="session-form__session-order-up-button session-form__session-order-up-button_disabled small-button small-button_disabled" style={button_style}>up</span>
    }
    else {
      // There is a training session above so the up button is enabled.
      up_button = <span className="session-form__session-order-up-button session-form__session-order-up-button_enabled small-button small-button_enabled" onClick={this.handleSessionOrderUpClicked} style={button_style}>up</span>
    }

    if (current_session_index == ordered_training_session_ids.length - 1 || current_session_index == null) {
      // Last training session in the list so the down button is disabled.
      down_button = <span className="session-form__session-order-down-button session-form__session-order-down-button_disabled small-button small-button_disabled" style={button_style}>down</span>
    }
    else {
      // There are sessions below the list so the down button is enabled.
      down_button = <span className="session-form__session-order-down-button session-form__session-order-down-button_enabled small-button small-button_enabled" onClick={this.handleSessionOrderDownClicked} style={button_style}>down</span>
    }

    return <span className="session-form__session_order_buttons">{up_button} {down_button}</span>;
  }

}

class ScheduleWidget extends React.Component {

  handleChangeTrainingPlanDay = (event) => {
    if (this.props.mode == 'training_session') {
      this.props.training_session.training_plan_day = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
    else {
      this.props.race.training_plan_day = event.target.value;
      this.props.updateRace(this.props.race);
    }
  }

  handleScheduleTimeField = (event) => {
    if (this.props.mode == 'training_session') {
      this.props.training_session.time = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  handleSelectTimeZone = (time_zone) => {
    if (this.props.mode == 'training_session') {
      this.props.training_session.time_zone = time_zone.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  changeDate = (date) => {

    const date_string = (date) ? date.format('Y-m-d') : null;

    if (this.props.mode == 'training_session') {
      // The extra check to compare dates here is required in case we
      // had to use the onblur event instead of onChange. This is a
      // workaround for a problem with Firefox on Android not recognising
      // the change event.
      if (this.props.training_session.date != date_string) {
        this.props.training_session.date = date_string;
        this.props.updateTrainingSession(this.props.training_session, true);
        this.props.training_session_form.setState({schedule_widget_open: false});
      }
    }
    else {
      if (this.props.race.date != date_string) {
        this.props.race.date = newDateFromDatestring(date_string);
        this.props.updateRace(this.props.race, true);
        this.props.training_session_form.setState({schedule_widget_open: false});
      }
    }
  }

  render() {

    if (this.props.mode == 'closed') {
      return false;
    }

    var reschedule_date_field;
    let date_string;
    if (this.props.mode == 'training_session') {
      date_string = this.props.training_session.date;
    }
    else if (this.props.race.date) {
      date_string = this.props.race.getDateAsString();
    }

    let time_field;
    if (hasBetaAccess('google_calendar_connection') && this.props.mode == 'training_session' && !this.props.training_session.template) {
      time_field = (
        <span>
          <input
            type="time"
            step="60"
            value={this.props.training_session.time || ""}
            onChange={this.handleScheduleTimeField}
          />
          <div style={{display: 'inline-block', marginLeft: '5px'}}>
            <TimezoneSelect
              value={this.props.training_session.time_zone || "Europe/London"}
              onChange={this.handleSelectTimeZone}
            />
          </div>
        </span>
      );
    }

    if (this.props.columns == 1) {

      if ((this.props.training_session && this.props.training_session.training_plan_id) || (this.props.race && this.props.race.training_plan_id)) {
        let training_plan_day = this.props.mode == 'training_session' ? this.props.training_session.training_plan_day : this.props.race.training_plan_day;
        var date_element = (
          <div className="reschedule-date" style={{display: 'inline-block', marginTop: '10px'}}>
            <label style={{width: '122px', display: 'inline-block', paddingLeft: '5px'}} htmlFor="id_date">Day of plan</label>
            <input id="id_date" type="number" className="session-form__number-field" value={training_plan_day} onChange={this.handleChangeTrainingPlanDay} />
          </div>
        );
      }
      else if (hasSessionReschedulePermission(this.props.training_session, this.props.athlete_data, this.props.group_data)) {

        // Reverse date format.
        const date = (date_string) ? newDateFromDatestring(date_string) : null;
        var date_element = (
          <div className="reschedule-date" style={{display: 'inline-block', marginTop: '10px'}}>
            <label style={{width: '122px', display: 'inline-block', paddingLeft: '5px'}} htmlFor="id_date">Reschedule date:</label>
            <DatePicker
              fixedHeight
              selected={date}
              dateFormat="dd/MM/yyyy"
              id="id_date"
              locale="en-GB"
              name="date"
              onChange={this.changeDate}
              required={true}
              popperPlacement="top"
            />
          </div>
        )

      }
      else {
        var date = (date_string) ? moment(date_string, 'YYYY-MM-DD') : null;

        if (date == null) {
          logMessage('Date not set probably due to session deletion', {
            id: this.props.training_session.id,
            deleted: this.props.training_session.deleted,
            type: this.props.training_session.type,
            date: this.props.training_session.date,
          });
        }

        var date_element = (
          <div className="reschedule-date" style={{display: 'inline-block', marginTop: '10px'}}>
            <label style={{width: '122px', display: 'inline-block', paddingLeft: '5px'}} htmlFor="id_date">Date</label>
            <span id="id_date" className="readonly">{date.format('Do MMM YYYY')}</span>
          </div>
        );
      }
    }
    else {
      if (hasSessionReschedulePermission(this.props.training_session, this.props.athlete_data, this.props.group_data)) {

        // Reverse date format.
        const date = (date_string) ? newDateFromDatestring(date_string) : null;

        var date_element = (
          <div style={{marginTop: '15px'}}>
            <h2 className="title">Reschedule date</h2>
            {'date' in this.props.form_errors && <Errors errors={this.props.form_errors.date} />}
            <DatePicker
              inline
              fixedHeight
              selected={date}
              dateFormat="dd/MM/yyyy"
              id="id_date"
              name="date"
              onChange={this.changeDate}
              required={true}
              popoverAttachment="top right"
              popoverTargetOffset='10px 115px'
            />
          </div>
        )

      }
      else {
        var date_display_string = (date) ? moment(date, 'YYYY-MM-DD').format('Do MMM YYYY') : null;
        var date_label = <label htmlFor="id_date">Date</label>
        var date_element = <span id="id_date" className="readonly">{date_display_string}</span>;
      }
    }

    var session_order_buttons;
    if (this.props.mode == 'training_session' && typeof(this.props.updateSessionOrder) != 'undefined' && this.props.columns == 1 && this.props.perspective == 'coach') {
      session_order_buttons = (
        <SessionOrderButtons
          perspective={this.props.perspective}
          training_session={this.props.training_session}
          markers_on_this_date={this.props.markers_on_this_date}
          updateSessionOrder={this.props.updateSessionOrder}
        />
      );
    }

    return (
      <div className="schedule-widget">
        <div className="schedule-widget-inner">
          {date_element} {session_order_buttons} {time_field}
        </div>
      </div>
    )
  }
}

export class IntensityWidget extends React.Component {

  getZoneName = (intensity) => {

    // Bike and run zones should be the same so we just grab it from the run.
    if (
      this.props.training_zones &&
      this.props.training_zones.run.zones &&
      this.props.training_zones.run.zones[intensity - 1] &&
      this.props.training_zones.run.zones[intensity - 1].name
    ) {
      return this.props.training_zones.run.zones[intensity - 1].name;
    }

    switch (intensity) {
      case 1:
        return 'Easy'
      case 2:
        return 'Steady'
      case 3:
        return 'Moderate'
      case 4:
        return 'Hard'
      case 5:
        return 'Max'
    }
  }

  render() {

    function zones_contain(training_zones, discipline, key_min, key_max) {

      if (!training_zones || !training_zones[discipline] || !training_zones[discipline].zones) {
        return false;
      }

      for (var i = 0; i < training_zones[discipline].zones.length; i++) {
        if (training_zones[discipline].zones[i][key_min] || training_zones[discipline].zones[i][key_max]) {
          return true;
        }
      }

      return false;
    }

    var has_bike_heart_rate = zones_contain(this.props.training_zones, 'bike', 'heart_rate_min', 'heart_rate_max');
    var has_bike_power = zones_contain(this.props.training_zones, 'bike', 'power_min', 'power_max');
    var has_run_heart_rate = zones_contain(this.props.training_zones, 'run', 'heart_rate_min', 'heart_rate_max');
    var has_run_power = zones_contain(this.props.training_zones, 'run', 'power_min', 'power_max');
    var has_run_pace = zones_contain(this.props.training_zones, 'run', 'meters_per_second_fastest', 'meters_per_second_slowest');

    var readonly = (hasSessionPlanEditPermission(this.props.training_session, this.props.athlete_data, this.props.group_data, this.props.coaching_companies)) ? false : true;
    const distance_unit = this.props.training_session.getDistanceUnit();

    return (
      <div className="intensity-widget session-form__intensity-widget" readOnly={readonly}>
        <div className="intensity-widget-inner session_form__intensity-widget_inner">
          <label htmlFor="id_intensity">Intensity:</label>
          <ul>
            <IntensityOption key='0' level_number='0' athlete_data={this.props.athlete_data} group_data={this.props.group_data} coaching_companies={this.props.coaching_companies} guide_text="Don't specify intensity" training_session={this.props.training_session} updateTrainingSession={this.props.updateTrainingSession} training_session_form={this.props.training_session_form} prefix={this.props.prefix} has_bike_heart_rate={has_bike_heart_rate} has_bike_power={has_bike_power} has_run_heart_rate={has_run_heart_rate} has_run_power={has_run_power} has_run_pace={has_run_pace} columns={this.props.columns} pace_unit={distance_unit} />
            <IntensityOption key='1' level_number='1' bike_zone={this.props.training_zones && this.props.training_zones.bike.zones ? this.props.training_zones.bike.zones[0] : null} run_zone={this.props.training_zones && this.props.training_zones.run.zones ? this.props.training_zones.run.zones[0] : null} athlete_data={this.props.athlete_data} group_data={this.props.group_data} coaching_companies={this.props.coaching_companies} guide_text={this.getZoneName(1)} training_session={this.props.training_session} updateTrainingSession={this.props.updateTrainingSession} columns={this.props.columns} training_session_form={this.props.training_session_form} prefix={this.props.prefix} has_bike_heart_rate={has_bike_heart_rate} has_bike_power={has_bike_power} has_run_heart_rate={has_run_heart_rate} has_run_power={has_run_power} has_run_pace={has_run_pace} pace_unit={distance_unit} />
            <IntensityOption key='2' level_number='2' bike_zone={this.props.training_zones && this.props.training_zones.bike.zones ? this.props.training_zones.bike.zones[1] : null} run_zone={this.props.training_zones && this.props.training_zones.run.zones ? this.props.training_zones.run.zones[1] : null} athlete_data={this.props.athlete_data} group_data={this.props.group_data} coaching_companies={this.props.coaching_companies} guide_text={this.getZoneName(2)} training_session={this.props.training_session} updateTrainingSession={this.props.updateTrainingSession} columns={this.props.columns} training_session_form={this.props.training_session_form} prefix={this.props.prefix} has_bike_heart_rate={has_bike_heart_rate} has_bike_power={has_bike_power} has_run_heart_rate={has_run_heart_rate} has_run_power={has_run_power} has_run_pace={has_run_pace} pace_unit={distance_unit} />
            <IntensityOption key='3' level_number='3' bike_zone={this.props.training_zones && this.props.training_zones.bike.zones ? this.props.training_zones.bike.zones[2] : null} run_zone={this.props.training_zones && this.props.training_zones.run.zones ? this.props.training_zones.run.zones[2] : null} athlete_data={this.props.athlete_data} group_data={this.props.group_data} coaching_companies={this.props.coaching_companies} guide_text={this.getZoneName(3)} training_session={this.props.training_session} updateTrainingSession={this.props.updateTrainingSession} columns={this.props.columns} training_session_form={this.props.training_session_form} prefix={this.props.prefix} has_bike_heart_rate={has_bike_heart_rate} has_bike_power={has_bike_power} has_run_heart_rate={has_run_heart_rate} has_run_power={has_run_power} has_run_pace={has_run_pace} pace_unit={distance_unit} />
            <IntensityOption key='4' level_number='4' bike_zone={this.props.training_zones && this.props.training_zones.bike.zones ? this.props.training_zones.bike.zones[3] : null} run_zone={this.props.training_zones && this.props.training_zones.run.zones ? this.props.training_zones.run.zones[3] : null} athlete_data={this.props.athlete_data} group_data={this.props.group_data} coaching_companies={this.props.coaching_companies} guide_text={this.getZoneName(4)} training_session={this.props.training_session} updateTrainingSession={this.props.updateTrainingSession} columns={this.props.columns} training_session_form={this.props.training_session_form} prefix={this.props.prefix} has_bike_heart_rate={has_bike_heart_rate} has_bike_power={has_bike_power} has_run_heart_rate={has_run_heart_rate} has_run_power={has_run_power} has_run_pace={has_run_pace} pace_unit={distance_unit} />
            <IntensityOption key='5' level_number='5' bike_zone={this.props.training_zones && this.props.training_zones.bike.zones ? this.props.training_zones.bike.zones[4] : null} run_zone={this.props.training_zones && this.props.training_zones.run.zones ? this.props.training_zones.run.zones[4] : null} athlete_data={this.props.athlete_data} group_data={this.props.group_data} coaching_companies={this.props.coaching_companies} guide_text={this.getZoneName(5)} training_session={this.props.training_session} updateTrainingSession={this.props.updateTrainingSession} columns={this.props.columns} training_session_form={this.props.training_session_form} prefix={this.props.prefix} has_bike_heart_rate={has_bike_heart_rate} has_bike_power={has_bike_power} has_run_heart_rate={has_run_heart_rate} has_run_power={has_run_power} has_run_pace={has_run_pace} pace_unit={distance_unit} />
          </ul>
        </div>
      </div>
    )
  }
}

export class RaceTypeField extends React.Component {

  render () {

    return (
        <div className="race-type field-wrapper race">
          {this.props.form_errors && <Errors errors={this.props.form_errors} />}
          <label htmlFor="id_race_type">Race type:</label>
          <select name="race_type" id="id_race_type" value={this.props.race_type || ""} onChange={this.props.onChange} required={this.props.required}>
            <option value="">Please select</option>
            <optgroup label="Triathlon">
              <option value="4">Sprint Triathlon</option>
              <option value="3">Standard / Olympic Triathlon</option>
              <option value="2">Middle / Half Iron Triathlon</option>
              <option value="1">Long / Iron Triathlon</option>
              <option value="5">Other Distance Triathlon</option>
            </optgroup>
            <optgroup label="Duathlon">
              <option value="41">Sprint Duathlon</option>
              <option value="42">Standard Duathlon</option>
              <option value="43">Other Distance Duathlon</option>
            </optgroup>
            <optgroup label="Aquabike">
              <option value="60">Aquabike</option>
            </optgroup>
            <optgroup label="Aquathlon">
              <option value="51">Sprint Aquathlon</option>
              <option value="52">Standard Aquathlon</option>
              <option value="53">Other Distance Aquathlon</option>
            </optgroup>
            <optgroup label="Swimming">
              <option value="31">Gala</option>
              <option value="32">Open water swim</option>
            </optgroup>
            <optgroup label="Cycling">
              <option value="24">Road race</option>
              <option value="23">Time trial</option>
              <option value="28">Gravel</option>
              <option value="25">Criterium</option>
              <option value="26">Sportive</option>
              <option value="21">Cyclocross</option>
              <option value="22">Mountain bike</option>
              <option value="27">Track</option>
            </optgroup>
            <optgroup label="Running">
              <option value="11">Road race</option>
              <option value="12">Cross country</option>
              <option value="13">Trail run</option>
            </optgroup>
            <option value="0">Other</option>
          </select>
        </div>
    );
  }
}

class RaceFieldsLeft extends React.Component {

  change_race_name = (event) => {
    this.props.race.race_name = event.target.value;
    this.props.updateRace(this.props.race);
  }

  race_stages_are_blank = () => {
    // Checks for any time or distance values in the race stages.
    // Also a saved form returns as changed.
    for (var index = 0; index < this.props.race.stages.length; index++) {
      var race_stage = this.props.race.stages[index];
      if (race_stage.actual_distance_meters || race_stage.advertised_distance_meters || race_stage.completed_seconds || race_stage.id) {
        return false;
      }
    }
    return true; // No data found if we reach here.
  }

  change_race_type = (event) => {
    this.props.race.race_type = event.target.value;

    // Add race stages if none are already set up.
    if (this.race_stages_are_blank()) {
      // Start from scratch if there are fields but none are filled in.
      this.props.race.stages = [];
    }

    if (this.props.race.stages.length == 0) {
      var default_stages = getDefaultStages(this.props.race.race_type);
      for (var index = 0; index < default_stages.length; index++) {
        this.props.race.stages.push(new RaceStage({discipline: default_stages[index], stage_number: index}))
      }
    }

    this.props.updateRace(this.props.race);
  }

  change_race_priority = (event) => {
    this.props.race.race_priority = event.target.value;
    this.props.updateRace(this.props.race);
  }

  change_taper_days = (event) => {
    this.props.race.taper_days = event.target.value;
    this.props.updateRace(this.props.race);
  }

  change_attendance_probability = (event) => {
    this.props.race.attendance_probability = event.target.value;
    this.props.updateRace(this.props.race);
  }

  change_race_url = (event) => {
    this.props.race.race_url = event.target.value;
    this.props.updateRace(this.props.race);
  }

  render() {

    return (
      <div className="race-form session-plan">
        <div className="field-wrapper">
          {'race_name' in this.props.form_errors && <Errors errors={this.props.form_errors.race_name} />}
          <label htmlFor="id_race_name">Race name:</label>
          <input type="text" name="race_name" id="id_race_name" maxLength="200" value={this.props.race.race_name || ""} onChange={this.change_race_name} />
        </div>

        <RaceTypeField
          form_errors={this.props.form_errors.race_type}
          race_type={this.props.race.race_type}
          onChange={this.change_race_type}
          required={false}
        />

        <div className="field-wrapper">
          {'race_priority' in this.props.form_errors && <Errors errors={this.props.form_errors.race_priority} />}
          <label htmlFor="id_race_priority">Race priority:</label>
          <select name="race_priority" id="id_race_priority" value={this.props.race.race_priority || ""} onChange={this.change_race_priority}>
            <option value="">Please select</option>
            <option value="a">A</option>
            <option value="b">B</option>
            <option value="c">C</option>
          </select>
        </div>

        <div className="field-wrapper">
          {'taper_days' in this.props.form_errors && <Errors errors={this.props.form_errors.taper_days} />}
          <label htmlFor="id_taper_days">Taper days:</label>
          <input type="number" className="session-form__number-field" name="taper_days" id="id_taper_days" value={isNaN(this.props.race.taper_days) ? "" : this.props.race.taper_days} onChange={this.change_taper_days} />
        </div>

        <div className="field-wrapper">
          {'attendance_probability' in this.props.form_errors && <Errors errors={this.props.form_errors.attendance_probability} />}
          <label htmlFor="id_attendance_probability">Attendance:</label>
          <select name="attendance_probability" id="id_attendance_probability" value={this.props.race.attendance_probability || ""} onChange={this.change_attendance_probability}>
            <option value="">Please select</option>
            <option value="1">Considering it</option>
            <option value="3">Applied for entry</option>
            <option value="2">Already booked</option>
          </select>
        </div>

        <div className="field-wrapper">
          {'race_url' in this.props.form_errors && <Errors errors={this.props.form_errors.race_url} />}
          <label htmlFor="id_race_url">Link to race site:</label>
          <input type="url" name="race_url" id="id_race_url" maxLength="200" value={this.props.race.race_url || ""} onChange={this.change_race_url} />
        </div>

      </div>
    )
  }
}

class RaceFieldsMiddle extends React.Component {

  changeAthletePlan = (event) => {
    this.props.race.athlete_plan = event.target.value;
    this.props.updateRace(this.props.race);
  }

  changeCoachPlan = (event) => {
    this.props.race.coach_plan = event.target.value;
    this.props.updateRace(this.props.race);
  }

  render() {

    if (this.props.perspective == 'athlete') {

      var athlete_race_plan = (
        <div className="field-wrapper">
          {'athlete_race_plan' in this.props.form_errors && <Errors errors={this.props.form_errors.athlete_race_plan} />}
          <label htmlFor="id_athlete_race_plan">Race plan:</label>
          <textarea name="athlete_race_plan" rows="10" cols="40" id="id_athlete_race_plan" className="session-form__athlete-race-plan" value={this.props.race.athlete_plan} onChange={this.changeAthletePlan}></textarea>
        </div>
      );

      var coach_race_plan;
      if (this.props.race.coach_plan) {
        coach_race_plan = (
          <div id="coach_race_plan">
            <h5>Coach race plans</h5>
            <div className="details">
              {get_text_as_jsx(this.props.race.coach_plan)}
            </div>
          </div>
        );
      }

    }
    else {

      var athlete_race_plan;

      if (this.props.race.has_permanent_id() && !this.props.is_group) {
        if (this.props.race.athlete_plan) {
          var athlete_plan = get_text_as_jsx(this.props.race.athlete_plan);
        }
        else {
          var athlete_plan = <p>No plan</p>;
        }
        athlete_race_plan = (
          <div id="athlete_race_plan">
            <h4>Athlete's race plans</h4>
            <div className="details">
              {athlete_plan}
            </div>
          </div>
        );
      }

      var coach_race_plan = (
        <div className="field-wrapper">
          {'coach_race_plan' in this.props.form_errors && <Errors errors={this.props.form_errors.coach_race_plan} />}
          <label htmlFor="id_coach_race_plan" style={{fontSize: '14px', fontWeight: 'bold', margin: '13px 0 2px', display: 'block'}}>Coach race plan</label>
          <textarea name="coach_race_plan" rows="10" cols="40" id="id_coach_race_plan" value={this.props.race.coach_plan} onChange={this.changeCoachPlan}></textarea>
        </div>
      );

    }

    return (
      <div className="session-log">
        {athlete_race_plan}
        {coach_race_plan}
      </div>
    );

  }
}

class RaceFieldsRight extends React.Component {

  changeAthleteRaceFeedback = (event) => {
    this.props.race.athlete_feedback = event.target.value;
    this.props.updateRace(this.props.race);
  }

  changeCoachComments = (event) => {
    this.props.race.coach_feedback = event.target.value;
    this.props.updateRace(this.props.race);
  }

  changePositionAgeGroup = (event) => {
    this.props.race.position_age_group = event.target.value;
    this.props.updateRace(this.props.race);
  }

  changePositionOverall = (event) => {
    this.props.race.position_overall = event.target.value;
    this.props.updateRace(this.props.race);
  }

  render() {

    var permalink;
    if (this.props.display_more_details_link && this.props.race.id && this.props.race.attached_training_logs && this.props.race.attached_training_logs.length > 0) {
      permalink = (<a href={'/race/' + this.props.race.id + '/'} className="permalink">Click for more details</a>);
    }

    if (this.props.perspective == 'coach') {
      var coach_race_comments = (
        <div className="field-wrapper coach-race-comments">
          {'race_coaches_comments' in this.props.form_errors && <Errors errors={this.props.form_errors.race_coaches_comments} />}
          <label htmlFor="id_race_coaches_comments" style={{fontSize: '14px', fontWeight: 'bold', margin: '13px 0 2px', display: 'block' }}>Coach race feedback</label>
          <textarea name="race_coaches_comments" rows="10" cols="40" id="id_race_coaches_comments" value={this.props.race.coach_feedback} onChange={this.changeCoachComments}></textarea>
        </div>
      );
    }
    else if (this.props.perspective == 'athlete') {
      var coach_race_comments;
      if (this.props.race.coach_feedback) {
        coach_race_comments = (
          <div id="coach_race_comments">
            <h5>Coach race comments</h5>
            <div className="details">
              {get_text_as_jsx(this.props.race.coach_feedback)}
            </div>
          </div>
        );
      }
    }

    if (this.props.perspective == 'coach') {
      if (this.props.race.athlete_feedback) {
        var athlete_feedback_text = get_text_as_jsx(this.props.race.athlete_feedback);
      }
      else {
        var athlete_feedback_text = <div>No feedback given.</div>
      }
      var athlete_feedback = (
        <div className="field-wrapper athlete-race-feedback">
          <h4>Athlete feedback</h4>
          <div className="comments">{athlete_feedback_text}</div>
        </div>
      );
    }
    else if (this.props.perspective == 'athlete') {
      var athlete_feedback = (
        <div className="field-wrapper athlete-race-feedback">
          {'athlete_race_feedback' in this.props.form_errors && <Errors errors={this.props.form_errors.athlete_race_feedback} />}
          <label htmlFor="id_athlete_race_feedback">How did it go?</label>
          <textarea
            className="session-form__athlete-race-feedback"
            name="athlete_race_feedback"
            rows="10"
            cols="40"
            id="id_athlete_race_feedback"
            value={this.props.race.athlete_feedback}
            onChange={this.changeAthleteRaceFeedback}
          ></textarea>
        </div>
      );
    }

    var position_readonly = (this.props.perspective == 'coach') ? true : false;

    var race_results;
    const disciplines = RaceStage.DISCIPLINE_OPTIONS;
    const distance_unit = this.props.race.getDistanceUnit();
    if (this.props.perspective == 'coach' && this.props.race.stages.length > 0 && this.props.columns == 1) {
      let stages = [];
      for (let i = 0; i < this.props.race.stages.length; i++) {

        const stage = this.props.race.stages[i];

        // Only show advertised distance when completed distance is not entered.
        let advertised_distance_cell;
        let completed_distance_cell;
        if (distance_unit == 'kilometers') {
          advertised_distance_cell = <td className="advertised-distance number">{stage.advertised_distance_meters ? formatDistance(stage.advertised_distance_meters, 2) : '-'}<span className="unit">km</span></td>;
          completed_distance_cell = <td className="actual-distance number" style={{fontWeight: 'bold'}}>{stage.actual_distance_meters ? formatDistance(stage.actual_distance_meters, 2) : '-'}<span className="unit">km</span></td>;
        }
        else if (distance_unit == 'miles') {
          advertised_distance_cell = <td className="advertised-distance number">{stage.advertised_distance_meters ? formatDistance(kilometersToMiles(stage.advertised_distance_meters), 2) : ''}<span className="unit">mi</span></td>
          completed_distance_cell = <td className="actual-distance number" style={{fontWeight: 'bold'}}>{stage.actual_distance_meters ? formatDistance(kilometersToMiles(stage.actual_distance_meters), 2) : ''}<span className="unit">mi</span></td>;
        }

        stages.push(
          <tr key={stage.id}>
            <td className="discipline" style={{textAlign: 'left'}}>{RACE_STAGE_DISCIPLINES[stage.discipline]}</td>
            {advertised_distance_cell}
            <td className="duration-goal number">{stage.goal_seconds ? formatTime(stage.goal_seconds) : '-'}</td>
            {completed_distance_cell}
            <td className="duration-completed number" style={{fontWeight: 'bold'}}>{stage.completed_seconds ? formatTime(stage.completed_seconds) : '-'}</td>
          </tr>
        );

      }

      race_results = (
        <div className="results">
          <h3>Results</h3>
          <table style={{textAlign: 'right', width: '378px', margin: '0 0px 0 -5px', fontSize: '13px', borderCollapse: 'collapse'}}>
            <thead>
              <tr>
                <th className="discipline" style={{textAlign: 'left', fontWeight: 'normal'}}>Stage</th>
                <th className="actual-distance number" style={{fontWeight: 'normal'}}>Advertised</th>
                <th className="duration-completed number" style={{fontWeight: 'normal'}}>Goal time</th>
                <th colSpan="2" className="actual-distance number">Completed</th>
              </tr>
            </thead>
            <tbody>
              {stages}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="4" className="number">Overall time</th>
                <td className="number overall-time" style={{fontWeight: 'bold'}}>{formatTime(this.props.race.overall_seconds)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    var rpe_field;
    if (!(this.props.athlete_data?.preferences && this.props.athlete_data?.preferences.hide_rate_of_perceived_exertion == 'y')) {
      rpe_field = <RPERow
        race={this.props.race}
        updateRace={this.props.updateRace}
        form_errors={this.props.form_errors}
      />
    }

    var position_age_group = (
      <div className="field-wrapper number position position-age-group race">
        {'position_age_group' in this.props.form_errors && <Errors errors={this.props.form_errors.position_age_group} />}
        <label htmlFor="id_position_age_group">Age group position:</label>
        <input type="number" className="session-form__number-field" name="position_age_group" id="id_position_age_group" value={this.props.race.position_age_group || ""} readOnly={position_readonly} onChange={this.changePositionAgeGroup} />
      </div>
    );

    var position_overall = (
      <div className="field-wrapper number position position-overall race">
        {'position_overall' in this.props.form_errors && <Errors errors={this.props.form_errors.position_overall} />}
        <label htmlFor="id_position_overall">Position overall:</label>
        <input type="number" className="session-form__number-field" name="position_overall" id="id_position_overall" value={this.props.race.position_overall || ""} readOnly={position_readonly} onChange={this.changePositionOverall} />
      </div>
    );

    if (this.props.columns == 1) {
      return (
        <div className="session-feedback">
          {athlete_feedback}
          {race_results}
          {rpe_field}
          {position_age_group}
          {position_overall}
          {permalink}
          {coach_race_comments}
        </div>
      );
    }
    else {
      return (
        <div className="session-feedback">
          {athlete_feedback}
          {race_results}
          {rpe_field}
          {position_age_group}
          {position_overall}
          {coach_race_comments}
          {permalink}
        </div>
      );
    }

  }
}

class AttachmentsField extends React.Component {

  handleRemoveAttachment = (event, attachment) => {
    event.stopPropagation();

    const attachment_id_to_remove = attachment.id;

    // Create a new array without the attachment with the specified ID
    const updated_attachments = this.props.training_session.attachment_ids.filter(id => id !== attachment_id_to_remove);

    // Update the training_session with the updated attachment IDs
    const updated_training_session = {
      ...this.props.training_session,
      attachment_ids: updated_attachments,
    };

    this.props.updateTrainingSession(updated_training_session);

    this.props.formChanged();
  }

  render () {

    let attachments = null;

      let add_attachment_button;
      if (this.props.content_attachments_meta_data?.uploading_granted && typeof(this.props.openMediaAttachmentSelector) == 'function') {
        add_attachment_button = <div
          className="training_session_form__add_content_attachment_button"
          onClick={this.props.openMediaAttachmentSelector.bind(null, 'training_plan')}>
            add attachment
        </div>
      }

      let attachment_items = [];
      if (
        typeof(this.props.session_to_use_for_plan.attachment_ids) != 'undefined' &&
        typeof(this.props.content_attachments) != 'undefined' &&
        this.props.session_to_use_for_plan.attachment_ids.length > 0
      ) {
        for (let i = 0; i < this.props.session_to_use_for_plan.attachment_ids.length; i++) {
          const attachment = this.props.content_attachments[this.props.session_to_use_for_plan.attachment_ids[i]];

          let remove_option;
          if (
            this.props.perspective == 'coach' &&
            typeof(this.props.openMediaAttachmentSelector) == 'function' &&
            this.props.current_user_id == attachment.owner_id
          ) {
            remove_option = <span
              className="content-attachment--remove"
              style={{float: 'right'}}
              onClick={(event) => this.handleRemoveAttachment(event, this.props.content_attachments[this.props.session_to_use_for_plan.attachment_ids[i]])}>
                remove
            </span>
          }

          attachment_items.push(
            <div
              className="content-attachments--content-attachment"
              style={{padding: '5px 5px 0 5px', display: 'flex', alignItems: 'center'}}
              key={this.props.session_to_use_for_plan.attachment_ids[i]}
            >
              <a style={{textDecoration: 'none', display: 'block', flexGrow: 1}} href={attachment.url} target="_blank">
                <img style={{ verticalAlign: 'middle', marginRight: '5px', borderRadius: '5px', background: '#ebebeb', color: '#ebebeb', display: 'inline-block', width: '50px', height: '50px' }} className="content-attachment--thumbnail" width="50" height="50" src={this.props.content_attachments[this.props.session_to_use_for_plan.attachment_ids[i]]?.thumbnail} />
                <span className="content-attachment--title">{this.props.content_attachments[this.props.session_to_use_for_plan.attachment_ids[i]]?.title}</span>
              </a>
              {remove_option}
            </div>
          );
        }
      }

      if (attachment_items.length > 0 || add_attachment_button) {
        attachments = (
          <div className="content-attachments training-plan-section">
            <div className="content-attachments training-plan-section-block" style={{paddingBottom: '5px'}}>
              {attachment_items}
              {add_attachment_button}
            </div>
          </div>
        );
      }

      return attachments


  }

}

class TrainingPlanFields extends React.Component {

  state = {
    unexpected_error: false,
  }

  componentDidCatch(error, info) {
    logError(error, info);
  }

  static getDerivedStateFromError(error) {
    return { unexpected_error: true };
  }

  change_prescribed_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.prescribed_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_prescribed_distance = (event) => {
    var discipline_category = this.props.training_session.getDisciplineCategory();
    if (discipline_category == 'swim') {
      var distance_unit = this.props.training_session.getPoolUnit();
    }
    else {
      var distance_unit = (this.props.training_session.getDistanceUnit() == 'kilometers') ? 'km' : 'miles';
    }
    if (distance_unit == 'meters') {
      this.props.training_session.prescribed_meters = event.target.value;
    }
    else if (distance_unit == 'yards') {
      this.props.training_session.prescribed_yards = event.target.value;
    }
    else if (distance_unit == 'km') {
      this.props.training_session.prescribed_km = event.target.value;
    }
    else if (distance_unit == 'miles') {
      this.props.training_session.prescribed_miles = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_prescribed_swim_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.prescribed_swim_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_prescribed_bike_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.prescribed_bike_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_prescribed_run_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.prescribed_run_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_prescribed_swim_distance = (event) => {
    if (this.props.training_session.getPoolUnit() == 'yards') {
      this.props.training_session.prescribed_swim_yards = event.target.value;
    }
    else if (this.props.training_session.getPoolUnit() == 'meters') {
      this.props.training_session.prescribed_swim_meters = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_prescribed_bike_distance = (event) => {
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      this.props.training_session.prescribed_bike_km = event.target.value;
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles') {
      this.props.training_session.prescribed_bike_miles = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_prescribed_run_distance = (event) => {
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      this.props.training_session.prescribed_run_km = event.target.value;
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles') {
      this.props.training_session.prescribed_run_miles = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  changePrescribedTrainingNotesUsingValue = (value) => {
    this.props.training_session.prescribed_training_notes = value;
    this.props.updateTrainingSession(this.props.training_session);
    this.props.formChanged();
  }

  // This is just here temporarily while we have a beta with two types of text field.
  changePrescribedTrainingNotes = (event) => {
    this.changePrescribedTrainingNotesUsingValue(event.target.value);
  }

  change_template_sharing = (event) => {
    this.props.training_session.template_sharing = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_diary_summary = (event) => {
    this.props.training_session.diary_summary = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  setToWriteOwnPlan = (event) => {
    this.props.training_session.group_parent_session.use_this_for_plan = false;
    this.props.updateTrainingSession(this.props.training_session, true);
  }

  setToUseGroupPlan = (event) => {
    this.props.training_session.group_parent_session.use_this_for_plan = true;
    this.props.updateTrainingSession(this.props.training_session, true);
  }

  handleSubtitleChange = (event) => {
    this.props.training_session.subtitle = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  render() {

    if (this.state.unexpected_error) {
      return <div style={{margin: '15px', lineHeight: '1.3em'}}>Sorry, there was a problem rendering this section due to a bug. If this problem continues please let us know so we can fix it.</div>;
    }

    let has_edit_permission = hasSessionPlanEditPermission(
      this.props.training_session,
      this.props.athlete_data,
      this.props.group_data,
      this.props.coaching_companies
    );

    let readonly = has_edit_permission ? false : true;

    // Allow group to override plan.
    if (this.props.training_session.using_group_parent_session()) {
      var session_to_use_for_plan = this.props.training_session.group_parent_session;
    }
    else {
      var session_to_use_for_plan = this.props.training_session;
    }

    var discipline_category = this.props.training_session.getDisciplineCategory();

    if (discipline_category == 'swim') {
      var distance_unit = this.props.training_session.getPoolUnit();
    }
    else {
      var distance_unit = (this.props.training_session.getDistanceUnit() == 'kilometers') ? 'km' : 'miles';
    }

    var print_training_plan;
    if (this.props.training_session.has_permanent_id() && discipline_category != 'rest' && discipline_category != 'diary' && !this.props.training_session.template) {
      print_training_plan = (<a style={{ float: 'right', fontSize: '13px', lineHeight: '19px'}} id="print-training-plan" className="training-session" href={'/coach/print-training-plan/' + this.props.training_session.id + '/'} target="_blank">print training plan</a>);
    }

    // Show prescribed minutes and distance on non-brick training sessions.
    var prescribed_minutes;
    var prescribed_distance;
    if (discipline_category == 'swim' || discipline_category == 'bike' || discipline_category == 'run' || discipline_category == 'gym' || discipline_category == 'other') {

      prescribed_minutes = (
        <div className="field-wrapper number">
          {'prescribed_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_minutes} />}
          <label htmlFor="id_prescribed_minutes">Time to train:</label>
          <input type="number" className="session-form__number-field" name="prescribed_minutes" step="1" readOnly={readonly} id="id_prescribed_minutes" min="0" value={session_to_use_for_plan.prescribed_minutes || ""} onChange={this.change_prescribed_minutes} /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      if (distance_unit == 'meters') {
        var prescribed_distance_value = decimalPlaces(session_to_use_for_plan.prescribed_meters, 0, 'null', false, true);
      }
      else if (distance_unit == 'yards') {
        var prescribed_distance_value = decimalPlaces(session_to_use_for_plan.prescribed_yards, 0, 'null', false, true);
      }
      else if (distance_unit == 'km') {
        var prescribed_distance_value = decimalPlaces(session_to_use_for_plan.prescribed_km, 3, 'null', false, true);
      }
      else if (distance_unit == 'miles') {
        var prescribed_distance_value = decimalPlaces(session_to_use_for_plan.prescribed_miles, 3, 'null', false, true);
      }

      prescribed_distance = (
        <div className="field-wrapper number">
          {'prescribed_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_distance} />}
          <label htmlFor="id_prescribed_distance">Distance to train:</label>
          <input type="number" className="session-form__number-field" name="prescribed_distance" step="0.001" id="id_prescribed_distance" readOnly={readonly} min="0" value={prescribed_distance_value || ""} onChange={this.change_prescribed_distance} /> <span className="unit distance-unit">{distance_unit}</span>
        </div>
      );

    }

    var subtitle;
    if (
      this.props.columns == 3 &&
      this.props.perspective == 'athlete' &&
      this.props.mode == 'training_session' &&
      (this.props.training_session.is_training_session() || this.props.training_session.subtitle) &&
      hasBetaAccess('athlete_temporary_subtitle_field')
    ) {
      subtitle = (
        <div className="field-wrapper inline training-session">
          {'subtitle' in this.props.form_errors && <Errors errors={this.props.form_errors.subtitle} />}
          <label htmlFor="id_subtitle">Subtitle:</label><input type="text" name="subtitle" id="id_subtitle" maxLength="100" onChange={this.handleSubtitleChange} value={this.props.training_session.subtitle || ""} />
        </div>
      );
    }

    // Show separate minutes and distance fields for swim, bike and run on brick sessions.
    var prescribed_swim_title;
    var prescribed_swim_minutes;
    var prescribed_swim_distance;
    var prescribed_bike_title;
    var prescribed_bike_minutes;
    var prescribed_bike_distance;
    var prescribed_run_title;
    var prescribed_run_minutes;
    var prescribed_run_distance;
    if (discipline_category == 'brick') {

      if (distance_unit == 'km') {
        var prescribed_bike_distance_data = decimalPlaces(session_to_use_for_plan.prescribed_bike_km, 3, 'null', false, true);
        var prescribed_run_distance_data = decimalPlaces(session_to_use_for_plan.prescribed_run_km, 3, 'null', false, true);
      }
      else if (distance_unit == 'miles') {
        var prescribed_bike_distance_data = decimalPlaces(session_to_use_for_plan.prescribed_bike_miles, 3, 'null', false, true);
        var prescribed_run_distance_data = decimalPlaces(session_to_use_for_plan.prescribed_run_miles, 3, 'null', false, true);
      }

      if (this.props.training_session.getPoolUnit() == 'yards') {
        var prescribed_swim_distance = decimalPlaces(session_to_use_for_plan.prescribed_swim_yards, 0, 'null', false, true);
      }
      else if (this.props.training_session.getPoolUnit() == 'meters') {
        var prescribed_swim_distance = decimalPlaces(session_to_use_for_plan.prescribed_swim_meters, 0, 'null', false, true);
      }

      var prescribed_swim_title = (<h4 className="swim">Swim</h4>);

      var prescribed_swim_minutes = (
        <div className="field-wrapper number">
          {'prescribed_swim_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_swim_minutes} />}
          <label htmlFor="id_prescribed_swim_minutes">Time to swim:</label>
          <input type="number" className="session-form__number-field" name="prescribed_swim_minutes" step="1" id="id_prescribed_swim_minutes" readOnly={readonly} min="0" value={session_to_use_for_plan.prescribed_swim_minutes || ""} onChange={this.change_prescribed_swim_minutes} /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      var prescribed_swim_distance = (
        <div className="field-wrapper number">
          {'prescribed_swim_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_swim_distance} />}
          <label htmlFor="id_prescribed_swim_distance">Distance to swim:</label>
          <input type="number" className="session-form__number-field" name="prescribed_swim_distance" step="0.001" id="id_prescribed_swim_distance" readOnly={readonly} min="0" value={prescribed_swim_distance || ""} onChange={this.change_prescribed_swim_distance} /> <span className="unit swim-unit">{this.props.training_session.getPoolUnit()}</span>
        </div>
      );

      var prescribed_bike_title = (<h4 className="bike">Bike</h4>);

      var prescribed_bike_minutes = (
        <div className="field-wrapper number">
          {'prescribed_bike_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_bike_minutes} />}
          <label htmlFor="id_prescribed_bike_minutes">Time to bike:</label>
          <input type="number" className="session-form__number-field" name="prescribed_bike_minutes" step="1" id="id_prescribed_bike_minutes" readOnly={readonly} min="0" value={session_to_use_for_plan.prescribed_bike_minutes || ""} onChange={this.change_prescribed_bike_minutes} /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      var prescribed_bike_distance = (
        <div className="field-wrapper number">
          {'prescribed_bike_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_bike_distance} />}
          <label htmlFor="id_prescribed_bike_distance">Distance to bike:</label>
          <input type="number" className="session-form__number-field" name="prescribed_bike_distance" step="0.001" id="id_prescribed_bike_distance" readOnly={readonly} min="0" value={prescribed_bike_distance_data || ""} onChange={this.change_prescribed_bike_distance} /> <span className="unit distance-unit">{this.props.training_session.getDistanceUnit() == 'kilometers' ? 'km' : 'miles'}</span>
        </div>
      );

      var prescribed_run_title = (<h4 className="run">Run</h4>);

      var prescribed_run_minutes = (
        <div className="field-wrapper number">
          {'prescribed_run_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_run_minutes} />}
          <label htmlFor="id_prescribed_run_minutes">Time to run:</label>
          <input type="number" className="session-form__number-field" name="prescribed_run_minutes" step="1" id="id_prescribed_run_minutes" readOnly={readonly} min="0" value={session_to_use_for_plan.prescribed_run_minutes || ""} onChange={this.change_prescribed_run_minutes} /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      var prescribed_run_distance = (
        <div className="field-wrapper number">
          {'prescribed_run_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.prescribed_run_distance} />}
          <label htmlFor="id_prescribed_run_distance">Distance to run:</label>
          <input type="number" className="session-form__number-field" name="prescribed_run_distance" step="0.001" id="id_prescribed_run_distance" readOnly={readonly} min="0" value={prescribed_run_distance_data || ""} onChange={this.change_prescribed_run_distance} /> <span className="unit distance-unit">{this.props.training_session.getDistanceUnit() == 'kilometers' ? 'km' : 'miles'}</span>
        </div>
      );

    }

    var brief_description;
    if (discipline_category == 'diary') {
      brief_description = (
        <div className="field-wrapper">
          {'diary_summary' in this.props.form_errors && <Errors errors={this.props.form_errors.diary_summary} />}
          <label htmlFor="id_diary_summary">Brief description:</label>
          <input type="text" name="diary_summary" id="id_diary_summary" readOnly={readonly} value={this.props.training_session.diary_summary || ""} onChange={this.change_diary_summary} maxLength="50" />
        </div>
      );
    }

    var key_session;
    if (this.props.training_session.key_session) {
      key_session = (<div className="key-session"><p>This is a key session; not to be missed.</p></div>);
    }

    var override_session_plan_link;
    if (this.props.training_session.creator.type == CREATOR_GROUP && this.props.athlete_data && hasGroupSessionOverridePermission(this.props.training_session)) {
      if (this.props.training_session.using_group_parent_session()) {
        var button_text = (this.props.perspective == 'athlete') ? 'write own plan' : 'override plan';
        override_session_plan_link = <span id="override-session" onClick={this.setToWriteOwnPlan}>{button_text}</span>;
      }
      else {
        override_session_plan_link = <span id="override-session" onClick={this.setToUseGroupPlan}>use group plan</span>;
      }
    }

    var session_plan_label_text = (this.props.perspective == 'coach') ? 'What\'s the plan?' : 'What are you planning to do?';
    if (this.props.training_session.creator.type == CREATOR_COACH) {
      // Note this is done so that another member of the company can add
      // without it looking odd to the athlete. If we alter it back to
      // showing the name of the coach this would need to be taken into
      // consideration.
      if ('creator_company_name' in this.props.training_session.creator) {
        session_plan_label_text = 'Session plan by ' + this.props.training_session.creator.creator_company_name;
      }
      else {
        session_plan_label_text = 'Session plan by ' + this.props.training_session.creator.creator_name;
      }
    }
    else if (this.props.training_session.using_group_parent_session()) {
      session_plan_label_text = 'Session plan by ' + this.props.training_session.creator.group_name;
    }

    var training_plan_notes;
    var attachments;
    if (this.props.training_session.discipline_id) {
      if (readonly) {
        // Read only version is displayed in a div so that links can be turned
        // into URLs.
        training_plan_notes = (
          <div className="field-wrapper training-plan-notes" style={{marginTop: '7px'}}>
            {override_session_plan_link}
            <label htmlFor="id_training_plan_notes">{session_plan_label_text}</label>
            <div id="id_training_plan_notes" className="session-form__training-plan-notes">{get_text_as_jsx(session_to_use_for_plan.prescribed_training_notes)}</div>
          </div>
        );
      }
      else {

        // The better solution for this is probably to get rid of this state
        // within the DraftJS widget and hold the note object in Redux.
        // However, that was awkward while we were using this widget for the
        // training plan along with a beta that is just a plain text. So
        // now we have removed the beta consider rewriting this.

        training_plan_notes = (
          <div className="training-plan-section field-wrapper training-plan-notes">
            {'training_plan_notes' in this.props.form_errors && <Errors errors={this.props.form_errors.training_plan_notes} />}
            {override_session_plan_link}
            <label htmlFor="id_training_plan_notes">{session_plan_label_text}</label>

            <ResizableTextareaDraftJS
              id="id_training_plan_notes"
              className="training-plan-section-block"
              session_id={session_to_use_for_plan.id}
              initial_value={session_to_use_for_plan.prescribed_training_notes}
              onChange={this.changePrescribedTrainingNotesUsingValue}
              read_only={readonly}
              placeholder='Describe the session plan...'
              style={{minHeight: '100px'}}
            />

          </div>
        );

      }

      attachments = <AttachmentsField
        training_session={this.props.training_session}
        content_attachments_meta_data={this.props.content_attachments_meta_data}
        openMediaAttachmentSelector={this.props.openMediaAttachmentSelector}
        content_attachments={this.props.content_attachments}
        perspective={this.props.perspective}
        current_user_id={this.props.current_user_id}
        session_to_use_for_plan={session_to_use_for_plan}
        updateTrainingSession={this.props.updateTrainingSession}
        formChanged={this.props.formChanged}
      />

    }

    let structured_training;
    let structured_training_button;
    if (
      (
        discipline_category == 'bike' ||
        discipline_category == 'brick' ||
        discipline_category == 'run' ||
        (hasBetaAccess('structured_training_swimming') && discipline_category == 'swim')
      ) &&
      !(this.props.perspective == 'athlete' && this.props.open_activity_plan_uuid)
    ) {

      let discipline = discipline_category;
      if (discipline_category == 'brick') {
        // NOTE Obviously eventually we need to allow creating a bike or a run
        // or both. But for now I just want to get the run working on a
        // separate run session for ease of development.
        discipline = 'bike';
      }

      const has_existing_structured_workout = this.props.activity_plans && this.props.training_session.activity_plan_uuids;

      // TODO When removing the beta we won't need has_edit_permission either. This
      // should allow athletes to always add structured training where it doesn't
      // already exist on coach created sessions (or their own sessions.
      const has_create_structured_workouts_permission = (hasBetaAccess('coached_athlete_can_always_create_structured_workouts') || has_edit_permission) && xhale_meta_data.has_premium_features;

      if (has_existing_structured_workout) {
        structured_training = (
          <div className="training-plan-section">
            {'activity_plan' in this.props.form_errors && <Errors errors={this.props.form_errors.activity_plan} />}
            <StructuredTrainingSection
              activity_plans={this.props.activity_plans}
              athlete_data={this.props.athlete_data}
              discipline={discipline}
              training_session={this.props.training_session}
              training_zones={this.props.athlete_data ? this.props.athlete_data.training_zones : null}
              has_edit_access={has_edit_permission}
              has_premium_features={xhale_meta_data.has_premium_features}
              perspective={this.props.perspective}
              formChanged={this.props.formChanged}
              openStructuredWorkout={this.props.openStructuredWorkout}
              updateTrainingSession={this.props.updateTrainingSession}
            />
          </div>
        );
      }

     if (
       typeof(this.props.openStructuredWorkout) == 'function' &&
       has_create_structured_workouts_permission &&
       (!has_existing_structured_workout || hasBetaAccess('structured_training_add_multiple_workouts'))
     ) {
        structured_training_button = (
          <StructuredTrainingButton
            training_session={this.props.training_session}
            discipline={discipline}
            another_structured_workout_exists={has_existing_structured_workout}
            openStructuredWorkout={this.props.openStructuredWorkout}
          />
        );
      }

    }

    var template_sharing;
    if (this.props.perspective == 'coach' && this.props.training_session.template) {
      if (this.props.training_session.template_folder_id) {

        if (this.props.training_session.template_folder) {
          var folder_description = 'This template belongs to the ' + this.props.training_session.template_folder.title + ' folder';
          if (this.props.training_session.template_folder.template_sharing == 1) {
            // TODO This section will error when looking at a template from a folder in a training plan.
            // The solution is to not allow access to this folder when viewing the plan because it should
            // not be shared with this plan but only an individual group or athlete. I just need to reload
            // the templates.
            var athlete_or_group_name = this.props.athlete_data ? this.props.athlete_data.name : this.props.group_data.name;
            folder_description += ' and is for ' + athlete_or_group_name + ' only.';
          }
          else if (this.props.training_session.template_folder.template_sharing == 2) {
            folder_description += ' and can be used for any of your athletes.' ;
          }
          else if (this.props.training_session.template_folder.template_sharing == 3) {
            folder_description += ' and can be used by any coach in your coaching company.' ;
          }
        }
        else {
          var folder_description = 'This template belongs to a folder.';
        }

        template_sharing = (<div className="field-wrapper session-form__template-owner-message">{folder_description}</div>);

      }
      else {

        if (!readonly) {

          var sharing_option_public;
          var public_url;
          if (this.props.group_data && this.props.group_data.id == 58) { // 58 is the Marketing Sessions group.
            sharing_option_public = <option value="4">Public</option>;

            if (this.props.training_session.template_sharing == 4 && this.props.training_session.has_permanent_id()) {
              var public_url = (
                <div className="public-template-url-section" style={{marginTop: '5px', fontSize: '13px'}}>
                  <span>Share link:</span><br /><span className="public-template-url">{'https://trainxhale.com/template/' + this.props.training_session.id + '/'}</span>
                </div>
              );
            }

          }

          // Training plans miss this option entirely.
          let current_athlete_or_group_option;
          if (this.props.athlete_data) {
            current_athlete_or_group_option = <option value="1">{this.props.athlete_data.name}</option>;
          }
          else if (this.props.group_data) {
            current_athlete_or_group_option = <option value="1">{this.props.group_data.name}</option>;
          }

          template_sharing = (
            <div className="field-wrapper">
              <label htmlFor="id_template_sharing">This template is for:</label> <select name="template_sharing" id="id_template_sharing" value={this.props.training_session.template_sharing} onChange={this.change_template_sharing}>
                {current_athlete_or_group_option}
                <option value="2">All your athletes</option>
                <option value="3">All {this.props.coaching_company_name} athletes</option>
                {sharing_option_public}
              </select>
              {public_url}
            </div>
          );
        }
      }
    }

    var training_plan_title;
    if (this.props.training_session.discipline_id) {
      training_plan_title = <h3>Training plan</h3>;
    }

    return (
      <div className="session-plan">
        {subtitle}
        {print_training_plan}
        {training_plan_title}
        {prescribed_minutes}
        {prescribed_distance}
        {prescribed_swim_title}
        {prescribed_swim_minutes}
        {prescribed_swim_distance}
        {prescribed_bike_title}
        {prescribed_bike_minutes}
        {prescribed_bike_distance}
        {prescribed_run_title}
        {prescribed_run_minutes}
        {prescribed_run_distance}
        {brief_description}
        <div className="training-plan-sections">
          {training_plan_notes}
          {attachments}
          {structured_training}
          {structured_training_button}
        </div>
        {key_session}
        {template_sharing}
      </div>
    )
  }
}


class Comment extends React.Component {

  handleClick = (event) => {
    if (!(event.target instanceof HTMLAnchorElement)) {
      this.props.setEditingComment(this.props.comment.message_message_id);
    }
  }

  changeComment = (event) => {
    this.props.training_session.comments[this.props.comment.message_message_id].updated = true;
    this.props.training_session.comments[this.props.comment.message_message_id].content = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  addDatetimeToLastParagraph = (jsx_elements, datetime) => {

    const datetime_span = <span key="datetime" className="comment-stream__comment-datetime">{datetime}</span>;

    // If the list is empty, return a standalone datetime element
    if (!jsx_elements || jsx_elements.length === 0) {
      return [datetime_span];
    }

    const last_element_index = jsx_elements.length - 1;
    const last_element = jsx_elements[last_element_index];

    // Append datetime if the last element isn't a paragraph, otherwise
    // add the datetime to the last paragraph.
    jsx_elements[last_element_index] =
    last_element?.type === 'p'
      ? React.cloneElement(last_element, {}, <>
          {last_element.props.children}
          {datetime_span}
        </>)
      : jsx_elements.concat(datetime_span);

    return jsx_elements;
  }

  render () {

    let author;
    if (this.props.show_author) {
      author = <div className="comment-stream__comment-author">{this.props.comment.sender_name}</div>;
    }

    let message;
    if (this.props.edit_mode) {
      message = <textarea
        autoFocus
        className="comment-stream__comment-input"
        value={this.props.comment.content}
        onChange={this.changeComment}
        onClick={this.changeComment}
      ></textarea>
    }
    else {

      let message_content = get_text_as_jsx(this.props.comment.content);

      // Inject the formatted local time into the message content. This is so the
      // datestamp can show on the last line as part of the flow of the text.
      // Otherwise this is very hard to style.
      let message_datetime = new Date(this.props.comment.publish_datetime)
        .toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
      message_content = this.addDatetimeToLastParagraph(message_content, message_datetime);

      let clickHandler = (this.props.comment.sender_id == this.props.current_user_id) ? this.handleClick : null;
      let intensity = (this.props.training_session.intensity) ? this.props.training_session.intensity : 0;
      let classname = 'comment-stream__comment-content';
      classname += ' comment-stream__comment-content--intensity-' + intensity;
      classname += (this.props.comment.sender_id == this.props.current_user_id) ? ' comment-stream__comment-content--editable' : ' comment-stream__comment-content--not-editable';
      message = (
        <div className={classname} onClick={clickHandler}>
          <div className="comment-stream__comment-text">{message_content}</div>
        </div>
      );
    }

    let attachments;
      if (typeof(this.props.comment.attachment_ids) != 'undefined' && this.props.comment.attachment_ids.length > 0) {
        let attachment_items = [];
        for (let i = 0; i < this.props.comment.attachment_ids.length; i++) {
          const attachment = this.props.content_attachments[this.props.comment.attachment_ids[i]];

          if (!attachment) {
            attachment_items.push(
              <div
                className="content-attachment"
                style={{padding: '5px', cursor: 'pointer'}}
                key={this.props.comment.attachment_ids[i]}
              >
                <span>
                  <span className="content-attachment--title">This attachment did not load.</span>
                </span>
              </div>
            );
          }
          else {
            attachment_items.push(
              <div
                className="content-attachment"
                style={{padding: '5px', cursor: 'pointer'}}
                key={this.props.comment.attachment_ids[i]}
              >
                <a href={attachment.url} target="_blank">
                  <img style={{verticalAlign: 'middle', marginRight: '5px' }} className="content-attachment--thumbnail" width="50" height="50" src={this.props.content_attachments[this.props.comment.attachment_ids[i]]?.thumbnail} />
                  <span className="content-attachment--title">{this.props.content_attachments[this.props.comment.attachment_ids[i]]?.title}</span>
                </a>
              </div>
            );
          }
        }
        attachments = (
          <div className="content-attachments training-plan-section">
            <div className="content-attachments training-plan-section-block">
              {attachment_items}
            </div>
          </div>
        );
      }

    return (
      <div className={'comment-stream__comment'}>
        {author}
        {message}
        {attachments}
      </div>
    );

  }
}

export class Comments extends React.Component {

  state = {
    show_comment_input: false,
    current_training_session_id: null,
  }

  toggleDisplayCommentInput = () => {
    this.setState({show_comment_input: !this.state.show_comment_input});
  }

  setAthleteCommentInputVisibility = () => {
    if (this.props.perspective == 'athlete' && this.state.current_training_session_id !== this.props.training_session.id) {
      this.setState({
        show_comment_input: false,
        current_training_session_id: this.props.training_session.id,
      });
    }
  }

  componentDidMount = () => {
    this.setAthleteCommentInputVisibility();
  }

  UNSAFE_componentWillUpdate = () => {
    this.setAthleteCommentInputVisibility();
  }

  change_athlete_feedback = (event) => {
    this.props.training_session.athlete_feedback = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_coach_comments = (event) => {
    this.props.training_session.coaches_comments = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  changeNewComment = (event) => {
    if (typeof(this.props.training_session.comments.new_comment) == 'undefined') {
      this.props.training_session.comments.new_comment = {
        uuid: uuidv1(),
        content: '',
        attachments: [],
      }
    }
    this.props.training_session.comments.new_comment.content = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  getOrderedComments = () => {

    // Put comments into a list that excludes the unsaved 'new' comment.
    let comments = [];
    Object.keys(this.props.training_session.comments).forEach((comment_id) => {
      if (comment_id != 'new' && comment_id != 'new_comment') {
        comments.push(this.props.training_session.comments[comment_id]);
      }
    });

    // Order the training sessions by date.
    comments.sort(function (a, b) {
      return a.publish_datetime - b.publish_datetime;
    });

    return comments;
  }

  /**
   * Chooses appropriate link text for athletes to create another message.
   *
   * If the previous message was from the coach, it suggests replying.
   * If the previous message was from themselves, it suggests sending another.
   * If there are no messages yet, it suggests writing one.
   */
  getAddCommentLinkText = (ordered_comments) => {
    for (let i = ordered_comments.length - 1; i >= 0; i--) {
      if (ordered_comments[i].message_message_id != 'new') {
        if (ordered_comments[i].sender_id == this.props.current_user_id) {
          return 'write another message';
        }
        else {
          return 'reply to your coach';
        }
      }
    }

    return 'write a message to your coach';
  }

  render() {

    if (this.props.perspective == 'coach') {

      if (this.props.training_session.athlete_feedback) {
        var athlete_feedback_text = get_text_as_jsx(this.props.training_session.athlete_feedback);
      }
      else {
        var athlete_feedback_text = <div>No feedback given.</div>
      }
      var athlete_feedback = (
        <div className="field-wrapper athlete-feedback">
          <h4>Athlete session feedback</h4>
          <div className="comments">{athlete_feedback_text}</div>
        </div>
      );

      var coach_comments;
      let comments = [];
      let ordered_comments = this.getOrderedComments();
      for (let i = 0; i < ordered_comments.length; i++) {
        let show_author = (i == 0 || ordered_comments[i-1].sender_id != ordered_comments[i].sender_id) ? true : false;
        let in_edit_mode = (this.props.editing_comment_id == ordered_comments[i].message_message_id) ? true : false;
        comments.push(
          <Comment
            key={ordered_comments[i].message_message_id}
            comment={ordered_comments[i]}
            show_author={show_author}
            content_attachments={this.props.content_attachments}
            training_session={this.props.training_session}
            updateTrainingSession={this.props.updateTrainingSession}
            current_user_id={this.props.current_user_id}
            edit_mode={in_edit_mode}
            setEditingComment={this.props.setEditingComment}
          />
        );
      }

      let add_attachment_button;
      // NOTE For now this is disabled so that we can concentrate on releasing attachments just on
      // training plan fields.
      //if (hasBetaAccess('content_attachments') && typeof(this.props.openMediaAttachmentSelector) == 'function') {
      //  add_attachment_button = <div
      //    className="small-button training_session_form__add_content_attachment_button"
      //    onClick={this.props.openMediaAttachmentSelector.bind(null, 'comments')}>
      //      add attachment
      //  </div>
      //}

      let attachments;
      if (this.props.training_session.comments.new_comment?.attachments) {

        let attachment_items = [];

        for (let i = 0; i < this.props.training_session.comments.new_comment.attachments.length; i++) {
          const attachment = this.props.content_attachments[this.props.training_session.comments.new_comment.attachments[i]];

          attachment_items.push(
            <div
              className="content-attachment"
              style={{padding: '5px', cursor: 'pointer'}}
              key={attachment.id}
            >
              <a href={attachment.url} target="_blank">
                <img style={{verticalAlign: 'middle', marginRight: '5px' }} className="content-attachment--thumbnail" width="50" height="50" src={attachment.thumbnail} />
                <span className="content-attachment--title">{attachment.title}</span>
              </a>
            </div>
          );
        }
        attachments = (
          <div className="content-attachments training-plan-section">
            <div className="content-attachments training-plan-section-block">
              {attachment_items}
            </div>
          </div>
        );
      }

      coach_comments = (
        <div className="comment-stream" style={{overflow: 'auto'}}>
          <h4>Comments</h4>
          {comments}
          {add_attachment_button}
          {attachments}
          <textarea
            className="comment-stream__comment-input"
            placeholder="Comment on this session"
            value={this.props.training_session.comments.new_comment?.content || ''}
            onChange={this.changeNewComment}
            onClick={this.changeNewComment}
          ></textarea>
        </div>
      );

    }
    else {
      var athlete_feedback = (
        <div className="field-wrapper">
          {'athlete_feedback' in this.props.form_errors && <Errors errors={this.props.form_errors.athlete_feedback} />}
          <label htmlFor="id_athlete_feedback">How did it go?</label>
          <textarea
            name="athlete_feedback"
            rows="10"
            cols="40"
            id="id_athlete_feedback"
            className="session-form__athlete-feedback"
            value={this.props.training_session.athlete_feedback}
            onChange={this.change_athlete_feedback}
            disabled={this.props.current_user_id == this.props.training_session.athlete_id ? false : true}
          ></textarea>
        </div>
      );

      var coach_comments;

      let ordered_comments = this.getOrderedComments();

      let comments = [];
      for (let i = 0; i < ordered_comments.length; i++) {
        let show_author = (i == 0 || ordered_comments[i-1].sender_id != ordered_comments[i].sender_id) ? true : false;
        let in_edit_mode = (this.props.editing_comment_id == ordered_comments[i].message_message_id) ? true : false;
        comments.push(
          <Comment
            key={ordered_comments[i].message_message_id}
            comment={ordered_comments[i]}
            show_author={show_author}
            training_session={this.props.training_session}
            content_attachments={this.props.content_attachments}
            updateTrainingSession={this.props.updateTrainingSession}
            current_user_id={this.props.current_user_id}
            edit_mode={in_edit_mode}
            setEditingComment={this.props.setEditingComment}
          />
        );
      }

      let comment_input;
      if (this.props.athlete_data.coaching_service == 'mentor' || this.props.athlete_data.coaching_service == 'full-coaching') {

        let comment_textarea;
        if (this.state.show_comment_input) {
          comment_textarea = (
            <textarea
              autoFocus
              className="comment-stream__comment-input"
              placeholder="Comment on this session"
              value={this.props.training_session.comments.new_comment?.content}
              onChange={this.changeNewComment}
              onClick={this.changeNewComment}
            ></textarea>
          );
        }

        comment_input = (
          <div className="comment-stream__make-comment-button">
            <span onClick={this.toggleDisplayCommentInput}>{this.getAddCommentLinkText(ordered_comments)}</span>
            {comment_textarea}
          </div>
        );

      }

      if (comments.length > 0 || this.props.athlete_data.coaching_service == 'mentor' || this.props.athlete_data.coaching_service == 'full-coaching') {
        coach_comments = (
          <div className="comment-stream" style={{overflow: 'auto'}}>
            {comments}
            {comment_input}
          </div>
        );
      }

      if (this.props.training_session.coaches_comments) {
        coach_comments = (
          <div className="coaches-comments">
            <h3>Coach's Comments</h3>
            <div className="comments">
              {get_text_as_jsx(this.props.training_session.coaches_comments)}
            </div>
          </div>
        );
      }

    }

    return (
      <div className="comments" style={{marginTop: '10px'}}>
        {athlete_feedback}
        {coach_comments}
      </div>
    )

  }
}

class CompletedTrainingFields extends React.Component {

  change_completed_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.completed_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_completed_distance = (event) => {
    var discipline_category = this.props.training_session.getDisciplineCategory();
    if (discipline_category == 'swim') {
      var distance_unit = this.props.training_session.getPoolUnit();
    }
    else {
      var distance_unit = (this.props.training_session.getDistanceUnit() == 'kilometers') ? 'km' : 'miles';
    }
    if (distance_unit == 'meters') {
      this.props.training_session.completed_meters = event.target.value;
    }
    else if (distance_unit == 'yards') {
      this.props.training_session.completed_yards = event.target.value;
    }
    else if (distance_unit == 'km') {
      this.props.training_session.completed_km = event.target.value;
    }
    else if (distance_unit == 'miles') {
      this.props.training_session.completed_miles = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_swim_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.completed_swim_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_completed_bike_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.completed_bike_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_completed_run_minutes = (event) => {
    if (!isNaN(parseInt(event.target.value)) || event.target.value == '') {
      this.props.training_session.completed_run_minutes = event.target.value;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  change_completed_swim_distance = (event) => {
    if (this.props.training_session.getPoolUnit() == 'yards') {
      this.props.training_session.completed_swim_yards = event.target.value;
    }
    else if (this.props.training_session.getPoolUnit() == 'meters') {
      this.props.training_session.completed_swim_meters = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_bike_distance = (event) => {
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      this.props.training_session.completed_bike_km = event.target.value;
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles') {
      this.props.training_session.completed_bike_miles = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_run_distance = (event) => {
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      this.props.training_session.completed_run_km = event.target.value;
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles') {
      this.props.training_session.completed_run_miles = event.target.value;
    }
    this.props.updateTrainingSession(this.props.training_session);
  }

  componentDidMount = () => {
    this.importAttachedData();
  }

  UNSAFE_componentWillUpdate = () => {
    this.importAttachedData();
  }

  /**
   * If there is data ready to import, update the session with that data.
   */
  importAttachedData = () => {

    // TODO Pull this logic out of here and update the values in a single place.
    // (if I don't completely change how I implement this.) Then instead of
    // UNSAFE_componentWillUpdate call the method directly when appropriate.

    // Record which session, data has already been added to, in case we open
    // up an alternate session.
    if (this.current_session_id != this.props.training_session.id) {
      this.current_session_id = this.props.training_session.id;
      this.added_training_log_ids = [];
      this.cumulative_totals_added = {
        swim_seconds: 0,
        swim_distance: 0,
        bike_seconds: 0,
        bike_distance: 0,
        run_seconds: 0,
        run_distance: 0,
        total_seconds: 0,
        total_distance: 0,
      };
    }

    var discipline_category = this.props.training_session.getDisciplineCategory();

    if (discipline_category == 'swim') {
      var distance_unit = this.props.training_session.getPoolUnit();
      var pool_unit = this.props.training_session.getPoolUnit();
    }
    else {
      var distance_unit = (this.props.training_session.getDistanceUnit() == 'kilometers') ? 'km' : 'miles';
      var pool_unit = this.props.training_session.getPoolUnit();
    }

    if (this.props.training_session.is_training_session() && this.props.related_training_logs && this.props.related_training_logs.length > 0) {

      for (var i = 0; i < this.props.related_training_logs.length; i++) {

        var total_seconds = 0;
        var swim_seconds = 0;
        var bike_seconds = 0;
        var run_seconds = 0;
        var total_distance = 0;
        var swim_distance = 0;
        var bike_distance = 0;
        var run_distance = 0;

        var related_training_log = this.props.related_training_logs[i];
        var importing = (this.props.file_ids_marked_to_import.indexOf(related_training_log.file_data.id) >= 0 && !this.props.training_session.missed_session) ? true : false;
        var already_added_in_this_load = (this.added_training_log_ids.indexOf(related_training_log.file_data.id) >= 0) ? true : false;
        var already_added_and_saved = (this.props.training_session.attached_training_logs.indexOf(related_training_log.file_data.id) >= 0) ? true : false;
        var already_added = already_added_in_this_load || already_added_and_saved;
        var adding = importing && !already_added ? true : false;
        var removing = !importing && already_added_in_this_load ? true : false;

        var store_state = store.getState(); // TODO No longer a pure function. Pass this into the component through props instead.
        var related_training_log = store_state.main.uploaded_files[related_training_log.file_data.id];

        if ((adding || removing) && related_training_log.activities) {

          // Calculate values for the data to be added or removed.
          $.each(related_training_log.activities, (key, activity_data) => {

            // If activity type is not set, determine if this is a cycling or
            // running activity.
            if (activity_data.discipline != 'unknown' || activity_data.discipline == 'other' || activity_data.discipline == 'training') {
              if (discipline_category == 'swim') {
                activity_data.discipline = 'swimming';
              }
              if (discipline_category == 'bike') {
                activity_data.discipline = 'cycling';
              }
              else if (discipline_category == 'run') {
                activity_data.discipline = 'running';
              }
            }

            if (discipline_category == 'brick') {
              if (activity_data.discipline == 'swimming') {
                swim_seconds += activity_data.total_time_seconds;
                if (pool_unit == 'yards') {
                  swim_distance += xhale.helper.meters_to_yards(activity_data.total_distance_km * 1000);
                }
                else {
                  swim_distance += activity_data.total_distance_km * 1000;
                }
              }
              else if (activity_data.discipline == 'cycling') {
                bike_seconds += activity_data.total_time_seconds;

                if (distance_unit == 'km') {
                  bike_distance += activity_data.total_distance_km;
                }
                else if (distance_unit == 'miles') {
                  bike_distance += activity_data.total_distance_miles;
                }
              }
              else if (activity_data.discipline == 'running') {
                run_seconds += activity_data.total_time_seconds;
                if (distance_unit == 'km') {
                  run_distance += activity_data.total_distance_km;
                }
                else if (distance_unit == 'miles') {
                  run_distance += activity_data.total_distance_miles;
                }
              }
            }
            else {
              total_seconds += activity_data.total_time_seconds;

              if (activity_data.discipline == 'swimming' && activity_data.total_distance_km) {
                if (pool_unit == 'yards') {
                  total_distance += xhale.helper.meters_to_yards(activity_data.total_distance_km * 1000);
                }
                else {
                  total_distance += activity_data.total_distance_km * 1000;
                }
              }
              else if (distance_unit == 'km' && activity_data.total_distance_km) {
                total_distance += activity_data.total_distance_km;
              }
              else if (distance_unit == 'miles' && activity_data.total_distance_miles) {
                total_distance += activity_data.total_distance_miles;
              }

            }

          });

          // Update session data.
          if (adding) {

            // Add to the list of added training log ids.
            this.added_training_log_ids.push(related_training_log.file_data.id);

            if (discipline_category == 'brick') {

              if (!this.props.training_session.completed_swim_minutes || (this.props.training_session.completed_swim_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_swim_seconds + this.cumulative_totals_added.swim_seconds))) {
                this.cumulative_totals_added.swim_seconds += swim_seconds;
                this.props.training_session.completed_swim_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_swim_seconds + this.cumulative_totals_added.swim_seconds);
              }
              if (!this.props.training_session.completed_bike_minutes || (this.props.training_session.completed_bike_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_bike_seconds + this.cumulative_totals_added.bike_seconds))) {
                this.cumulative_totals_added.bike_seconds += bike_seconds;
                this.props.training_session.completed_bike_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_bike_seconds + this.cumulative_totals_added.bike_seconds);
              }
              if (!this.props.training_session.completed_run_minutes || (this.props.training_session.completed_run_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_run_seconds + this.cumulative_totals_added.run_seconds))) {
                this.cumulative_totals_added.run_seconds += run_seconds;
                this.props.training_session.completed_run_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_run_seconds + this.cumulative_totals_added.run_seconds);
              }
              if (pool_unit == 'yards' && (!this.props.training_session.completed_swim_yards || (decimalPlaces(this.props.training_session.completed_swim_yards, 2), decimalPlaces(xhale.helper.meters_to_yards(this.props.training_session.training_log_completed_swim_km * 1000) + this.cumulative_totals_added.swim_distance, 2)))) {
                this.cumulative_totals_added.swim_distance += swim_distance;
                this.props.training_session.completed_swim_yards = xhale.helper.meters_to_yards(this.props.training_session.training_log_completed_swim_km * 1000) + this.cumulative_totals_added.swim_distance;
              }
              else if (pool_unit == 'meters' && (!this.props.training_session.completed_swim_meters || (decimalPlaces(this.props.training_session.completed_swim_meters, 2) == decimalPlaces((this.props.training_session.training_log_completed_swim_km * 1000) + this.cumulative_totals_added.swim_distance, 2)))) {
                this.cumulative_totals_added.swim_distance += swim_distance;
                this.props.training_session.completed_swim_meters = (this.props.training_session.training_log_completed_swim_km * 1000) + this.cumulative_totals_added.swim_distance;
              }

              if (distance_unit == 'km') {
                if (!this.props.training_session.completed_bike_km || decimalPlaces(this.props.training_session.completed_bike_km, 2) == decimalPlaces(this.props.training_session.training_log_completed_bike_km + this.cumulative_totals_added.bike_distance, 2)) {
                  this.cumulative_totals_added.bike_distance += bike_distance;
                  this.props.training_session.completed_bike_km = this.props.training_session.training_log_completed_bike_km + this.cumulative_totals_added.bike_distance;
                }
                if (!this.props.training_session.completed_run_km || decimalPlaces(this.props.training_session.completed_run_km, 2) == decimalPlaces(this.props.training_session.training_log_completed_run_km + this.cumulative_totals_added.run_distance, 2)) {
                  this.cumulative_totals_added.run_distance += run_distance;
                  this.props.training_session.completed_run_km = this.props.training_session.training_log_completed_run_km + this.cumulative_totals_added.run_distance;
                }
              }
              else if (distance_unit == 'miles') {
                if (!this.props.training_session.completed_bike_miles || decimalPlaces(this.props.training_session.completed_bike_miles, 2) == decimalPlaces(xhale.helper.km_to_miles(this.props.training_session.training_log_completed_bike_km) + this.cumulative_totals_added.bike_distance, 2)) {
                  this.cumulative_totals_added.bike_distance += bike_distance;
                  this.props.training_session.completed_bike_miles = xhale.helper.km_to_miles(this.props.training_session.training_log_completed_bike_km) + this.cumulative_totals_added.bike_distance;
                }
                if (!this.props.training_session.completed_run_miles || decimalPlaces(this.props.training_session.completed_run_miles, 2) == decimalPlaces(xhale.helper.km_to_miles(this.props.training_session.training_log_completed_run_km) + this.cumulative_totals_added.run_distance, 2)) {
                  this.cumulative_totals_added.run_distance += run_distance;
                  this.props.training_session.completed_run_miles = xhale.helper.km_to_miles(this.props.training_session.training_log_completed_run_km) + this.cumulative_totals_added.run_distance;
                }
              }

            }
            else {

              if (!this.props.training_session.completed_minutes || (this.props.training_session.completed_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_seconds + this.cumulative_totals_added.total_seconds))) {
                this.cumulative_totals_added.total_seconds += total_seconds;
                this.props.training_session.completed_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_seconds + this.cumulative_totals_added.total_seconds);
              }

              if (discipline_category == 'swim') {

                if (pool_unit == 'meters' && (!this.props.training_session.completed_meters || (decimalPlaces(this.props.training_session.completed_meters, 2) == decimalPlaces((this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance, 2)))) {
                  this.cumulative_totals_added.total_distance += total_distance;
                  this.props.training_session.completed_meters = (this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance;
                }
                else if (pool_unit == 'yards' && (!this.props.training_session.completed_yards || (decimalPlaces(this.props.training_session.completed_yards, 2) == decimalPlaces(xhale.helper.meters_to_yards((this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance, 2))))) {
                  this.cumulative_totals_added.total_distance += total_distance;
                  this.props.training_session.completed_yards = xhale.helper.meters_to_yards(this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance;
                }
              }
              else if (distance_unit == 'km' && (!this.props.training_session.completed_km || (decimalPlaces(this.props.training_session.completed_km, 2) == decimalPlaces(this.props.training_session.training_log_completed_km + this.cumulative_totals_added.total_distance, 2)))) {
                this.cumulative_totals_added.total_distance += total_distance;
                this.props.training_session.completed_km = this.props.training_session.training_log_completed_km + this.cumulative_totals_added.total_distance;
              }
              else if (distance_unit == 'miles' && (!this.props.training_session.completed_miles || (decimalPlaces(this.props.training_session.completed_miles, 2) == decimalPlaces(xhale.helper.km_to_miles(this.props.training_session.training_log_completed_km) + this.cumulative_totals_added.total_distance, 2)))) {
                this.cumulative_totals_added.total_distance += total_distance;
                this.props.training_session.completed_miles = xhale.helper.km_to_miles(this.props.training_session.training_log_completed_km) + this.cumulative_totals_added.total_distance;
              }

            }
          }
          else if (removing) {

            // Remove from the list of added training log ids.
            this.added_training_log_ids.splice(this.added_training_log_ids.indexOf(related_training_log.file_data.id), 1);

            if (discipline_category == 'brick') {
              if (this.props.training_session.completed_swim_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_swim_seconds + this.cumulative_totals_added.swim_seconds)) {
                this.cumulative_totals_added.swim_seconds -= swim_seconds;
                this.props.training_session.completed_swim_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_swim_seconds + this.cumulative_totals_added.swim_seconds);
              }
              if (this.props.training_session.completed_bike_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_bike_seconds + this.cumulative_totals_added.bike_seconds)) {
                this.cumulative_totals_added.bike_seconds -= bike_seconds;
                this.props.training_session.completed_bike_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_bike_seconds + this.cumulative_totals_added.bike_seconds);
              }
              if (this.props.training_session.completed_run_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_run_seconds + this.cumulative_totals_added.run_seconds)) {
                this.cumulative_totals_added.run_seconds -= run_seconds;
                this.props.training_session.completed_run_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_run_seconds + this.cumulative_totals_added.run_seconds);
              }

              if (pool_unit == 'yards' && this.props.training_session.completed_swim_yards == xhale.helper.meters_to_yards(this.props.training_session.training_log_completed_swim_km * 1000) + this.cumulative_totals_added.swim_distance) {
                this.cumulative_totals_added.swim_distance -= swim_distance;
                this.props.training_session.completed_swim_yards = xhale.helper.meters_to_yards(this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.swim_distance;
              }
              else if (pool_unit == 'meters' && this.props.training_session.completed_swim_meters == (this.props.training_session.training_log_completed_swim_km * 1000) + this.cumulative_totals_added.swim_distance) {
                this.cumulative_totals_added.swim_distance -= swim_distance;
                this.props.training_session.completed_swim_meters = (this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.swim_distance;
              }

              if (distance_unit == 'km') {
                if (this.props.training_session.completed_bike_km == this.props.training_session.training_log_completed_bike_km + this.cumulative_totals_added.bike_distance) {
                  this.cumulative_totals_added.bike_distance -= bike_distance;
                  this.props.training_session.completed_bike_km = this.props.training_session.training_log_completed_bike_km + this.cumulative_totals_added.bike_distance;
                }
                if (this.props.training_session.completed_run_km == this.props.training_session.training_log_completed_run_km + this.cumulative_totals_added.run_distance) {
                  this.cumulative_totals_added.run_distance -= run_distance;
                  this.props.training_session.completed_run_km = this.props.training_session.training_log_completed_run_km + this.cumulative_totals_added.run_distance;
                }
              }
              else if (distance_unit == 'miles') {
                if (this.props.training_session.completed_bike_miles == xhale.helper.km_to_miles(this.props.training_session.training_log_completed_bike_km) + this.cumulative_totals_added.bike_distance) {
                  this.cumulative_totals_added.bike_distance -= bike_distance;
                  this.props.training_session.completed_bike_miles = xhale.helper.km_to_miles(this.props.training_session.training_log_completed_bike_km) + this.cumulative_totals_added.bike_distance;
                }
                if (this.props.training_session.completed_run_miles == xhale.helper.km_to_miles(this.props.training_session.training_log_completed_run_km) + this.cumulative_totals_added.run_distance) {
                  this.cumulative_totals_added.run_distance -= run_distance;
                  this.props.training_session.completed_run_miles = xhale.helper.km_to_miles(this.props.training_session.training_log_completed_run_km) + this.cumulative_totals_added.run_distance;
                }
              }

            }
            else {
              if (this.props.training_session.completed_minutes == xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_seconds + this.cumulative_totals_added.total_seconds)) {
                this.cumulative_totals_added.total_seconds -= total_seconds;
                this.props.training_session.completed_minutes = xhale.helper.seconds_to_rounded_minutes(this.props.training_session.training_log_completed_seconds + this.cumulative_totals_added.total_seconds);
              }
              if (distance_unit == 'meters' && this.props.training_session.completed_meters == (this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance) {
                this.cumulative_totals_added.total_distance -= total_distance;
                this.props.training_session.completed_meters = (this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance;
              }
              else if (distance_unit == 'yards' && this.props.training_session.completed_yards == xhale.helper.meters_to_yards(this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance) {
                this.cumulative_totals_added.total_distance -= total_distance;
                this.props.training_session.completed_yards = xhale.helper.meters_to_yards(this.props.training_session.training_log_completed_km * 1000) + this.cumulative_totals_added.total_distance;
              }
              else if (distance_unit == 'km' && this.props.training_session.completed_km == this.props.training_session.training_log_completed_km + this.cumulative_totals_added.total_distance) {
                this.cumulative_totals_added.total_distance -= total_distance;
                this.props.training_session.completed_km = this.props.training_session.training_log_completed_km + this.cumulative_totals_added.total_distance;
              }
              else if (distance_unit == 'miles' && this.props.training_session.completed_miles == xhale.helper.km_to_miles(this.props.training_session.training_log_completed_km) + this.cumulative_totals_added.total_distance) {
                this.cumulative_totals_added.total_distance -= total_distance;
                this.props.training_session.completed_miles = xhale.helper.km_to_miles(this.props.training_session.training_log_completed_km) + this.cumulative_totals_added.total_distance;
              }
            }

          }

        }
      }
    }

  }

  render() {

    // This is used to throw an error mostly to check for logging functionality.
    if ('test_training_session_form_throw_error' in window && window.test_training_session_form_throw_error) {
      throw 'Test throwing an error';
    }

    var discipline_category = this.props.training_session.getDisciplineCategory();

    var athlete_fields_readonly = (this.props.perspective == 'coach') ? true : false;

    if (discipline_category == 'swim') {
      var distance_unit = this.props.training_session.getPoolUnit();
      var pool_unit = this.props.training_session.getPoolUnit();
    }
    else {
      var distance_unit = (this.props.training_session.getDistanceUnit() == 'kilometers') ? 'km' : 'miles';
      var pool_unit = this.props.training_session.getPoolUnit();
    }

    var completed_minutes;
    var completed_distance;
    if (!this.props.training_session.missed_session && (discipline_category == 'swim' || discipline_category == 'bike' || discipline_category == 'run' || discipline_category == 'gym' || discipline_category == 'other')) {

      completed_minutes = (
        <div className="field-wrapper number">
          {'completed_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_minutes} />}
          <label htmlFor="id_completed_minutes">Time completed:</label>
          <input type="number" className="session-form__number-field" name="completed_minutes" step="1" id="id_completed_minutes" min="0" value={this.props.training_session.completed_minutes || ""} onChange={this.change_completed_minutes} /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      if (distance_unit == 'meters') {
        var completed_distance_value = decimalPlaces(this.props.training_session.completed_meters, 0, 'null', false, true);
      }
      else if (distance_unit == 'yards') {
        var completed_distance_value = decimalPlaces(this.props.training_session.completed_yards, 0, 'null', false, true);
      }
      else if (distance_unit == 'km') {
        var completed_distance_value = decimalPlaces(this.props.training_session.completed_km, 3, 'null', false, true);
      }
      else if (distance_unit == 'miles') {
        var completed_distance_value = decimalPlaces(this.props.training_session.completed_miles, 3, 'null', false, true);
      }

      completed_distance = (
        <div className="field-wrapper number">
          {'completed_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_distance} />}
          <label htmlFor="id_completed_distance">Distance completed:</label>
          <input type="number" className="session-form__number-field" name="completed_distance" step="0.001" id="id_completed_distance" min="0" value={completed_distance_value || ""} onChange={this.change_completed_distance} /> <span className="unit distance-unit">{distance_unit}</span>
        </div>
      );

    }

    var completed_swim_title;
    var completed_swim_minutes;
    var completed_swim_distance;
    var completed_bike_title;
    var completed_bike_minutes;
    var completed_bike_distance;
    var completed_run_title;
    var completed_run_minutes;
    var completed_run_distance;
    if (!this.props.training_session.missed_session && discipline_category == 'brick') {

      if (distance_unit == 'km') {
        var completed_bike_distance = decimalPlaces(this.props.training_session.completed_bike_km, 3, 'null', false, true);
        var completed_run_distance = decimalPlaces(this.props.training_session.completed_run_km, 3, 'null', false, true);
      }
      else if (distance_unit == 'miles') {
        var completed_bike_distance = decimalPlaces(this.props.training_session.completed_bike_miles, 3, 'null', false, true);
        var completed_run_distance = decimalPlaces(this.props.training_session.completed_run_miles, 3, 'null', false, true);
      }

      if (this.props.training_session.getPoolUnit() == 'yards') {
        var completed_swim_distance = decimalPlaces(this.props.training_session.completed_swim_yards, 0, 'null', false, true);
      }
      else if (this.props.training_session.getPoolUnit() == 'meters') {
        var completed_swim_distance = decimalPlaces(this.props.training_session.completed_swim_meters, 0, 'null', false, true);
      }

      var completed_swim_title = (<h4 className="swim">Swim</h4>);

      var completed_swim_minutes = (
        <div className="field-wrapper number">
          {'completed_swim_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_swim_minutes} />}
          <label htmlFor="id_completed_swim_minutes">Time completed:</label>
          <input type="number" className="session-form__number-field" name="completed_swim_minutes" step="1" id="id_completed_swim_minutes" min="0" value={this.props.training_session.completed_swim_minutes || ""} onChange={this.change_completed_swim_minutes } /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      var completed_swim_distance = (
        <div className="field-wrapper number">
          {'completed_swim_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_swim_distance} />}
          <label htmlFor="id_completed_swim_distance">Distance completed:</label>
          <input type="number" className="session-form__number-field" name="completed_swim_distance" step="0.001" id="id_completed_swim_distance" min="0" value={completed_swim_distance || ""} onChange={this.change_completed_swim_distance} /> <span className="unit swim-unit">{this.props.training_session.getPoolUnit()}</span>
        </div>
      );

      var completed_bike_title = (<h4 className="bike">Bike</h4>);

      var completed_bike_minutes = (
        <div className="field-wrapper number">
          {'completed_bike_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_minutes} />}
          <label htmlFor="id_completed_bike_minutes">Time completed:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_minutes" step="1" id="id_completed_bike_minutes" min="0" value={this.props.training_session.completed_bike_minutes || ""} onChange={this.change_completed_bike_minutes} /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      var completed_bike_distance = (
        <div className="field-wrapper number">
          {'completed_bike_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_distance} />}
          <label htmlFor="id_completed_bike_distance">Distance completed:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_distance" step="0.001" id="id_completed_bike_distance" min="0" value={completed_bike_distance || ""} onChange={this.change_completed_bike_distance} /> <span className="unit distance-unit">{this.props.training_session.getDistanceUnit() == 'kilometers' ? 'km' : 'miles'}</span>
        </div>
      );

      var completed_run_title = (<h4 className="run">Run</h4>);

      var completed_run_minutes = (
        <div className="field-wrapper number">
          {'completed_run_minutes' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_run_minutes} />}
          <label htmlFor="id_completed_run_minutes">Time completed:</label>
          <input type="number" className="session-form__number-field" name="completed_run_minutes" step="1" id="id_completed_run_minutes" min="0" value={this.props.training_session.completed_run_minutes || ""} onChange={this.change_completed_run_minutes} /> <span className="unit minute-unit">minutes</span>
        </div>
      );

      var completed_run_distance = (
        <div className="field-wrapper number">
          {'completed_run_distance' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_run_distance} />}
          <label htmlFor="id_completed_run_distance">Distance completed:</label>
          <input type="number" className="session-form__number-field" name="completed_run_distance" step="0.001" id="id_completed_run_distance" min="0" value={completed_run_distance || ""} onChange={this.change_completed_run_distance} /> <span className="unit distance-unit">{this.props.training_session.getDistanceUnit() == 'kilometers' ? 'km' : 'miles'}</span>
        </div>
      );

    }

    var rpe_field;
    if (!(this.props.athlete_data?.preferences && this.props.athlete_data?.preferences.hide_rate_of_perceived_exertion == 'y')) {
      rpe_field = <RPERow
        training_session={this.props.training_session}
        updateTrainingSession={this.props.updateTrainingSession}
        form_errors={this.props.form_errors}
      />
    }

    var title = (this.props.perspective == 'coach') ? 'How did their training go?' : 'How did your training go?';

    return (
      <div className="session-completed-details">
        <h3>{title}</h3>
        {completed_minutes}
        {completed_distance}
        {completed_swim_title}
        {completed_swim_minutes}
        {completed_swim_distance}
        {completed_bike_title}
        {completed_bike_minutes}
        {completed_bike_distance}
        {completed_run_title}
        {completed_run_minutes}
        {completed_run_distance}
        {rpe_field}
      </div>
    );

  }
}

/**
 * React component representing a single training file to import.
 */
class ImportingDataMessage extends React.Component {

  /**
   * The checkbox for whether to import this training file has been clicked.
   */
  handleClick = () => {
    this.props.toggleImporting(this.props.training_file.file_data.id);
  }

  /**
   * Implement React's render method.
   */
  render() {
    var datetime = this.props.training_file.file_data.local_activity_start_datetime;

    var already_imported = (typeof(this.props.attached_training_logs) != 'undefined' && this.props.attached_training_logs.indexOf(this.props.training_file.file_data.id) >= 0) ? true : false;

    if (already_imported) {
      var classes = "importing-activity-file already-imported";
      var add_button = <div className="already-imported-text" style={{position: 'absolute', right: '8px', top: '11px'}}>already added</div>;
    }
    else {
      if (this.props.importing) {
        var button_text = String.fromCharCode(10003); // tick
        var classes = "importing-activity-file importing";
      }
      else {
        var button_text = '';
        var classes = "importing-activity-file not-importing";
      }
      var add_button = <div className="add-button" onClick={this.handleClick}>{button_text}</div>;
    }

    return (
      <div className={classes}>
        {add_button}
        <div className="details">
          <span className="importing-activity-description">
            <span className="activity-type">{this.props.training_file.file_data.activity_type}</span> <span className="importing-activity-datetime">from <span className="importing-activity-date">{datetime.format('D, jS F Y')}</span> at <span className="importing-activity-time">{datetime.format('g:i a')}</span> </span>
          </span>
        </div>
      </div>
    );
  }
}

class TagInput extends React.Component {

  constructor(props) {
    super(props);
    this.state = this.getFreshState();
    this.debounceTimer = null;
    this.add_tag_input_ref = React.createRef();
  }

  getFreshState = () => ({
    remove_mode: false,
    removed_tags: [],
    input: '',
    suggestions: [],
    cache: {},
  });

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.training_session_uuid != prevProps.training_session_uuid) {
      this.setState(this.getFreshState());
    }
  }

  handleInputChange = (event) => {
    const input = event.target.value.toLowerCase();
    if (input.length <= 20) {
      this.setState({ input }, () => {
        this.debouncedFetchSuggestions(input);
      });
    }
  }

  debouncedFetchSuggestions = (input) => {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.fetchSuggestions(input), 300);
  }

  fetchSuggestions = (input) => {
    if (this.state.cache[input]) {
      this.setState({ suggestions: this.state.cache[input] });
    }
    else {
      this.props.fetchAutocompleteSuggestions(input, (suggestions) => {
        this.setState(prevState => ({
          suggestions,
          cache: { ...prevState.cache, [input]: suggestions }
        }));
      });
    }
  }

  handleAddTag = (tag) => {
    if (!this.props.tags.includes(tag) && tag.length <= 20 && tag.length > 0) {
      const new_tags = [...this.props.tags, tag];
      const new_removed_tags = removeFromArrayIfExists(this.state.removed_tags, tag);
      this.props.onUpdate(new_tags);
      this.setState({ input: '', suggestions: [], removed_tags: new_removed_tags });
    }
  }

  handleRemoveTag = (tag_to_remove) => {
    const new_tags = this.props.tags.filter(tag => tag !== tag_to_remove);
    const new_removed_tags = addToArrayIfNotExists(this.state.removed_tags, tag_to_remove);
    this.props.onUpdate(new_tags);
    this.setState({removed_tags: new_removed_tags});
  }

  handleToggleRemoveTag = (tag) => {
    if (this.state.removed_tags.indexOf(tag) > -1) {
      this.handleAddTag(tag);
    }
    else {
      this.handleRemoveTag(tag);
    }
  }

  handleKeyDown = (event) => {
    if (event.key === 'Enter' && this.state.input) {
      this.handleAddTag(this.state.input);
      event.preventDefault();
    }
  }

  handleToggleRemoveMode = () => {
    this.setState({remove_mode: !this.state.remove_mode});
  }

  render() {

    let existing_and_removed_tags = [...this.state.removed_tags, ...this.props.tags].sort();

    let edit_button;
    if (this.props.tags.length > 0) {
      edit_button = <span className="small-button" onClick={this.handleToggleRemoveMode} style={{padding: '5px 5px', marginLeft: '2px'}}><span className="pencil-icon" style={{padding: '7px'}}></span></span>;
    }
    else {
      edit_button = <span className="small-button small-button_disabled">no custom tags</span>;
    }

    return (
      <span className="tag-input">
          {existing_and_removed_tags.map((tag, index) => (
            <Tag
              key={tag}
              tag_name={tag}
              removed={this.state.removed_tags.indexOf(tag) > -1}
              tagClickedCallback={this.props.tagClickedCallback}
              toggleRemoveTagCallback={this.state.remove_mode ? () => this.handleToggleRemoveTag(tag) : null}
              active={(typeof(this.props.filtered_by_tags) != 'undefined' && this.props.filtered_by_tags.indexOf(tag) > -1) ? true : false }
            />
          ))}
        <span ref={this.add_tag_input_ref} className="fake-text-input" style={{
          display: 'inline-block',
          padding: '1px 1px 1px 5px',
          background: 'white',
          position: 'relative',
        }}>
          <input
            type="text"
            value={this.state.input}
            onChange={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            aria-label="Add a tag"
            placeholder="add a tag"
            style={{
              fontSize: '12px',
              border: 'none',
              padding: '0',
              width: '10em',
            }}
          />
          <span className={`tag-input__submit-new-tag-button small-button ${this.state.input === '' ? 'small-button_disabled' : ''}`} onClick={() => this.handleAddTag(this.state.input)} style={{
              display: 'inline-block',
              height: '12px',
              padding: '3px',
              fontSize: '11px',
              borderRadius: '2px',
              width: '14px',
            }}
          >
            {'\u23CE'}
          </span>
        {this.state.suggestions.length > 0 && (
          <TagInputSuggestionsDropdown
            suggestions={this.state.suggestions}
            handleAddTag={this.handleAddTag}
            anchor_element_ref={this.add_tag_input_ref}
          />
        )}
        </span> {edit_button}
      </span>
    );
  }
}

class TagInputSuggestionsDropdown extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      highlighted_option: null,
    };

    this.dropdownList = React.createRef();
  }

  shouldDisplayAboveAnchorElement = () => {
    const rect = this.props.anchor_element_ref.current?.getBoundingClientRect();
    const space_below = rect ? window.innerHeight - rect.bottom : 0;
    const is_display_above = space_below < 92; // Assuming a height of 92 pixels for the suggestion list which is 5 items.
    return is_display_above;
  }

  render() {

    if (!this.props.anchor_element_ref.current) {
      return null;
    }

    const should_display_above_anchor = this.shouldDisplayAboveAnchorElement();

    // Using position absolute here works well, except it means the menu will
    // disappear behind other elements beyond the scroll. But fixed styling
    // makes positioning it more complicated.
    let dropdown_style = {
      marginTop: should_display_above_anchor ? '0px' : '4px',
    }

    // Adjust position based on space availability
    if (should_display_above_anchor) {
      const anchor_element_rect = this.props.anchor_element_ref.current?.getBoundingClientRect();
      dropdown_style.bottom = anchor_element_rect.height + 'px';
    }

    let dropdown_row_elements = [];
    let options_counter = 0;
    for (let i = 0; i < this.props.suggestions.length; i++) {

      dropdown_row_elements.push(
        <div
          key={i}
          className="tag-input__dropdown-list-item"
          style={{padding: '3px'}}
          onClick={this.props.handleAddTag.bind(null, this.props.suggestions[i])}>
          {this.props.suggestions[i]}
        </div>
      );

      options_counter++;

    }

    let dropdown_list = (
      <div
        className="tag-input__dropdown-list"
        ref={this.dropdownList}
        style={dropdown_style}
        onKeyDown={this.handleKeyDown}
        onBlur={this.handleBlur}
        tabIndex="0"
      >
        {dropdown_row_elements}
      </div>
    );

    return dropdown_list;

  }

}

class Tag extends React.Component {

  handleTagClicked = () => {
    if (this.props.toggleRemoveTagCallback) {
      this.props.toggleRemoveTagCallback(this.props.tag_name);
    }
    else if (this.props.tagClickedCallback && !this.props.removed) {
      this.props.tagClickedCallback(this.props.tag_name);
    }
  }

  render() {
    let class_name = 'tag';
    if (this.props.removed) {
      class_name += ' removed';
    }
    else if (this.props.active) {
      class_name += ' active';
    }

    let remove_tag_element;
    if (this.props.toggleRemoveTagCallback) {
      remove_tag_element = <span className="remove-button" style={{fontWeight: 'bold'}}>x</span>;
    }

    return <span className={class_name} onClick={this.handleTagClicked}>{this.props.tag_name} {remove_tag_element}</span>
  }

}

class SavedSelectionLink extends React.Component {

  openSavedSelection = () => {
    if (this.props.openSavedSelectionCallback) {
      this.props.openSavedSelectionCallback(this.props.collection, this.props.training_session);
    }
  }

  render = () => {
    return <li key={this.props.collection.id} onClick={this.openSavedSelection} style={{cursor: 'pointer', textDecoration: 'underline'}}>{this.props.collection.name}</li>;
  }

}

class ExtraDetails extends React.Component {

  state = {
    enter_data_manually_open: null,
    import_training_open: null,
  }

  change_completed_bike_average_speed = (event) => {
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      this.props.training_session.completed_bike_average_km_hr = event.target.value;
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles'){
      this.props.training_session.completed_bike_average_miles_hr = event.target.value;
    }
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_bike_average_heart_rate_bpm = (event) => {
    this.props.training_session.completed_bike_average_heart_rate_bpm = event.target.value;
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  changeCompletedBikeAverageWatts = (event) => {
    this.props.training_session.completed_bike_average_watts = event.target.value;
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  changeCompletedBikeNormalizedWatts = (event) => {
    this.props.training_session.completed_bike_normalized_watts = event.target.value;
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_bike_height_climbed = (event) => {
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      this.props.training_session.completed_bike_meters_climbed = event.target.value;
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles'){
      this.props.training_session.completed_bike_feet_climbed = event.target.value;
    }
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_run_height_climbed = (event) => {
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      this.props.training_session.completed_run_meters_climbed = event.target.value;
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles'){
      this.props.training_session.completed_run_feet_climbed = event.target.value;
    }
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_bike_average_cadence_rpm = (event) => {
    this.props.training_session.completed_bike_average_cadence_rpm = event.target.value;
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_completed_run_average_heart_rate_bpm = (event) => {
    this.props.training_session.completed_run_average_heart_rate_bpm = event.target.value;
    this.recordManuallyAlteredFields();
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_missed_session = (event) => {
    this.props.training_session.missed_session = event.target.checked;

    // If the session is marked as missed, do not import any data.
    if (this.props.training_session.missed_session) {
      for (let i = 0; i < this.props.file_ids_marked_to_import; i++) {
        this.props.toggleImporting(this.props.file_ids_marked_to_import[i]);
      }
    }

    this.props.updateTrainingSession(this.props.training_session);
  }

  handleMissedSessionClicked = () => {
    this.props.training_session.missed_session = this.props.training_session.missed_session ? false : true;
    this.props.updateTrainingSession(this.props.training_session);
  }

  change_training_log_url = (event) => {
    this.props.training_session.training_log_url = event.target.value;
    this.props.updateTrainingSession(this.props.training_session);
  }

  handleSelectButtonClicked = (event) => {
    if (this.props.handleSelectTrainingSession) {
      this.props.handleSelectTrainingSession(this.props.training_session.id);
    }
  }

  componentDidMount = () => {
    this.importAttachedData();
  }

  UNSAFE_componentWillUpdate = () => {
    this.importAttachedData();
  }

  // Record whether the extra data fields have been manually updated.
  recordManuallyAlteredFields = () => {
      this.saved_fields_match_training_log = {};

      this.saved_fields_match_training_log.completed_run_average_heart_rate_bpm = (this.props.training_session.completed_run_average_heart_rate_bpm == this.props.training_session.training_log_completed_run_average_heart_rate_bpm) ? true : false;
      this.saved_fields_match_training_log.completed_bike_average_heart_rate_bpm = (this.props.training_session.completed_bike_average_heart_rate_bpm == this.props.training_session.training_log_completed_bike_average_heart_rate_bpm) ? true : false;
      this.saved_fields_match_training_log.completed_bike_average_cadence_rpm = (this.props.training_session.completed_bike_average_cadence_rpm == this.props.training_session.training_log_completed_bike_average_cadence_rpm) ? true : false;
      this.saved_fields_match_training_log.completed_bike_average_watts = (this.props.training_session.completed_bike_average_watts == this.props.training_session.training_log_completed_bike_average_watts) ? true : false;
      this.saved_fields_match_training_log.completed_bike_normalized_watts = (this.props.training_session.completed_bike_normalized_watts == this.props.training_session.training_log_completed_bike_normalized_watts) ? true : false;

      if (this.props.training_session.getDistanceUnit() == 'kilometers') {

        // Ensure values are set.
        const completed_bike_average_km_hr = (typeof(this.props.training_session.completed_bike_average_km_hr) == 'undefined') ? null : this.props.training_session.completed_bike_average_km_hr;
        const completed_bike_meters_climbed = (typeof(this.props.training_session.completed_bike_meters_climbed) == 'undefined') ? null : this.props.training_session.completed_bike_meters_climbed;
        const completed_run_meters_climbed = (typeof(this.props.training_session.completed_run_meters_climbed) == 'undefined') ? null : this.props.training_session.completed_run_meters_climbed;
        const training_log_completed_bike_average_km_hr = (typeof(this.props.training_session.training_log_completed_bike_average_km_hr) == 'undefined') ? null : this.props.training_session.training_log_completed_bike_average_km_hr;
        const training_log_completed_bike_meters_climbed = (typeof(this.props.training_session.training_log_completed_bike_meters_climbed) == 'undefined') ? null : this.props.training_session.training_log_completed_bike_meters_climbed;
        const training_log_completed_run_meters_climbed = (typeof(this.props.training_session.training_log_completed_run_meters_climbed) == 'undefined') ? null : this.props.training_session.training_log_completed_run_meters_climbed;

        this.saved_fields_match_training_log.completed_bike_average_km_hr = (Math.round(completed_bike_average_km_hr) == Math.round(training_log_completed_bike_average_km_hr)) ? true : false;
        this.saved_fields_match_training_log.completed_bike_meters_climbed = (Math.round(completed_bike_meters_climbed) == Math.round(training_log_completed_bike_meters_climbed)) ? true : false;
        this.saved_fields_match_training_log.completed_run_meters_climbed = (Math.round(completed_run_meters_climbed) == Math.round(training_log_completed_run_meters_climbed)) ? true : false;

      }
      else if (this.props.training_session.getDistanceUnit() == 'miles') {

        // Ensure values are set.
        const completed_bike_average_miles_hr = (typeof(this.props.training_session.completed_bike_average_miles_hr) == 'undefined') ? null : this.props.training_session.completed_bike_average_miles_hr;
        const completed_bike_feet_climbed = (typeof(this.props.training_session.completed_bike_feet_climbed) == 'undefined') ? null : this.props.training_session.completed_bike_feet_climbed;
        const completed_run_feet_climbed = (typeof(this.props.training_session.completed_run_feet_climbed) == 'undefined') ? null : this.props.training_session.completed_run_feet_climbed;
        const training_log_completed_bike_average_miles_hr = (typeof(this.props.training_session.training_log_completed_bike_average_km_hr) == 'undefined') ? null : kilometersToMiles(this.props.training_session.training_log_completed_bike_average_km_hr);
        const training_log_completed_bike_feet_climbed = (typeof(this.props.training_session.training_log_completed_bike_meters_climbed) == 'undefined') ? null : xhale.helper.meters_to_feet(this.props.training_session.training_log_completed_bike_meters_climbed);
        const training_log_completed_run_feet_climbed = (typeof(this.props.training_session.training_log_completed_run_meters_climbed) == 'undefined') ? null : xhale.helper.meters_to_feet(this.props.training_session.training_log_completed_run_meters_climbed);

        this.saved_fields_match_training_log.completed_bike_average_miles_hr = (Math.round(completed_bike_average_miles_hr) == Math.round(training_log_completed_bike_average_miles_hr)) ? true : false;
        this.saved_fields_match_training_log.completed_bike_feet_climbed = (Math.round(completed_bike_feet_climbed) == Math.round(training_log_completed_bike_feet_climbed)) ? true : false;
        this.saved_fields_match_training_log.completed_run_feet_climbed = (Math.round(completed_run_feet_climbed) == Math.round(training_log_completed_run_feet_climbed)) ? true : false;
      }

  }

  /**
   * If there is data ready to import, update the session with that data.
   */
  importAttachedData = () => {

    // TODO See other comments in other component's importAttachedData methods.

    function arraysEqual(a, b) {
      if (a === b) return true;
      if (a == null || b == null) return false;
      if (a.length != b.length) return false;
      for (var i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    }

    var discipline_category = this.props.training_session.getDisciplineCategory();
    if (discipline_category == 'none') {
      return;
    }

    if (!this.props.file_ids_marked_to_import) {
      return;
    }

    if (this.current_session_id != this.props.training_session.id && this.current_session_id != 'temp-' + this.props.training_session.temporary_id) {
      this.setState({enter_data_manually_open: null, import_training_open: null});
      this.saved_fields_match_training_log = null;
    }

    const import_updated = !(this.current_session_id == this.props.training_session.id && arraysEqual(this.props.file_ids_marked_to_import, this.previous_file_ids_marked_to_import));
    if (import_updated || this.unprocessed_file_ids.length > 0) {
      this.current_session_id = this.props.training_session.id;
      this.unprocessed_file_ids = this.props.file_ids_marked_to_import.slice();
      this.previous_file_ids_marked_to_import = this.props.file_ids_marked_to_import.slice();
    }
    else {
      return;
    }

    if (this.saved_fields_match_training_log === null) {
      this.recordManuallyAlteredFields();
    }

    // Data we are adding.
    var average_watts = [];
    var normalized_watts = [];
    var cadence = [];
    var heart_rate_bike = [];
    var heart_rate_run = [];
    var speed = [];
    var height_climbed_bike = 0;
    var height_climbed_run = 0;

    // Add already saved data.
    if (this.props.training_session.training_log_completed_bike_average_watts) {
      average_watts.push([this.props.training_session.training_log_completed_bike_average_watts, this.props.training_session.training_log_completed_bike_seconds]);
    }
    if (this.props.training_session.training_log_completed_bike_normalized_watts) {
      normalized_watts.push([this.props.training_session.training_log_completed_bike_normalized_watts, this.props.training_session.training_log_completed_bike_seconds]);
    }
    if (this.props.training_session.training_log_completed_bike_average_cadence_rpm) {
      cadence.push([this.props.training_session.training_log_completed_bike_average_cadence_rpm, this.props.training_session.training_log_completed_bike_seconds]);
    }
    if (this.props.training_session.training_log_completed_bike_average_heart_rate_bpm) {
      heart_rate_bike.push([this.props.training_session.training_log_completed_bike_average_heart_rate_bpm, this.props.training_session.training_log_completed_bike_seconds]);
    }
    if (this.props.training_session.training_log_completed_run_average_heart_rate_bpm) {
      heart_rate_run.push([this.props.training_session.training_log_completed_run_average_heart_rate_bpm, this.props.training_session.training_log_completed_run_seconds]);
    }
    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      if (this.props.training_session.training_log_completed_bike_average_km_hr) {
        speed.push([this.props.training_session.training_log_completed_bike_average_km_hr, this.props.training_session.training_log_completed_bike_seconds]);
      }
      if (this.props.training_session.training_log_completed_bike_meters_climbed) {
        height_climbed_bike += this.props.training_session.training_log_completed_bike_meters_climbed;
      }
      if (this.props.training_session.training_log_completed_run_meters_climbed) {
        height_climbed_run += this.props.training_session.training_log_completed_run_meters_climbed;
      }
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles') {
      if (this.props.training_session.training_log_completed_bike_average_km_hr) {
        speed.push([xhale.helper.km_to_miles(this.props.training_session.training_log_completed_bike_average_km_hr), this.props.training_session.training_log_completed_bike_seconds]);
      }
      if (this.props.training_session.training_log_completed_bike_meters_climbed) {
        height_climbed_bike += xhale.helper.meters_to_feet(this.props.training_session.training_log_completed_bike_meters_climbed);
      }
      if (this.props.training_session.training_log_completed_run_meters_climbed) {
        height_climbed_run += xhale.helper.meters_to_feet(this.props.training_session.training_log_completed_run_meters_climbed);
      }
    }

    if (this.props.related_training_logs && this.props.related_training_logs.length > 0) {
      for (var i = 0; i < this.props.related_training_logs.length; i++) {

        var related_training_log = this.props.related_training_logs[i];
        var importing = (this.props.file_ids_marked_to_import.indexOf(related_training_log.file_data.id) >= 0) ? true : false;
        var already_added_and_saved = (typeof(this.props.training_session.attached_training_logs) != 'undefined' && this.props.training_session.attached_training_logs.indexOf(related_training_log.file_data.id) >= 0) ? true : false;

        var store_state = store.getState(); // TODO No longer a pure function. Pass this into the component through props instead.
        var related_training_log = store_state.main.uploaded_files[related_training_log.file_data.id];

        if (related_training_log.activities) {
          this.unprocessed_file_ids.splice(this.unprocessed_file_ids.indexOf(related_training_log.file_data.id), 1);
        }

        if (importing && !already_added_and_saved && related_training_log.activities) {

          $.each(related_training_log.activities, (key, activity_data) => {

            // If activity type is not set, determine if this is a cycling or
            // running activity. Swim doesn't have any fields here.
            if (activity_data.discipline == 'unknown' || activity_data.discipline == 'other' || activity_data.discipline == 'training') {
              if (discipline_category == 'bike') {
                activity_data.discipline = 'cycling';
              }
              else if (discipline_category == 'run') {
                activity_data.discipline = 'running';
              }
            }

            if (activity_data.discipline == 'running') {
              if (activity_data.average_heart_rate_bpm) {
                heart_rate_run.push([activity_data.average_heart_rate_bpm, activity_data.total_time_seconds]);
              }
              if (this.props.training_session.getDistanceUnit() == 'kilometers') {
                if (activity_data.height_climbed_meters) {
                  height_climbed_run += activity_data.height_climbed_meters;
                }
              }
              else if (this.props.training_session.getDistanceUnit() == 'miles') {
                if (activity_data.height_climbed_feet) {
                  height_climbed_run += activity_data.height_climbed_feet;
                }
              }
 
            }
            else if (activity_data.discipline == 'cycling') {

              if (activity_data.normalized_watts) {
                normalized_watts.push([activity_data.normalized_watts, activity_data.total_time_seconds]);
              }

              if (activity_data.average_watts) {
                average_watts.push([activity_data.average_watts, activity_data.total_time_seconds]);
              }

              if (activity_data.average_cadence_rpm) {
                cadence.push([activity_data.average_cadence_rpm, activity_data.total_time_seconds]);
              }

              if (activity_data.average_heart_rate_bpm) {
                heart_rate_bike.push([activity_data.average_heart_rate_bpm, activity_data.total_time_seconds]);
              }

              if (this.props.training_session.getDistanceUnit() == 'kilometers') {
                if (activity_data.speed_km_hr) {
                  speed.push([activity_data.speed_km_hr, activity_data.total_time_seconds]);
                }
                if (activity_data.height_climbed_meters) {
                  height_climbed_bike += activity_data.height_climbed_meters;
                }
              }
              else if (this.props.training_session.getDistanceUnit() == 'miles') {
                if (activity_data.speed_miles_hr) {
                  speed.push([activity_data.speed_miles_hr, activity_data.total_time_seconds]);
                }
                if (activity_data.height_climbed_feet) {
                  height_climbed_bike += activity_data.height_climbed_feet;
                }
              }
            }

          });


        }

      }
    }

    if (this.saved_fields_match_training_log.completed_bike_average_cadence_rpm) {
      this.props.training_session.completed_bike_average_cadence_rpm = Math.round(meanWeightedByDuration(cadence));
    }

    if (this.saved_fields_match_training_log.completed_bike_average_watts) {
      this.props.training_session.completed_bike_average_watts = Math.round(meanWeightedByDuration(average_watts));
    }

    if (this.saved_fields_match_training_log.completed_bike_normalized_watts) {
      this.props.training_session.completed_bike_normalized_watts = Math.round(meanWeightedByDuration(normalized_watts));
    }

    if (this.saved_fields_match_training_log.completed_bike_average_heart_rate_bpm) {
      this.props.training_session.completed_bike_average_heart_rate_bpm = meanWeightedByDuration(heart_rate_bike);
    }

    if (this.saved_fields_match_training_log.completed_run_average_heart_rate_bpm) {
      this.props.training_session.completed_run_average_heart_rate_bpm = meanWeightedByDuration(heart_rate_run);
    }

    if (this.props.training_session.getDistanceUnit() == 'kilometers') {
      if (this.saved_fields_match_training_log.completed_bike_average_km_hr) {
        this.props.training_session.completed_bike_average_km_hr = meanWeightedByDuration(speed);
      }
      if (this.saved_fields_match_training_log.completed_bike_meters_climbed) {
        this.props.training_session.completed_bike_meters_climbed = height_climbed_bike;
      }
      if (this.saved_fields_match_training_log.completed_run_meters_climbed) {
        this.props.training_session.completed_run_meters_climbed = height_climbed_run;
      }
    }
    else if (this.props.training_session.getDistanceUnit() == 'miles') {
      if (this.saved_fields_match_training_log.completed_bike_average_miles_hr) {
        this.props.training_session.completed_bike_average_miles_hr = meanWeightedByDuration(speed);
      }
      if (this.saved_fields_match_training_log.completed_bike_feet_climbed) {
        this.props.training_session.completed_bike_feet_climbed = height_climbed_bike;
      }
      if (this.saved_fields_match_training_log.completed_run_feet_climbed) {
        this.props.training_session.completed_run_feet_climbed = height_climbed_run;
      }
 
    }

  }

  getTrainingMapDisplayCSS = () => {
    // Should map display
    if (this.props.mode == 'training_session' && !this.props.training_session.missed_session) {
      if (this.props.training_session.activity_data) {
        return 'block';
      }
      else {
        for (var i = 0; i < this.props.related_training_logs.length; i++) {
          var importing = (this.props.file_ids_marked_to_import.indexOf(this.props.related_training_logs[i].file_data.id) >= 0) ? true : false;
          if (importing && typeof(this.props.related_training_logs[i].activities) != 'undefined') {
            return 'block';
          }
        }
      }
    }
    return 'none';
  }

  handleEnterDataManuallyClicked = () => {
    const enter_data_manually_open = this.state.enter_data_manually_open ? false : true;
    this.setState({enter_data_manually_open: enter_data_manually_open});
  }

  displayImportTraining = () => {
    if (this.state.import_training_open === null) {
      return this.props.related_training_logs && this.props.related_training_logs.length > 0 && !this.props.training_session.missed_session;
    }
    else {
      return this.state.import_training_open;
    }
  }

  handleImportTrainingClicked = () => {

    const import_training_open = this.displayImportTraining() ? false : true;

    // De-select importing files if the section is hidden.
    if (!import_training_open) {
      for (let i = 0; i < this.props.file_ids_marked_to_import.length; i++) {
        this.props.toggleImporting(this.props.file_ids_marked_to_import[i]);
      }
    }

    this.setState({import_training_open: import_training_open});
  }

  handleTagUpdated = (tags) => {
    this.props.training_session.tags_user = tags;
    this.props.updateTrainingSession(this.props.training_session);
    this.props.formChanged();
  }

  fetchTagAutocompleteSuggestions = (input, callback) => {
    ajaxRequest({
      url: '/coach/fetch-tag-suggestions/',
      method: 'GET',
      data: {'q': input},
      success: (response, status) => {
        callback(response.suggestions)
      },
      expected_response_type: 'json',
    })
  }

  render() {

    var athlete_fields_readonly = (this.props.perspective == 'coach') ? true : false;
    var discipline_category = this.props.training_session.getDisciplineCategory();
    var importing_training = this.props.related_training_logs && this.props.related_training_logs.length > 0 && !this.props.training_session.missed_session;

    var related_training_logs_widget;
    var related_training_logs = [];
    if (importing_training) {
      for (var i = 0; i < this.props.related_training_logs.length; i++) {
        var related_training_log = this.props.related_training_logs[i];
        var importing = (this.props.file_ids_marked_to_import.indexOf(related_training_log.file_data.id) >= 0) ? true : false;
        related_training_logs.push(
          <ImportingDataMessage
            key={related_training_log.file_data.id}
            training_file={related_training_log}
            toggleImporting={this.props.toggleImporting}
            importing={importing}
            attached_training_logs={this.props.training_session.attached_training_logs}
          />
        );
      }

      related_training_logs_widget = (
        <div className="related-training-logs-widget">
          <h5>Check the activities you would like to include in this session</h5>
          {related_training_logs}
        </div>
      );
    }

    var training_log_url;
    if (this.props.display_training_log_url && this.props.related_training_logs && (discipline_category == 'swim' || discipline_category == 'bike' || discipline_category == 'run' || discipline_category == 'gym' || discipline_category == 'brick' || discipline_category == 'other')) {
      training_log_url = (
        <div className="field-wrapper">
          {'training_log_url' in this.props.form_errors && <Errors errors={this.props.form_errors.training_log_url} />}
          <label htmlFor="id_training_log_url">Training log url:</label>
          <input type="url" readOnly={athlete_fields_readonly} name="training_log_url" id="id_training_log_url" value={this.props.training_session.training_log_url || ""} onChange={this.change_training_log_url} />
        </div>
      )
    }

    var missed_session;
    var is_missable_discipline = (discipline_category == 'swim' || discipline_category == 'bike' || discipline_category == 'run' || discipline_category == 'gym' || discipline_category == 'brick' || discipline_category == 'other');
    var no_training_attached = (!this.props.training_session.attached_training_logs || this.props.training_session.attached_training_logs.length < 1);
    if (this.props.perspective == 'coach') {
      if (this.props.training_session.missed_session) {
        missed_session = (<div className="field-wrapper">This was a missed session</div>);
      }
    }
    else if (is_missable_discipline && (this.props.training_session.missed_session == true || no_training_attached)) {
      missed_session = (<div className="field-wrapper inline">
        {'missed_session' in this.props.form_errors && <Errors errors={this.props.form_errors.missed_session} />}
        <label htmlFor="id_missed_session">Missed session:</label>
        <input type="checkbox" readOnly={athlete_fields_readonly} name="missed_session" id="id_missed_session" checked={this.props.training_session.missed_session ? true : false} onChange={this.change_missed_session} />
      </div>);
    }

    var permalink;
    if (this.props.display_more_details_link && this.props.training_session.has_permanent_id() && this.props.training_session.attached_training_logs && this.props.training_session.attached_training_logs.length > 0) {
      permalink = (<a className="permalink" href={'/session/' + this.props.training_session.id + '/'}>Click for more details</a>);
    }

    var average_watts;
    var normalized_watts;
    var average_speed;
    var bike_height_climbed_field;
    var run_height_climbed_field;
    var bike_average_heart_rate;
    var bike_average_cadence;
    if (discipline_category == 'bike' || discipline_category == 'brick') {

      if (this.props.training_session.getDistanceUnit() == 'kilometers') {
        var completed_bike_average_speed = decimalPlaces(this.props.training_session.completed_bike_average_km_hr, 2, 'null', false, true);
        var completed_bike_height_climbed = decimalPlaces(this.props.training_session.completed_bike_meters_climbed, 0, 'null', false, true);
      }
      else if (this.props.training_session.getDistanceUnit() == 'miles') {
        var completed_bike_average_speed = decimalPlaces(this.props.training_session.completed_bike_average_miles_hr, 2, 'null', false, true);
        var completed_bike_height_climbed = decimalPlaces(this.props.training_session.completed_bike_feet_climbed, 0, 'null', false, true);
      }

      average_watts = (
        <div className="field-wrapper number" style={{clear: 'left'}}>
          {'completed_bike_average_watts' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_average_watts} />}
          <label htmlFor="id_completed_bike_average_watts">Average power:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_average_watts" step="1" id="id_completed_bike_average_watts" min="0" value={this.props.training_session.completed_bike_average_watts || ""} onChange={this.changeCompletedBikeAverageWatts} /> <span className="unit power-unit">watts</span>
        </div>
      );

      normalized_watts = (
        <div className="field-wrapper number" style={{clear: 'left'}}>
          {'completed_bike_normalized_watts' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_normalized_watts} />}
          <label htmlFor="id_completed_bike_normalized_watts">Normalised power:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_normalized_watts" step="1" id="id_completed_bike_normalized_watts" min="0" value={this.props.training_session.completed_bike_normalized_watts || ""} onChange={this.changeCompletedBikeNormalizedWatts} /> <span className="unit power-unit">watts</span>
        </div>
      );

      average_speed = (
        <div className="field-wrapper number">
          {'completed_bike_average_speed' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_average_speed} />}
          <label htmlFor="id_completed_bike_average_speed">Average speed:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_average_speed" step="0.01" id="id_completed_bike_average_speed" min="0" value={completed_bike_average_speed || ""} onChange={this.change_completed_bike_average_speed} /> <span className="unit speed-unit">{this.props.training_session.getDistanceUnit() == 'kilometers' ? 'kph' : 'mph'}</span>
        </div>
      );

      bike_height_climbed_field = (
        <div className="field-wrapper number">
          {'completed_bike_height_climbed' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_height_climbed} />}
          <label htmlFor="id_completed_bike_height_climbed">Height climbed:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_height_climbed" step="1" id="id_completed_bike_height_climbed" min="0" value={completed_bike_height_climbed || ""} onChange={this.change_completed_bike_height_climbed} /> <span className="unit height-unit">{this.props.training_session.getDistanceUnit() == 'kilometers' ? 'meters' : 'feet'}</span>
        </div>
      );

      bike_average_heart_rate = (
        <div className="field-wrapper number">
          {'completed_bike_average_heart_rate_bpm' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_average_heart_rate_bpm} />}
          <label htmlFor="id_completed_bike_average_heart_rate_bpm">Average heart rate:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_average_heart_rate_bpm" step="1" id="id_completed_bike_average_heart_rate_bpm" min="0" value={this.props.training_session.completed_bike_average_heart_rate_bpm || ""} onChange={this.change_completed_bike_average_heart_rate_bpm} /> <span className="unit heart-rate-unit">bpm</span>
        </div>
      );

      bike_average_cadence = (
        <div className="field-wrapper number">
          {'completed_bike_average_cadence_rpm' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_bike_average_cadence_rpm} />}
          <label htmlFor="id_completed_bike_average_cadence_rpm">Average cadence:</label>
          <input type="number" className="session-form__number-field" name="completed_bike_average_cadence_rpm" step="1" id="id_completed_bike_average_cadence_rpm" min="0" value={this.props.training_session.completed_bike_average_cadence_rpm || ""} onChange={this.change_completed_bike_average_cadence_rpm} /> <span className="unit cadence-unit">rpm</span>
        </div>
      );
    }

    var run_average_heart_rate;
    if (discipline_category == 'run' || discipline_category == 'brick') {
      run_average_heart_rate = (
        <div className="field-wrapper number">
          {'completed_run_average_heart_rate_bpm' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_run_average_heart_rate_bpm} />}
          <label htmlFor="id_completed_run_average_heart_rate_bpm">Average heart rate:</label>
          <input type="number" className="session-form__number-field" name="completed_run_average_heart_rate_bpm" step="1" id="id_completed_run_average_heart_rate_bpm" min="0" value={this.props.training_session.completed_run_average_heart_rate_bpm || ""} onChange={this.change_completed_run_average_heart_rate_bpm} /> <span className="unit heart-rate-unit">bpm</span>
        </div>
      );

      if (this.props.training_session.getDistanceUnit() == 'kilometers') {
        var completed_run_height_climbed = decimalPlaces(this.props.training_session.completed_run_meters_climbed, 0, 'null', false, true);
      }
      else if (this.props.training_session.getDistanceUnit() == 'miles') {
        var completed_run_height_climbed = decimalPlaces(this.props.training_session.completed_run_feet_climbed, 0, 'null', false, true);
      }

      run_height_climbed_field = (
        <div className="field-wrapper number">
          {'completed_run_height_climbed' in this.props.form_errors && <Errors errors={this.props.form_errors.completed_run_height_climbed} />}
          <label htmlFor="id_completed_run_height_climbed">Height climbed:</label>
          <input type="number" className="session-form__number-field" name="completed_run_height_climbed" step="1" id="id_completed_run_height_climbed" min="0" value={completed_run_height_climbed || ""} onChange={this.change_completed_run_height_climbed} /> <span className="unit height-unit">{this.props.training_session.getDistanceUnit() == 'kilometers' ? 'meters' : 'feet'}</span>
        </div>
      );

    }

    var bike_title;
    var run_title;
    if (discipline_category == 'brick') {
      bike_title = (<h4 className="bike">Bike</h4>);
      run_title = (<h4 className="run">Run</h4>);
    }

    var tags;
    if (hasBetaAccess('tagging_system') && this.props.perspective == 'coach' && this.props.display_tags) {
      var automatic_tag_names = [];
      for (var i = 0; i < this.props.training_session.tags_automatic.length; i++) {
        var active = (typeof(this.props.filtered_by_tags) != 'undefined' && this.props.filtered_by_tags.indexOf(this.props.training_session.tags_automatic[i]) > -1) ? true : false;
        automatic_tag_names.push(
          <Tag
            key={this.props.training_session.tags_automatic[i]}
            tag_name={this.props.training_session.tags_automatic[i]}
            tagClickedCallback={this.props.tag_clicked_callback}
            active={active}
          />
        );
      }

      let tag_input;
      if (hasBetaAccess('tagging_system_custom_tags')) {
        tag_input = <TagInput
          training_session_uuid={this.props.training_session.uuid}
          tags={this.props.training_session.tags_user}
          filtered_by_tags={this.props.filtered_by_tags}
          onUpdate={this.handleTagUpdated}
          fetchAutocompleteSuggestions={this.fetchTagAutocompleteSuggestions}
          tagClickedCallback={this.props.tag_clicked_callback}
        />
      }

      tags = (
        <div className="tags">
          <h3>Filter sessions by tag</h3>
          <span className="automatic-tags">{automatic_tag_names}</span>
          {tag_input}
        </div>
      )

    }

    var select_button;
    var collections_section;
    if (hasBetaAccess('compare_sessions') && this.props.perspective == 'coach' && this.props.handleSelectTrainingSession) {

      var select_button_text = (this.props.selected_sessions.indexOf(this.props.training_session.id) >= 0) ? 'remove from ' : 'add to ';
      select_button_text += (this.props.open_collection) ? this.props.open_collection.name + ' selection' : 'new selection';
      select_button = (
        <div>
          <div className="small-button" style={{display: 'inline-block'}} onClick={this.handleSelectButtonClicked}>{select_button_text}</div>
          <div className="description" style={{fontSize: '12px', marginTop: '6px'}}>To select multiple sessions, hold the shift key while clicking on the sessions on the calendar.</div>
        </div>
      );

      var collection_entities = [];
      if (this.props.session_collections && this.props.session_collections.by_session_id && this.props.session_collections.by_session_id[this.props.training_session.id]) {
        var collection_ids = this.props.session_collections.by_session_id[this.props.training_session.id];

        for (var i = 0; i < collection_ids.length; i++) {
          var collection = this.props.session_collections.by_id[collection_ids[i]];
          collection_entities.push(
            <SavedSelectionLink
              key={collection.id}
              display_more_details_link={this.props.display_more_details_link}
              open_saved_selection={this.props.display_more_details_link}
              training_session={this.props.training_session}
              collection={collection}
              openSavedSelectionCallback={this.props.openSavedSelectionCallback}
          />);
        }

      }

      collections_section = (
        <div>
          <h3>Saved selections</h3>
          <ul style={{margin: '8px'}}>
            {collection_entities}
          </ul>
        </div>
      )

    }

    var extra_details;
    if ((!importing_training || this.state.enter_data_manually_open) && !this.props.training_session.missed_session) {
      extra_details = (
        <div className="extra-details">
          {bike_title}
          {average_watts}
          {normalized_watts}
          {average_speed}
          {bike_height_climbed_field}
          {bike_average_heart_rate}
          {bike_average_cadence}
          {run_title}
          {run_average_heart_rate}
          {run_height_climbed_field}
        </div>
      );
    }

    var map_container;
    if (this.props.include_map) {
      map_container = (
        <div id="training-map-container" style={{ display: this.getTrainingMapDisplayCSS() }}>
          <MapComponent id='training-map' existing_activity_data={this.props.training_session.activity_data} mode={this.props.mode} related_training_logs={this.props.related_training_logs} file_ids_marked_to_import={this.props.file_ids_marked_to_import} />
        </div>
      );
    }

    var import_training_section;
    if (this.displayImportTraining()) {
      if (related_training_logs_widget) {
        import_training_section = (
          <div>
            {related_training_logs_widget}
          </div>
        );
      }
    }

    var title_enter_data_maually;
    var title_import_training;
    if (!this.props.training_session.missed_session) {
      if (this.props.related_training_logs && this.props.related_training_logs.length > 0) {
        title_enter_data_maually = <h3 className='session-form__enter-data-manually-title'><span onClick={this.handleEnterDataManuallyClicked}>Enter training data manually</span></h3>;
        title_import_training = <h3 className='session-form__import-training-title'><span onClick={this.handleImportTrainingClicked}>Import training</span></h3>;
      }
      else {
        title_enter_data_maually = <h3><span>Extra details</span></h3>;
      }
    }

    if (this.props.perspective == 'athlete') {
      return (
        <div>
          {title_enter_data_maually}
          {extra_details}
          {title_import_training}
          {import_training_section}
          {training_log_url}
          {map_container}
          {permalink}
          {missed_session}
          {tags}
          {collections_section}
          {select_button}
        </div>
      );
    }
    else {
      return (
        <div style={{marginTop: '12px'}}>
          {missed_session}
          {extra_details}
          {map_container}
          {permalink}
          {tags}
          {collections_section}
          {select_button}
        </div>
      );
    }

  }
}

class RPERow extends React.Component {

  changeRPE = (event) => {
    if (this.props.training_session) {
      this.props.training_session.rpe = event.target.value != '' ? parseInt(event.target.value) : null;
      this.props.updateTrainingSession(this.props.training_session);
    }
    else if (this.props.race) {
      this.props.race.rpe = event.target.value != '' ? parseInt(event.target.value) : null;
      this.props.updateRace(this.props.race);
    }
  }

  render() {

    let rpe_value = (this.props.training_session) ? this.props.training_session.rpe : this.props.race.rpe;

    return (
      <div className="rpe-row field-wrapper inline">
        {'rpe' in this.props.form_errors && <Errors errors={this.props.form_errors.rpe} />}
        <label htmlFor="rpe">RPE:</label><select name="rpe_select" id="rpe_select" style={{width: '148px', borderRadius: '3px', padding: '3px 0px'}} value={(rpe_value === 0 || rpe_value > 0) ? rpe_value : ""} onChange={this.changeRPE}>
          <option value="">Please select</option>
          <option value="0">0. Rest</option>
          <option value="1">1. Very easy</option>
          <option value="2">2. Easy</option>
          <option value="3">3. Easy to moderate</option>
          <option value="4">4. Moderate</option>
          <option value="5">5. Fairly hard</option>
          <option value="6">6. Hard</option>
          <option value="7">7. Very hard</option>
          <option value="8">8. Very, very hard</option>
          <option value="9">9. Nearly maximal</option>
          <option value="10">10. Maximal effort</option>
        </select>
      </div>
    );
  }
}

function seconds_to_hours_minutes_seconds(seconds, return_as_zero_padded_strings) {
  if (!seconds) {
    seconds = 0;
  }
  var minutes = Math.floor(seconds / 60);
  var hours = Math.floor(minutes / 60);
  var remaining_minutes = minutes % 60;
  var remaining_seconds = seconds % 60;
  if (return_as_zero_padded_strings) {
    return [hours, xhale.helper.zero_pad(remaining_minutes), xhale.helper.zero_pad(remaining_seconds)];
  }
  else {
    return [hours, remaining_minutes, remaining_seconds];
  }
}


function adjust_time_string(existing_seconds, time_unit, new_value) {

  var time_array = seconds_to_hours_minutes_seconds(existing_seconds);

  // Adjust the value.
  if (time_unit == 'hours') {
    time_array[0] = parseInt(new_value);
  }
  else if (time_unit == 'minutes') {
    time_array[1] = parseInt(new_value);
  }
  else if (time_unit == 'seconds') {
    time_array[2] = parseInt(new_value);
  }

  return (time_array[0] * 3600) + (time_array[1] * 60) + time_array[2];
}

class RaceStageRow extends React.Component {

  change_discipline = (event) => {
    this.props.race_stage.discipline = event.target.value;
    this.props.updateRace(this.props.race);
  }

  normalise_to_km = (value) => {
    if (this.props.race_stage.discipline_category() == 'swim') {
      if (this.props.race.getPoolUnit() == 'meters') {
        return value / 1000;
      }
      else if (this.props.race.getPoolUnit() == 'yards') {
        return xhale.helper.yards_to_meters(value) / 1000;
      }
    }
    else {
      if (this.props.race.getDistanceUnit() == 'kilometers') {
        return value;
      }
      else if (this.props.race.getDistanceUnit() == 'miles') {
        return xhale.helper.miles_to_km(value);
      }
    }
  }

  change_advertised_distance = (event) => {
    this.props.race_stage.advertised_distance_meters = this.normalise_to_km(event.target.value);
    this.props.updateRace(this.props.race);
  }

  change_actual_distance = (event) => {
    this.props.race_stage.actual_distance_meters = this.normalise_to_km(event.target.value);
    this.props.updateRace(this.props.race);
  }

  change_goal_time_hours = (event) => {
    this.props.race_stage.goal_seconds = adjust_time_string(this.props.race_stage.goal_seconds, 'hours', event.target.value);
    this.props.updateRace(this.props.race);
  }

  change_goal_time_minutes = (event) => {
    this.props.race_stage.goal_seconds = adjust_time_string(this.props.race_stage.goal_seconds, 'minutes', event.target.value);
    this.props.updateRace(this.props.race);
  }

  change_goal_time_seconds = (event) => {
    this.props.race_stage.goal_seconds = adjust_time_string(this.props.race_stage.goal_seconds, 'seconds', event.target.value);
    this.props.updateRace(this.props.race);
  }

  change_duration_completed_hours = (event) => {
    var update_overall_time = this.stages_match_overall_time();
    this.props.race_stage.completed_seconds = adjust_time_string(this.props.race_stage.completed_seconds, 'hours', event.target.value);
    if (update_overall_time) {
      this.props.race.overall_seconds = this.props.race.get_overall_time_calculated_from_stages();
    }
    this.props.updateRace(this.props.race);
  }

  change_duration_completed_minutes = (event) => {
    var update_overall_time = this.stages_match_overall_time();
    this.props.race_stage.completed_seconds = adjust_time_string(this.props.race_stage.completed_seconds, 'minutes', event.target.value);
    if (update_overall_time) {
      this.props.race.overall_seconds = this.props.race.get_overall_time_calculated_from_stages();
    }
    this.props.updateRace(this.props.race);
  }

  change_duration_completed_seconds = (event) => {
    var update_overall_time = this.stages_match_overall_time();
    this.props.race_stage.completed_seconds = adjust_time_string(this.props.race_stage.completed_seconds, 'seconds', event.target.value);
    if (update_overall_time) {
      this.props.race.overall_seconds = this.props.race.get_overall_time_calculated_from_stages();
    }
    this.props.updateRace(this.props.race);
  }

  stages_match_overall_time = () => {
    var overall_time_calculated_from_stages = this.props.race.get_overall_time_calculated_from_stages();
    return (this.props.race.get_overall_time_seconds() == 0 || overall_time_calculated_from_stages == this.props.race.overall_seconds) ? true : false;
  }

  clean_up_stage_numbers = () => {
    // Set stage numbers so they are consistent with the order they appear
    // in the list. This shouldn't alter anything but fixes any problems if the
    // stage numbers are incorrect for any reason or there are duplicates.
    for (var index = 0; index < this.props.race.stages.length; index++) {
      this.props.race.stages[index].stage_number = index;
    }
  }

  deleteStageClicked = (event) => {
    activateConfirmationBox({
      message: 'Are you sure you want to delete this stage?',
      confirm_callback: this.confirmDeleteStageClicked,
    });
  }

  confirmDeleteStageClicked = (event) => {
    if (this.props.race_stage.id) {
      // Remove from database before removing from the DOM.
      var post_data = {'race_stage_id': this.props.race_stage.id};
      ajaxRequest({
        url: '/coach/delete-race-stage/',
        method: 'POST',
        data: post_data,
        success: this.race_stage_deleted,
        expected_response_type: 'json',
      })
    }
    else {
      // Not yet saved so just remove straight away.
      this.remove_race_stage(this.props.race_stage.id);
    }
  }

  race_stage_deleted = (response, status) => {
    this.remove_race_stage(response.race_stage.race_stage_id);

    for (var index=0; index < response.week_summaries.length; index++) {
      weekly_summary_json[response.week_summaries[index].week_key] = response.week_summaries[index];
    }
    xhale.widget.rendered_calendar.setState({weeks: weekly_summary_json});
  }

  remove_race_stage = (race_stage_id) => {
    var update_overall_time = this.stages_match_overall_time();
    for (var i = 0; i < this.props.race.stages.length; i++) {
      if (this.props.race.stages[i].id == race_stage_id) {
        this.props.race.stages.splice(i, 1);
        this.props.updateRace(this.props.race, false);
        break;
      }
    }
    if (update_overall_time) {
      this.props.race.overall_seconds = this.props.race.get_overall_time_calculated_from_stages();
      this.props.updateRace(this.props.race, true);
    }
  }

  move_up_clicked = (event) => {

    this.clean_up_stage_numbers();

    for (var i = 0; i < this.props.race.stages.length; i++) {
      if (this.props.race.stages[i].temporary_id == this.props.race_stage.temporary_id) {
        if (i > 0) {
          var previous_stage_number = this.props.race.stages[i-1].stage_number;
          var current_stage_number = this.props.race.stages[i].stage_number;
          this.props.race.stages[i].stage_number = previous_stage_number;
          this.props.race.stages[i-1].stage_number = current_stage_number;
          this.props.race.stages.sort(function (a, b) { return a.stage_number - b.stage_number });
          this.props.updateRace(this.props.race, true);
          return;
        }
      }
    }
  }

  move_down_clicked = (event) => {

    this.clean_up_stage_numbers();

    for (var i = 0; i < this.props.race.stages.length; i++) {
      if (this.props.race.stages[i].temporary_id == this.props.race_stage.temporary_id) {
        if (i < this.props.race.stages.length - 1) {
          var next_stage_number = this.props.race.stages[i+1].stage_number;
          var current_stage_number = this.props.race.stages[i].stage_number;
          this.props.race.stages[i].stage_number = next_stage_number;
          this.props.race.stages[i+1].stage_number = current_stage_number;
          this.props.race.stages.sort(function (a, b) { return a.stage_number - b.stage_number });
          this.props.updateRace(this.props.race, true);
          return;
        }
      }
    }
  }

  render() {

    var goal_time = this.props.race_stage.goal_seconds ? seconds_to_hours_minutes_seconds(this.props.race_stage.goal_seconds, true) : [null, null, null];
    var completed_time = this.props.race_stage.completed_seconds ? seconds_to_hours_minutes_seconds(this.props.race_stage.completed_seconds, true) : [null, null, null];

    if (this.props.race_stage.discipline_category() == 'swim') {
      if (this.props.race.getPoolUnit() == 'meters') {
        var distance_unit_suffix = <span className="unit">meters</span>;
        var advertised_distance = decimalPlaces((this.props.race_stage.advertised_distance_meters * 1000), 0, 'null', false, true);
        var actual_distance = decimalPlaces((this.props.race_stage.actual_distance_meters * 1000), 0, 'null', false, true);
      }
      else if (this.props.race.getPoolUnit() == 'yards') {
        var distance_unit_suffix = <span className="unit">yards</span>;
        var advertised_distance = decimalPlaces(xhale.helper.meters_to_yards((this.props.race_stage.advertised_distance_meters * 1000)), 0, 'null', false, true);
        var actual_distance = decimalPlaces(xhale.helper.meters_to_yards((this.props.race_stage.actual_distance_meters * 1000), 0, 'null'), 0, 'null', false, true);
      }
    }
    else {
      if (this.props.race.getDistanceUnit() == 'kilometers') {
        var distance_unit_suffix = <span className="unit">km</span>;
        var advertised_distance = decimalPlaces(this.props.race_stage.advertised_distance_meters, 3, 'null', false, true);
        var actual_distance = decimalPlaces(this.props.race_stage.actual_distance_meters, 3, 'null', false, true);
      }
      else if (this.props.race.getDistanceUnit() == 'miles') {
        var distance_unit_suffix = <span className="unit">miles</span>;
        var advertised_distance = decimalPlaces(xhale.helper.km_to_miles(this.props.race_stage.advertised_distance_meters), 3, 'null', false, true);
        var actual_distance = decimalPlaces(xhale.helper.km_to_miles(this.props.race_stage.actual_distance_meters), 3, 'null', false, true);
      }
    }

    var discipline_label;
    var advertised_distance_label;
    var duration_goal_label;
    var completed_distance_label;
    var duration_completed_label;
    if (this.props.columns == 1) {
      discipline_label = <label htmlFor={'id_form-' + this.props.form_index + '-discipline'}>Discipline</label>
      advertised_distance_label = <label htmlFor={'id_form-' + this.props.form_index + '-advertised_distance_meters'}>Advertised distance</label>;
      duration_goal_label = <label htmlFor={'id_form-' + this.props.form_index + '-duration_goal_0'}>Goal time</label>;
      completed_distance_label = <label htmlFor={'id_form-' + this.props.form_index + '-actual_distance_meters'}>Advertised distance</label>;
      duration_completed_label = <label htmlFor={'id_form-' + this.props.form_index + '-duration_completed_0'}>Time</label>;
    }

    var move_buttons;
    if (this.props.show_move_buttons) {
      move_buttons = (
        <td className="move-stages">
          <span className="move-up" onClick={this.move_up_clicked}>move up</span>
          <span className="move-down" onClick={this.move_down_clicked}>move down</span>
        </td>
      );
    }

    var delete_button;
    if (this.props.show_delete_buttons) {
      delete_button = (
        <td className="delete-stage">
          <span className="delete" onClick={this.deleteStageClicked}>delete</span>
        </td>
      );
    }

    var discipline_field = (
      <span className="field-wrapper">
        {'discipline' in this.props.stage_errors && <Errors errors={this.props.stage_errors.discipline} />}
        {'advertised_distance_km' in this.props.stage_errors && <Errors errors={this.props.stage_errors.advertised_distance_km} />}
        {'duration_goal_seconds' in this.props.stage_errors && <Errors errors={this.props.stage_errors.duration_goal_seconds} />}
        {'actual_distance_km' in this.props.stage_errors && <Errors errors={this.props.stage_errors.actual_distance_km} />}
        {'duration_completed_seconds' in this.props.stage_errors && <Errors errors={this.props.stage_errors.duration_completed_seconds} />}
        {discipline_label}
        <select name={'form-' + this.props.form_index + '-discipline'} id={'id_form-' + this.props.form_index + '-discipline'} value={this.props.race_stage.discipline || ""} onChange={this.change_discipline}>
          <option value="0">Choose</option>
          <optgroup label="Transition">
            <option value="1">Transition</option>
          </optgroup>
          <optgroup label="Swim">
            <option value="11">Pool swim</option>
            <option value="13">River swim</option>
            <option value="14">Lake swim</option>
            <option value="12">Sea swim</option>
            <option value="10">Other swim</option>
          </optgroup>
          <optgroup label="Bike">
            <option value="21">Road bike</option>
            <option value="22">Off-Road bike</option>
            <option value="20">Other bike</option>
          </optgroup>
          <optgroup label="Run">
            <option value="31">Road run</option>
            <option value="33">Cross country run</option>
            <option value="34">Trail run</option>;
            <option value="32">Track run</option>
            <option value="30">Other run</option>
          </optgroup>
          <optgroup label="Other">
            <option value="42">Kayak</option>
            <option value="41">Other</option>
          </optgroup>
        </select>
      </span>
    );

    var advertised_distance_field = (
      <span className="field-wrapper number">
        {advertised_distance_label}
        <input type="number" className="session-form__number-field" name={'form-' + this.props.form_index + '-advertised_distance_meters'} min="0" step="0.001" id={'id_form-' + this.props.form_index + '-advertised_distance_meters'} value={advertised_distance || ""} onChange={this.change_advertised_distance} />
      </span>
    );

    var goal_time_field = (
      <span className="field-wrapper number">
        {duration_goal_label}
        <input type="number" name={'form-' + this.props.form_index + '-duration_goal_0'} placeholder="00" min="0" max="167" className="session-form__interval-field interval-hours" id={'id_form-' + this.props.form_index + '-duration_goal_0'} value={goal_time[0] || ""} onChange={this.change_goal_time_hours} />
        <span className="interval-divider">:</span>
        <input type="number" name={'form-' + this.props.form_index + '-duration_goal_1'} placeholder="00" min="0" max="59" className="session-form__interval-field interval-minutes" id={'id_form-' + this.props.form_index + '-duration_goal_1'} value={goal_time[1] || ""} onChange={this.change_goal_time_minutes}  />
        <span className="interval-divider">:</span>
        <input type="number" name={'form-' + this.props.form_index + '-duration_goal_2'} placeholder="00" min="0" max="59" className="session-form__interval-field interval-seconds" id={'id_form-' + this.props.form_index + '-duration_goal_2'} value={goal_time[2] || ""} onChange={this.change_goal_time_seconds} />
      </span>
    );

    var completed_distance_field = (
      <span className="field-wrapper number">
        {completed_distance_label}
        <input type="number" className="session-form__number-field" name={'form-' + this.props.form_index + '-actual_distance_meters'} step="0.001" id={'id_form-' + this.props.form_index + '-actual_distance_meters'} min="0" value={actual_distance || ""} onChange={this.change_actual_distance} />
      </span>
    );

    var completed_time_field = (
      <span className="field-wrapper number">
        {duration_completed_label}
        <input type="number" name={'form-' + this.props.form_index + '-duration_completed_0'} placeholder="00" min="0" max="167" className="session-form__interval-field interval-hours" id={'id_form-' + this.props.form_index + '-duration_completed_0'} value={completed_time[0] || ""} onChange={this.change_duration_completed_hours} />
        <span className="interval-divider">:</span>
        <input type="number" name={'form-' + this.props.form_index + '-duration_completed_1'} placeholder="00" min="0" max="59" className="session-form__interval-field interval-minutes" id={'id_form-' + this.props.form_index + '-duration_completed_1'} value={completed_time[1] || ""} onChange={this.change_duration_completed_minutes} />
        <span className="interval-divider">:</span>
        <input type="number" name={'form-' + this.props.form_index + '-duration_completed_2'} placeholder="00" min="0" max="59" className="session-form__interval-field interval-seconds" id={'id_form-' + this.props.form_index + '-duration_completed_2'} value={completed_time[2] || ""} onChange={this.change_duration_completed_seconds} />
      </span>
    );

    if (this.props.columns == 1) {
      return (
        <tr className={'race-stage ' + this.props.race_stage.discipline_category()}>
          <td className="discipline">{discipline_field}</td>
          <td className="advertised-distance">{advertised_distance_field} {distance_unit_suffix}</td>
          <td className="goal-time" style={{whiteSpace: 'nowrap'}}>{goal_time_field}</td>
          <td className="completed-distance">{completed_distance_field} {distance_unit_suffix}</td>
          <td className="completed-time" style={{whiteSpace: 'nowrap'}}>{completed_time_field}</td>
          {move_buttons}
          {delete_button}
        </tr>
      );
    }
    else {
      return (
        <tr className={'race-stage ' + this.props.race_stage.discipline_category()}>
          <td className="discipline">{discipline_field}</td>
          <td className="advertised-distance">{advertised_distance_field}</td>
          <td className="advertised-distance-suffix">{distance_unit_suffix}</td>
          <td className="goal-time" style={{whiteSpace: 'nowrap'}}>{goal_time_field}</td>
          <td className="completed-distance">{completed_distance_field}</td>
          <td className="completed-distance-suffix">{distance_unit_suffix}</td>
          <td className="completed-time" style={{whiteSpace: 'nowrap'}}>{completed_time_field}</td>
          {move_buttons}
          {delete_button}
        </tr>
      );
    }
  }
}

class RaceStages extends React.Component {

  state = {
    show_move_buttons: false,
    show_delete_buttons: false
  }

  add_stage_clicked = (event) => {
    this.props.race.stages.push(new RaceStage({}));

    // Iterate through each stage and set up stage numbers so that the order
    // is kept consistent when loading from the database again.
    for (var index = 0; index < this.props.race.stages.length; index++) {
      this.props.race.stages[index].stage_number = index;
    }

    this.props.updateRace(this.props.race, true);
  }

  move_stages_clicked = (event) => {
    if (this.state.show_move_buttons) {
      this.setState({show_move_buttons: false});
    }
    else {
      // Hide the delete buttons when revealing the move buttons.
      this.setState({show_move_buttons: true, show_delete_buttons: false});
    }
  }

  delete_stages_clicked = (event) => {
    if (this.state.show_delete_buttons) {
      this.setState({show_delete_buttons: false});
    }
    else {
      // Hide the move buttons when revealing the delete buttons.
      this.setState({show_delete_buttons: true, show_move_buttons: false});
    }
  }

  change_overall_time_hours = (event) => {
    this.props.race.overall_seconds = adjust_time_string(this.props.race.overall_seconds, 'hours', event.target.value);
    this.props.updateRace(this.props.race);
  }

  change_overall_time_minutes = (event) => {
    this.props.race.overall_seconds = adjust_time_string(this.props.race.overall_seconds, 'minutes', event.target.value);
    this.props.updateRace(this.props.race);
  }

  change_overall_time_seconds = (event) => {
    this.props.race.overall_seconds = adjust_time_string(this.props.race.overall_seconds, 'seconds', event.target.value);
    this.props.updateRace(this.props.race);
  }

  componentDidMount = () => {
    this.added_training_log_ids = [];
    this.importAttachedData();
  }

  UNSAFE_componentWillUpdate = () => {
    this.importAttachedData();
  }

  selectRaceStageRow = (discipline, race, preceeding_disciplines) => {

    if (discipline == 'swimming') {
      var identifier = 'swim';
      var discipline_number = 10;
    }
    else if (discipline == 'cycling') {
      var identifier = 'bike';
      var discipline_number = 20;
    }
    else if (discipline == 'running') {
      var identifier = 'run';
      var discipline_number = 30;
    }
    else if (discipline == 'transition') {
      var identifier = 'transition';
      var discipline_number = 1;
    }
    else {
      var identifier = 'other';
      var discipline_number = 41;
    }

    // Look for an existing appropriate stage and return the stage array index if
    // a match is found and the match is empty.
    for (var index = 0; index < race.stages.length; index++) {
      var stage = race.stages[index];
      if ((!stage.discipline || identifier == stage.discipline_category()) && stage.has_empty_results()) {
        return index;
      }
    }

    // If an appropriate stage wasn't found add a new one to the end and
    // return the index for it (which will be the length of the array).
    var new_length = race.stages.push(new RaceStage({discipline: discipline_number, stage_number: race.stages.length}));
    var index = new_length - 1;
    return index;

  }

  /**
   * If there is data ready to import, update the session with that data.
   */
  importAttachedData = () => {

    // TODO See other comments in other component's importAttachedData methods.

    if (this.props.related_training_logs && this.props.related_training_logs.length > 0) {

      this.something_was_changed = false;

      // Record whether the current overall time matches the sum of all the
      // individual stages.
      var initial_overall_time_seconds = 0;
      for (var index = 0; index < this.props.race.stages.length; index++) {
        initial_overall_time_seconds += this.props.race.stages[index].completed_seconds;
      }
      var sum_of_stages_matches_overall_value = (initial_overall_time_seconds == this.props.race.get_overall_time_seconds()) ? true : false;

      var discipline_list = [];

      for (var i = 0; i < this.props.related_training_logs.length; i++) {

        var importing = (this.props.file_ids_marked_to_import.indexOf(this.props.related_training_logs[i].file_data.id) >= 0) ? true : false;
        var already_added_in_this_load = (this.added_training_log_ids.indexOf(this.props.related_training_logs[i].file_data.id) >= 0) ? true : false;
        var already_added_and_saved = (typeof(this.props.race.attached_training_logs) != 'undefined' && this.props.race.attached_training_logs.indexOf(this.props.related_training_logs[i].file_data.id) >= 0) ? true : false;
        var already_added = already_added_in_this_load || already_added_and_saved;

        var adding = importing && !already_added ? true : false;
        var removing = !importing && already_added_in_this_load ? true : false;

        var store_state = store.getState(); // TODO No longer a pure function. Pass this into the component through props instead.
        var related_training_log = store_state.main.uploaded_files[this.props.related_training_logs[i].file_data.id];

        if (adding && typeof(related_training_log.activities) != 'undefined') {

          this.added_training_log_ids.push(related_training_log.file_data.id);
          this.something_was_changed = true;

          $.each(related_training_log.activities, (activity_id, activity_data) => {

            // Pick the best row to fill in the details.
            var race_stage_index = this.selectRaceStageRow(activity_data.discipline, this.props.race, discipline_list);

            // Fill in the training log data.
            this.props.race.stages[race_stage_index].actual_distance_meters = decimalPlaces(activity_data.total_distance_km, 3, 'null', true);
            this.props.race.stages[race_stage_index].completed_seconds = Math.round(activity_data.total_time_seconds);
            this.props.race.stages[race_stage_index].training_log_id = related_training_log.file_data.id;

            // Record that we have already used this row.
            discipline_list.push(activity_data.discipline);

          });

        }

        if (removing) {

          this.something_was_changed = true;

          // Remove from the list of added training log ids.
          this.added_training_log_ids.splice(this.added_training_log_ids.indexOf(this.props.related_training_logs[i].file_data.id), 1);

          // Cycle through the list of stages and remove anything with the
          // this training log id. We iterate in reverse so that the indexing
          // is not affected when removing each item.
          for (var stage_index = this.props.race.stages.length - 1; stage_index >= 0; stage_index--) {
            if (typeof(this.props.race.stages[stage_index].training_log_id) != 'undefined' && this.props.race.stages[stage_index].training_log_id == this.props.related_training_logs[i].file_data.id) {
              this.props.race.stages.splice(stage_index, 1);
            }
          }

        }

      }

      if (this.something_was_changed) {

        // Update overall time if the overall time is still in line with the sum
        // of the stage times. If this is not the case, then the user has
        // overwritten the overall time value. There is a separate box for
        // recording overall time for cases where there isn't individual stage
        // times recorded. This is likely when there is no chip timing or the user
        // has only recorded one segment.
        if (sum_of_stages_matches_overall_value || this.props.race.get_overall_time_seconds() == 0) {
          var overall_time_seconds = 0;
          for (var index = 0; index < this.props.race.stages.length; index++) {
            overall_time_seconds += this.props.race.stages[index].completed_seconds;
          }
          this.props.race.overall_seconds = overall_time_seconds;
        }

        // Update race with new changes.
        this.props.updateRace(this.props.race);

      }

    }


  }

  render() {

    var race_stages = [];
    for (var index = 0; index < this.props.race.stages.length; index++) {
      var race_stage = this.props.race.stages[index];
      var stage_errors = {};
      if ('race_stages' in this.props.form_errors && race_stage.temporary_id in this.props.form_errors.race_stages) {
        stage_errors = this.props.form_errors.race_stages[race_stage.temporary_id];
      }
      race_stages.push(
        <TableRowTransition key={race_stage.temporary_id}>
          <RaceStageRow
            key={race_stage.temporary_id}
            form_index={index}
            race_stage={race_stage}
            race={this.props.race}
            updateRace={this.props.updateRace}
            columns ={this.props.columns}
            show_move_buttons={this.state.show_move_buttons}
            show_delete_buttons={this.state.show_delete_buttons}
            stage_errors={stage_errors}
          />
        </TableRowTransition>
      );
    }

    var overall_time = (this.props.race.overall_seconds) ? seconds_to_hours_minutes_seconds(this.props.race.overall_seconds, true) : 0;

    var overall_time_label = (<label htmlFor="id_overall_time_0">Overall time:</label>);
    var overall_time_field = (
      <span>
        <input type="number" name="overall_time_0" className="session-form__interval-field interval-hours" id="id_overall_time_0" value={overall_time[0] || ""} onChange={this.change_overall_time_hours} />
        <span className="interval-divider">:</span>
        <input type="number" name="overall_time_1" className="session-form__interval-field interval-minutes" id="id_overall_time_1" value={overall_time[1] || ""} onChange={this.change_overall_time_minutes} />
        <span className="interval-divider">:</span>
        <input type="number" name="overall_time_2" className="session-form__interval-field interval-seconds" id="id_overall_time_2" value={overall_time[2] || ""} onChange={this.change_overall_time_seconds} />
      </span>
    );
    var add_stage_button = <span className="add-stage small-button" onClick={this.add_stage_clicked}>Add a stage</span>;

    var move_stages_button;
    if (this.props.race.stages.length > 1) {
      move_stages_button = <span style={{marginLeft: '5px'}} className="move-stages-button small-button" onClick={this.move_stages_clicked}>Change order of stages</span>;
    }

    var delete_stages_button;
    if (this.props.race.stages.length > 0) {
      delete_stages_button = <span style={{marginLeft: '5px'}} className="delete-stages-button small-button" onClick={this.delete_stages_clicked}>Delete stages</span>;
    }

    if (this.props.columns == 1) {
      return (
        <div id="race-stages">
          <h3>Race results</h3>
          <table>
            <tbody>
              {race_stages}
            </tbody>
            <tfoot>
              <tr style={{background: '#eee'}}>
                <td>{add_stage_button}</td>
              </tr>
              <tr>
                <td>{overall_time_label}{overall_time_field}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }
    else {
      return (
        <div id="race-stages" className="results">
          <table>
            <thead>
              <tr>
                <th className="stage">Stage</th>
                <th className="advertised-distance" colSpan="2">Advertised distance</th>
                <th className="goal-time">Goal time</th>
                <th className="completed-distance" colSpan="2">Completed distance</th>
                <th className="completed-time">Time</th>
              </tr>
            </thead>
            <TransitionGroup component="tbody">
              {race_stages}
            </TransitionGroup>
            <tfoot>
              <tr>
                <td colSpan="4">{add_stage_button} {move_stages_button} {delete_stages_button}</td>
                <td colSpan="2" className="overall-time-label">{overall_time_label}</td>
                <td className="overall-time">{overall_time_field}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }
  }
}

export class Errors extends React.Component {
  render() {

    var style = {
      background: 'white',
      color: 'rgb(247, 63, 28)',
      padding: '7px',
      borderRadius: '4px',
      marginBottom: '5px',
      display: 'block',
      border: '2px solid rgb(247, 63, 28)',
    }
    var errors = [];
    for (var i = 0; i < this.props.errors.length; i++) {
      errors.push(<div key={i} className="error-item" style={style}>{this.props.errors[i]}</div>);
    }
    return <div className="error-list">{errors}</div>
  }
}

class SaveButton extends React.Component {
  render() {

    // Try to gather information about occassional missing save function.
    if (this.props.mode == 'training_session' && typeof(this.props.saveTrainingSessions) != 'function') {
      throw new Error('Missing property saveTrainingSessions in TrainingSessionForm');
    }

    if (typeof(this.props.currently_loading) != 'undefined' && this.props.currently_loading.length > 0) {
      var button_text = 'Loading data';
      var disabled = true;
    }
    else if (this.props.error_message) {
      var button_text = 'Did Not Save';
      if (this.props.changes_need_saving === false) {
        var disabled = true;
      }
    }
    else if (this.props.changes_need_saving === true) {
      var button_text = 'Save Changes';
      var disabled = false;
    }
    else if (this.props.currently_saving === true) {
      var button_text = 'Saving';
      var disabled = false;
    }
    else {
      var button_text = 'Saved';
      var disabled = true;
    }

    return (
      <input id="save-training-session" name="save-training-session" type="submit" className="standard-button" value={button_text} disabled={disabled} onClick={this.props.onClick} />
    )
  }
}

class MapComponent extends React.Component {

  componentDidMount = () => {

    if (typeof(mapbox_access_token) == 'undefined') {
      return;
    }

    const attribution = '<a href="https://www.mapbox.com/about/maps/" target="_blank">&copy; Mapbox &copy; OpenStreetMap</a> <a class="mapbox-improve-map" href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a>';

    const tile_layer = L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
          attribution: attribution,
          maxZoom: 18,
          id: 'mapbox/outdoors-v11',
          tileSize: 512,
          zoomOffset: -1,
          accessToken: mapbox_access_token,
        });

    // Initialise the map.
    xhale.widget[this.props.id] = new L.Map(this.props.id, { zoomControl: false });
    xhale.widget[this.props.id].attributionControl.options.prefix = '';
    xhale.widget[this.props.id].addLayer(tile_layer);
    xhale.widget[this.props.id].polyline_layer_group = L.featureGroup().addTo(xhale.widget[this.props.id]);

    this.componentDidUpdate();
  }

  shouldComponentUpdate = (nextProps, nextState) => {

    // Cycle through the new related training logs and look for changes to the
    // activities to which logs have activities that are being imported vs in
    // the existing props.
    var old_file_ids_with_activities_being_imported = [];
    var new_file_ids_with_activities_being_imported = [];
    for (var i = 0; i < this.props.related_training_logs.length; i++) {
      if (this.props.related_training_logs[i].importing && typeof(this.props.related_training_logs[i].activities) != 'undefined') {
        old_file_ids_with_activities_being_imported.push(this.props.related_training_logs[i].file_data.file_id);
      }
    }
    for (var i = 0; i < nextProps.related_training_logs.length; i++) {
      if (nextProps.related_training_logs[i].importing && typeof(nextProps.related_training_logs[i].activities) != 'undefined') {
        old_file_ids_with_activities_being_imported.push(nextProps.related_training_logs[i].file_data.file_id);
      }
    }

    // Rerender component if the activity data changed, the list of related
    // training logs being imported changed or we are now viewing
    // a race instead of a training session since the render on invisible
    // elements doesn't work.
    return (this.props.existing_activity_data != nextProps.existing_activity_data || this.props.mode != nextProps.mode || old_file_ids_with_activities_being_imported != new_file_ids_with_activities_being_imported);
  }

  collectActivityData = () => {

    var activity_data = [];

    $.each(this.props.existing_activity_data, function (activity_id, existing_activity_data) {
      activity_data.push(existing_activity_data);
    });

    for (var i = 0; i < this.props.related_training_logs.length; i++) {
      var importing = (this.props.file_ids_marked_to_import.indexOf(this.props.related_training_logs[i].file_data.id) >= 0) ? true : false;
      if (importing && typeof(this.props.related_training_logs[i].activities) != 'undefined') {
        $.each(this.props.related_training_logs[i].activities, function (activity_id, related_activity_data) {
          activity_data.push(related_activity_data);
        });
      }
    }

    return activity_data;
  }

  componentDidUpdate = () => {

    var activity_data = this.collectActivityData();

    // No activity data so don't do anything here. The map will just
    // hide itself as part of the render.
    if (activity_data.length == 0) {
      return;
    }

    // Select the appropriate map.
    var map = xhale.widget[this.props.id];

    // Clear existing data.
    map.polyline_layer_group.clearLayers();

    // Add activity data to map.
    for (var i = 0; i < activity_data.length; i++) {

      var coordinate_sets = []; // Used in case there is a break in the GPS trace and we need to start a new line.
      var coordinates = [];
      for (var coordinate_id in activity_data[i].trackpoints) {
        var coordinate = activity_data[i].trackpoints[coordinate_id];
        if (coordinate.latitude && coordinate.longitude) {
          coordinates.push([coordinate.latitude, coordinate.longitude]);
        }
        else if (coordinates.length > 0) {
          coordinate_sets.push(coordinates);
          coordinates = [];
        }
      }

      if (coordinates.length > 0) {
        coordinate_sets.push(coordinates);
      }

      if (coordinate_sets.length > 0) {
        switch (activity_data[i].discipline) {
          case 'swimming':
            var line_color = xhale.variables.colours.swim;
            break;
          case 'cycling':
            var line_color =  xhale.variables.colours.bike;
            break;
          case 'running':
            var line_color =  xhale.variables.colours.run;
            break;
          default:
            var line_color = 'black';
            break;
        }

        var polyline_options = { weight: 2, color: line_color, opacity: 1 }
        L.polyline(coordinate_sets, polyline_options).addTo(map.polyline_layer_group);
      }

    }

    var bounds = map.polyline_layer_group.getBounds();
    if (bounds.isValid()) {
      map.invalidateSize(); // Make sure Leaflet knows the correct size of the map now it is actually visible.
      map.fitBounds(bounds); // Zoom to the boundary of the data.
    }
  }

  render() {

    if (typeof(mapbox_access_token) == 'undefined') {
      return null;
    }

    // Only show the map at all if there is any data on it.

    var collected_activity_data = this.collectActivityData();
    var display = 'none';

    if (collected_activity_data.length > 0) {
      $.each(collected_activity_data, function (activity_id, activity_data) {
        if (activity_data.trackpoints && activity_data.trackpoints.length > 0) {
          // Cycle through each trackpoint looking for some latitude/longitude
          // data that could be displayed on the map.
          for (var i = 0; i < activity_data.trackpoints.length; i++) {
            if ('latitude' in activity_data.trackpoints[i] && activity_data.trackpoints[i].latitude) {
              display = 'block';
              return;
            }
          }
        }
      });
    }

    return (<div id={this.props.id} style={{ width: '100%', height: '196px', display: display}}></div>);
  }
}

export class DebugComponent extends React.Component {

  state = {
    draft_js: '',
    textarea: '',
  }

  handleChangeDraftJS = (value) => {
    this.setState({draft_js: value});
  }

  handleChangeTextarea = (event) => {
    this.setState({textarea: event.target.value});
  }

  handleCloseButtonClick = (event) => {
    this.props.closePopup();
  }

  render () {
    return <div>

      <div className="field-wrapper">
        <label htmlFor="draft_js_test" style={{display: 'block'}}>Draft</label>
        <ResizableTextareaDraftJS
          id="draft_js_test"
          initial_value={this.state.draft_js}
          onChange={this.handleChangeDraftJS}
          style={{minHeight: '100px', border: '1px solid black'}}
        />
      </div>

      <div className="field-wrapper" style={{marginTop: '10px'}}>
        <label htmlFor="text_area_test" style={{display: 'block'}}>Textarea controlled</label>
        <textarea
          name="text_area_test"
          rows="10"
          cols="40"
          id="text_area_test"
          value={this.state.textarea}
          onChange={this.handleChangeTextarea}>
        </textarea>
      </div>

      <div className="field-wrapper" style={{marginTop: '10px'}}>
        <label htmlFor="text_area_test" style={{display: 'block'}}>Textarea plain</label>
        <textarea
          name="text_area_test"
          rows="10"
          cols="40"
          id="text_area_test"
          >
        </textarea>
      </div>

      <div>
        <button onClick={this.handleCloseButtonClick}>close</button>
      </div>

    </div>
  }

}

export class TrainingSessionForm extends React.Component {

  state = {
    display_training_log_url: typeof(display_training_log_url) != 'undefined' ? display_training_log_url : false,
    discipline_widget_open: false,
    intensity_widget_open: false,
    error_message: null,
    unexpected_error: false,
    editing_comment_id: null,
  }

  componentDidCatch(error, info) {
    logError(error, info);
  }

  static getDerivedStateFromError(error) {
    return { unexpected_error: true };
  }

  formChanged = () => {
    this.props.sessionDataChanged();

    if (this.state.error_message) {
      this.setState({error_message: null});
    }
  }

  componentDidMount = () => {
    this.unsetMissedSessionIfImporting();
  }

  componentDidUpdate = () => {
    this.unsetMissedSessionIfImporting();
  }

  unsetMissedSessionIfImporting = () => {
    if (this.props.file_ids_marked_to_import &&
      this.props.file_ids_marked_to_import.length > 0 &&
      this.props.mode == 'training_session' &&
      this.props.training_session &&
      this.props.training_session.missed_session
    ) {
      this.props.training_session.missed_session = false;
      this.props.updateTrainingSession(this.props.training_session);
    }
  }

  submitForm = (event) => {

    event.preventDefault();

    // Remove existing error messages. This may be needed if there was a
    // saving issue so the submit button reactivates even though the user has
    // not altered the form again.
    if (this.state.error_message) {
      this.setState({error_message: null});
    }

    var data = {};
    let activity_plans_data = [];
    var attached_training_logs = [];

    if (this.props.mode == 'training_session') {

      var discipline_category = this.props.training_session.getDisciplineCategory();

      data.type = 'training_session';
      data.pool_unit = this.props.training_session.getPoolUnit();
      data.distance_unit = this.props.training_session.getDistanceUnit();
      data.order = this.props.training_session.order;
      data.uuid = this.props.training_session.uuid;

      data.comments = {};
      Object.keys(this.props.training_session.comments).forEach((comment_id) => {
        if (comment_id == 'new_comment' || this.props.training_session.comments[comment_id].updated == true) {
          data.comments[comment_id] = this.props.training_session.comments[comment_id];
        }
      });

      if (this.props.training_session.date) {
        data.date = this.props.training_session.date;
      }
      else if (this.props.training_session.training_plan_day) {
        data.training_plan_day = this.props.training_session.training_plan_day;
        data.training_plan_id = this.props.training_session.training_plan_id;
      }

      if (hasBetaAccess('google_calendar_connection') && this.props.training_session.time) {
        data.time = this.props.training_session.time;
        data.time_zone = this.props.training_session.time_zone ? this.props.training_session.time_zone : "Europe/London";
      }

      if (!this.props.training_session.has_permanent_id()) {
        // A new session.
        data.temporary_id = this.props.training_session.temporary_id;
      }
      else {
        // An existing session.
        data.id = this.props.training_session.id;
        attached_training_logs = this.props.training_session.attached_training_logs;
      }

      if (this.props.training_session.creator.type == this.props.training_session.CREATOR_GROUP) {
        data.group_parent_session_overridden = this.props.training_session.using_group_parent_session() ? 0 : 1;
      }

      if (hasSessionPlanEditPermission(this.props.training_session, this.props.athlete_data, this.props.group_data, this.props.coaching_companies) || this.props.perspective == 'coach') {
        data.discipline_id = this.props.training_session.discipline_id;
        data.other_discipline = this.props.training_session.other_discipline;
        data.subtitle = this.props.training_session.subtitle;
      }

      if (hasSessionPlanEditPermission(this.props.training_session, this.props.athlete_data, this.props.group_data, this.props.coaching_companies)) {
        data.intensity = this.props.training_session.intensity;
        data.diary_summary = this.props.training_session.diary_summary;

        data.prescribed_training_notes = this.props.training_session.prescribed_training_notes;
        data.attachment_ids = this.props.training_session.attachment_ids;

        data.prescribed_minutes = this.props.training_session.prescribed_minutes;
        data.prescribed_swim_minutes = this.props.training_session.prescribed_swim_minutes;
        data.prescribed_bike_minutes = this.props.training_session.prescribed_bike_minutes;
        data.prescribed_run_minutes = this.props.training_session.prescribed_run_minutes;

        if (discipline_category == 'swim') {
          if (this.props.training_session.getPoolUnit() == 'meters') {
            data.prescribed_distance = this.props.training_session.prescribed_meters;
          }
          else if (this.props.training_session.getPoolUnit() == 'yards') {
            data.prescribed_distance = this.props.training_session.prescribed_yards;
          }
        }
        else if (discipline_category == 'brick') {
          if (this.props.training_session.getPoolUnit() == 'meters') {
            data.prescribed_swim_distance = this.props.training_session.prescribed_swim_meters;
          }
          else if (this.props.training_session.getPoolUnit() == 'yards') {
            data.prescribed_swim_distance = this.props.training_session.prescribed_swim_yards;
          }
          if (this.props.training_session.getDistanceUnit() == 'kilometers') {
            data.prescribed_bike_distance = this.props.training_session.prescribed_bike_km;
            data.prescribed_run_distance = this.props.training_session.prescribed_run_km;
          }
          else if (this.props.training_session.getDistanceUnit() == 'miles') {
            data.prescribed_bike_distance = this.props.training_session.prescribed_bike_miles;
            data.prescribed_run_distance = this.props.training_session.prescribed_run_miles;
          }
        }
        else {
          if (this.props.training_session.getDistanceUnit() == 'kilometers') {
            data.prescribed_distance = this.props.training_session.prescribed_km;
          }
          else if (this.props.training_session.getDistanceUnit() == 'miles') {
            data.prescribed_distance = this.props.training_session.prescribed_miles;
          }
        }

      }

      if (!(this.props.athlete_data?.preferences && this.props.athlete_data?.preferences.hide_rate_of_perceived_exertion == 'y')) {
        data.rpe = this.props.training_session.rpe;
      }

      if (typeof(this.props.activity_plans) == 'object') {
        Object.keys(this.props.activity_plans).forEach((key) => {
          let activity_plan = this.props.activity_plans[key];
          if (activity_plan.training_session_ids.indexOf(this.props.training_session.id) >= 0) {
            activity_plans_data.push(activity_plan);
          }
        });
      }

      if (typeof(this.props.training_session.activity_plan_uuids_to_add) != 'undefined') {
        data.activity_plan_uuids_to_add = this.props.training_session.activity_plan_uuids_to_add;
      }

      if (typeof(this.props.training_session.activity_plan_uuids_to_remove) != 'undefined') {
        data.activity_plan_uuids_to_remove = this.props.training_session.activity_plan_uuids_to_remove;
      }

      data.completed_minutes = this.props.training_session.completed_minutes;
      data.completed_swim_minutes = this.props.training_session.completed_swim_minutes;
      data.completed_bike_minutes = this.props.training_session.completed_bike_minutes;
      data.completed_run_minutes = this.props.training_session.completed_run_minutes;

      if (discipline_category == 'bike' || discipline_category == 'brick') {
        data.completed_bike_average_watts = this.props.training_session.completed_bike_average_watts;
        data.completed_bike_normalized_watts = this.props.training_session.completed_bike_normalized_watts;
        data.completed_bike_average_heart_rate_bpm = this.props.training_session.completed_bike_average_heart_rate_bpm;
        data.completed_bike_average_cadence_rpm = this.props.training_session.completed_bike_average_cadence_rpm;
      }

      if (discipline_category == 'run' || discipline_category == 'brick') {
        data.completed_run_average_heart_rate_bpm = this.props.training_session.completed_run_average_heart_rate_bpm;
      }

      if (discipline_category == 'swim') {
        if (this.props.training_session.getPoolUnit() == 'meters') {
          data.completed_distance = this.props.training_session.completed_meters;
        }
        else if (this.props.training_session.getPoolUnit() == 'yards') {
          data.completed_distance = this.props.training_session.completed_yards;
        }
      }
      else if (discipline_category == 'brick') {
        if (this.props.training_session.getPoolUnit() == 'meters') {
          data.completed_swim_distance = this.props.training_session.completed_swim_meters;
        }
        else if (this.props.training_session.getPoolUnit() == 'yards') {
          data.completed_swim_distance = this.props.training_session.completed_swim_yards;
        }
        if (this.props.training_session.getDistanceUnit() == 'kilometers') {
          data.completed_bike_distance = this.props.training_session.completed_bike_km;
          data.completed_run_distance = this.props.training_session.completed_run_km;
          data.completed_bike_height_climbed = this.props.training_session.completed_bike_meters_climbed;
          data.completed_run_height_climbed = this.props.training_session.completed_run_meters_climbed;
          data.completed_bike_average_speed = this.props.training_session.completed_bike_average_km_hr;
        }
        else if (this.props.training_session.getDistanceUnit() == 'miles') {
          data.completed_bike_distance = this.props.training_session.completed_bike_miles;
          data.completed_run_distance = this.props.training_session.completed_run_miles;
          data.completed_bike_height_climbed = this.props.training_session.completed_bike_feet_climbed;
          data.completed_run_height_climbed = this.props.training_session.completed_run_feet_climbed;
          data.completed_bike_average_speed = this.props.training_session.completed_bike_average_miles_hr;
        }
      }
      else {
        if (this.props.training_session.getDistanceUnit() == 'kilometers') {
          data.completed_distance = this.props.training_session.completed_km;
          if (discipline_category == 'bike') {
            data.completed_bike_height_climbed = this.props.training_session.completed_bike_meters_climbed;
            data.completed_bike_average_speed = this.props.training_session.completed_bike_average_km_hr;
          }
          if (discipline_category == 'run') {
            data.completed_run_height_climbed = this.props.training_session.completed_run_meters_climbed;
          }
        }
        else if (this.props.training_session.getDistanceUnit() == 'miles') {
          data.completed_distance = this.props.training_session.completed_miles;
          if (discipline_category == 'bike') {
            data.completed_bike_height_climbed = this.props.training_session.completed_bike_feet_climbed;
            data.completed_bike_average_speed = this.props.training_session.completed_bike_average_miles_hr;
          }
          if (discipline_category == 'run') {
            data.completed_run_height_climbed = this.props.training_session.completed_run_feet_climbed;
          }
        }
      }

      // This can only be saved by a coach.
      if (this.props.perspective == 'coach') {
        data.coach_comments = this.props.training_session.coaches_comments;
        data.key_session = this.props.training_session.key_session;

        // Template related.
        data.template = this.props.training_session.template;
        data.template_folder = this.props.training_session.template_folder_id;
        data.template_sharing = this.props.training_session.template_sharing;

        // Sponsors are set for groups only.
        if (this.props.group_data && this.props.training_session.sponsor) {
          data.sponsor = this.props.training_session.sponsor;
        }

        if (this.props.training_session.tags_user) {
          data.tags_user = this.props.training_session.tags_user;
        }

      }

      // This can only be saved by an athlete.
      if (this.props.perspective == 'athlete') {

        data.athlete_feedback = this.props.training_session.athlete_feedback;
        data.missed_session = this.props.training_session.missed_session;

        data.training_log_url = this.props.training_session.training_log_url;

      }

    }
    else if (this.props.mode == 'race') {

      data.type = 'race';
      data.discipline = 16;

      data.id = this.props.race.id;
      data.uuid = this.props.race.uuid;

      if (this.props.race.date) {
        data.date = this.props.race.getDateAsString();
      }
      else if (this.props.race.training_plan_day) {
        data.training_plan_day = this.props.race.training_plan_day;
      }
      else if (this.props.training_session.training_plan_day) {
        data.training_plan_day = this.props.training_session.training_plan_day;
        data.training_plan_id = this.props.training_session.training_plan_id;
      }

      if (!this.props.race.id || (this.props.training_session && !this.props.training_session?.has_permanent_id())) {

        // A new race.
        data.temporary_id = this.props.training_session.temporary_id;

      }
      else {
        // An existing race.
        attached_training_logs = this.props.race.attached_training_logs;
      }

      data.race_name = this.props.race.race_name;
      data.race_type = this.props.race.race_type;
      data.race_priority = this.props.race.race_priority;
      data.taper_days = this.props.race.taper_days;
      data.attendance_probability = this.props.race.attendance_probability;
      data.race_url = this.props.race.race_url;
      if (this.props.perspective == 'athlete') {
        data.athlete_race_plan = this.props.race.athlete_plan;
        data.athlete_race_feedback = this.props.race.athlete_feedback;
        data.position_age_group = this.props.race.position_age_group;
        data.position_overall = this.props.race.position_overall;
        data.overall_time_seconds = this.props.race.overall_seconds;
      }
      else if (this.props.perspective == 'coach') {
        data.coach_race_plan = this.props.race.coach_plan;
        data.coaches_comments = this.props.race.coach_feedback;
      }

      if (!(this.props.athlete_data?.preferences && this.props.athlete_data?.preferences.hide_rate_of_perceived_exertion == 'y')) {
        data.rpe = this.props.race.rpe;
      }

      // Race stages.
      data.race_stages = [];
      for (var i = 0; i < this.props.race.stages.length; i++) {
        var stage = this.props.race.stages[i];
        var stage_data = {};
        stage_data.stage_id = stage.id;
        stage_data.temporary_id = stage.temporary_id;
        stage_data.stage_number = stage.stage_number;
        stage_data.discipline = stage.discipline;
        stage_data.advertised_distance_km = stage.advertised_distance_meters;
        stage_data.duration_goal_seconds = stage.goal_seconds
        stage_data.actual_distance_km = stage.actual_distance_meters;
        stage_data.duration_completed_seconds = stage.completed_seconds;
        data.race_stages.push(stage_data);
      }

    }

    // If we are importing training data, include that here and then remove
    // further references to it so it won't be included when opening up
    // other sessions.
    if (this.props.related_training_logs) {
      data.training_log_file_ids = [];
      for (var i = 0; i < this.props.related_training_logs.length; i++) {
        var training_log = this.props.related_training_logs[i];
        var importing = (this.props.file_ids_marked_to_import.indexOf(training_log.file_data.id) >= 0) ? true : false;
        var already_attached = (attached_training_logs.indexOf(training_log.file_data.id) >= 0) ? true : false;
        if (importing && !isNaN(training_log.file_data.id) && !already_attached) {
          data.training_log_file_ids.push(training_log.file_data.id);
        }
      }
    }

    if (typeof(xhale) != 'undefined') {
      delete(xhale.global_state.importing_training_log_file_id);
    }
    if (typeof(window.history.pushState) == 'function') {
      var url = removeURLParameter(window.location.search, 'import-training-log')
      history.pushState(null, '', url + window.location.hash);
    }

    if (this.props.athlete_data) {
      data.athlete_id = this.props.athlete_data.id;
    }
    else if (this.props.group_data) {
      data.group_id = this.props.group_data.id;
    }
    else if (this.props.training_plan) {
      data.training_plan_id = this.props.training_plan.id;
    }

    if (this.props.mode == 'training_session') {
      data.source_id = 'training_session_form_04072008'
      this.props.saveTrainingSessions(
        data,
        activity_plans_data,
        this.handleFormSaved,
        [handleLoggedOutError, this.handleAjaxErrors]
      );
    }
    else if (this.props.mode == 'race') {
      saveRaces(data, this.handleFormSaved, [handleLoggedOutError, this.handleAjaxErrors]);
    }

    this.props.savingStarted();

    // Send tracking to Google Analytics.
    if (typeof(ga) == 'function') {
      ga('send', 'event', 'form', 'submit', 'training session saved');
    }

  }

  handleAjaxErrors = (response, status) => {
    handleAjaxErrors(response, status, this.displayAjaxErrorMessage);
  }

  displayAjaxErrorMessage = (error_message, reactivate_save_button) => {
    if (reactivate_save_button) {
      this.props.sessionDataChanged();
    }
    this.setState({error_message: error_message});
  }

  handleFormSaved = (data, status) => {

    // TODO Can I bring some of the logic out of the athlete calendar save
    // function and put it into here for when it affects only the contents of
    // the currently displaying training session.

    if (typeof(data) == 'string') {
      data = $.parseJSON(data);
    }

    if ('errors' in data) {

      if ('all' in data.errors) {
        this.setState({error_message: data.errors.all[0], currently_saving: false});
      }
      else {
        this.setState({error_message: 'There was a problem saving. Please fix the problems above.', currently_saving: false});
      }

    }
    else {
      this.setState({
        editing_comment_id: null,
      });
    }

    this.props.form_saved_callback(data, status);
  }

  setEditingComment = (comment_id) => {
    this.setState({editing_comment_id: comment_id});
  }

  deleteSessionClicked = (event) => {
    var delete_confirmation_message = (this.props.mode == 'race') ? 'Are you sure you want to delete this race?' : 'Are you sure you want to delete this training session?';
    activateConfirmationBox({
      message: delete_confirmation_message,
      confirm_callback: this.confirmDeleteClicked,
    });
  }

  confirmDeleteClicked = () => {
    if (this.props.mode == 'training_session' && this.props.training_session.has_permanent_id()) {
      this.props.deleteTrainingSession(this.props.training_session.id, this.props.form_saved_callback, handleLoggedOutError);
      ga('send', 'event', 'link', 'click', 'athlete delete training session', this.props.training_session.id);
    }
    else if (this.props.mode == 'race' && this.props.race.id)  {
      var post_data = { 'race_id': this.props.race.id};
      ajaxRequest({
        url: '/coach/delete-race/',
        method: 'POST',
        data: post_data,
        success: [this.props.form_saved_callback],
        error: handleLoggedOutError,
        expected_response_type: 'json',
      });
      ga('send', 'event', 'link', 'click', 'athlete delete race', this.props.race.id);
    }
    this.props.closeTrainingSessionForm();
  }

  keep_session_open_button_pressed = () => {
    xhale.helper.setCookie('keep-open-training-session', this.props.training_session.id);
    this.props.activate_persistent_sidebar();
  }

  has_delete_permission = () => {
    return this.props.training_session.hasDeletePermission(xhale_meta_data, this.props.athlete_data, this.props.group_data);
  }

  getTrainingMapDisplayCSS = () => {
    // Should map display
    if (this.props.mode == 'training_session') {
      if (this.props.training_session.activity_data) {
        return 'block';
      }
      else {
        for (var i = 0; i < this.props.related_training_logs.length; i++) {
          var importing = (this.props.file_ids_marked_to_import.indexOf(this.props.related_training_logs[i].file_data.id) >= 0) ? true : false;
          if (importing && typeof(this.props.related_training_logs[i].activities) != 'undefined') {
            return 'block';
          }
        }
      }
    }
    return 'none';
  }

  renderTrainingSession = () => {
    const { has_edit_permission, edit_permission_reason } = sessionPlanEditPermission(
      this.props.training_session,
      this.props.athlete_data,
      this.props.group_data,
      this.props.coaching_companies
    );

    if (this.state.unexpected_error) {
      return <div style={{margin: '15px', lineHeight: '1.3em'}}>Sorry, there was a problem rendering this section due to a bug. If this problem continues please let us know so we can fix it.</div>;
    }

    let training_plan_fields;
    var session_fields_middle;
    var session_fields_right;
    var race_fields_left;
    var race_fields_middle;
    var race_fields_right;

      training_plan_fields = (
        <TrainingPlanFields
          training_session={this.props.training_session}
          content_attachments={this.props.content_attachments}
          content_attachments_meta_data={this.props.content_attachments_meta_data}
          athlete_data={this.props.athlete_data}
          current_user_id={this.props.current_user_id}
          group_data={this.props.group_data || null}
          coaching_companies={this.props.coaching_companies}
          perspective={this.props.perspective}
          updateTrainingSession={this.props.updateTrainingSession}
          openStructuredWorkout={this.props.openStructuredWorkout}
          form_errors={this.props.form_errors}
          coaching_company_name={this.props.coaching_company_name || null}
          columns={this.props.columns}
          mode={this.props.mode}
          activity_plans={this.props.activity_plans}
          formChanged={this.formChanged}
          openMediaAttachmentSelector={this.props.openMediaAttachmentSelector}
        />
      );

      if (
        (this.props.training_session.has_permanent_id() || this.props.perspective == 'athlete') &&
        !this.props.training_session.template &&
        typeof(this.props.athlete_data) == 'object' &&
        !this.props.group_data &&
        !this.props.training_plan
      ) {

        session_fields_middle = (
          <CompletedTrainingFields
            athlete_data={this.props.athlete_data}
            training_session={this.props.training_session}
            perspective={this.props.perspective}
            updateTrainingSession={this.props.updateTrainingSession}
            form_errors={this.props.form_errors}
            related_training_logs={this.props.related_training_logs}
            file_ids_marked_to_import={this.props.file_ids_marked_to_import}
          />
        );

        session_fields_right = (
          <ExtraDetails
            training_session={this.props.training_session}
            session_collections={this.props.session_collections}
            related_training_logs={this.props.related_training_logs}
            perspective={this.props.perspective}
            columns={this.props.columns}
            mode={this.props.mode}
            updateTrainingSession={this.props.updateTrainingSession}
            toggleImporting={this.props.toggleImporting}
            form_errors={this.props.form_errors}
            display_training_log_url={this.state.display_training_log_url}
            display_more_details_link={this.props.display_more_details_link}
            tag_clicked_callback={this.props.tag_clicked_callback}
            handleSelectTrainingSession={this.props.handleSelectTrainingSession}
            open_collection={this.props.open_collection}
            selected_sessions={this.props.selected_sessions}
            filtered_by_tags={this.props.filtered_by_tags}
            file_ids_marked_to_import={this.props.file_ids_marked_to_import}
            display_tags={this.props.display_tags}
            openSavedSelectionCallback={this.props.openSavedSelectionCallback}
            include_map={this.props.include_map}
            athlete_data={this.props.athlete_data}
            formChanged={this.formChanged}
          />
        );

      }

      var discipline_category = this.props.training_session.getDisciplineCategory();
      var form_classes = 'session-form';
      form_classes += ' discipline-' + discipline_category;

      if (this.props.training_session.intensity && discipline_category != 'rest' && discipline_category != 'diary') {
        form_classes += ' intensity-' + this.props.training_session.intensity + ' session-form--intensity-' + this.props.training_session.intensity;
      }
      else {
        form_classes += ' intensity-none session-form--intensity-none';
      }

    if (this.props.columns == 1) {
      form_classes += ' columns-1';
    }
    else {
      form_classes += ' columns-3';
    }

    var intensity_widget;
    // Widget is only shown for training sessions when in mobile mode or in
    // desktop mode when it is marked as open.
    if (
      (this.props.columns == 1 && this.props.training_session.is_training_session()) ||
      (this.props.columns > 1  && (this.props.training_session.is_training_session() || !this.props.training_session.discipline_id) && this.state.intensity_widget_open)
    ) {
      intensity_widget = (
       <FadeTransition>
          <IntensityWidget
            prefix={this.props.prefix}
            training_session_form={this}
            training_session={this.props.training_session}
            training_zones={this.props.athlete_data ? this.props.athlete_data.training_zones : null}
            coaching_companies={this.props.coaching_companies}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
            updateTrainingSession={this.props.updateTrainingSession}
            group_data={this.props.group_data}
            athlete_data={this.props.athlete_data}
          />
       </FadeTransition>
      );
    }

    var schedule_widget;
    // Widget is only shown for training sessions when in mobile mode or in
    // desktop mode when it is marked as open.
    var session_is_template = this.props.mode == 'training_session' && this.props.training_session.template ? true : false;
    if (!session_is_template && (this.props.columns == 1 || this.state.schedule_widget_open)) {
      schedule_widget = (
        <FadeTransition>
          <ScheduleWidget
            markers_on_this_date={this.props.markers_on_this_date}
            updateSessionOrder={this.props.updateSessionOrder}
            mode={this.props.mode}
            prefix={this.props.prefix}
            training_session_form={this}
            training_session={this.props.training_session}
            race={this.props.race}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
            updateTrainingSession={this.props.updateTrainingSession}
            updateRace={this.props.updateRace}
            perspective={this.props.perspective}
          />
        </FadeTransition>
      );
    }

    // Allow all races and athlete and group created training sessions to be deleted by the athlete.
    var delete_link;
    var is_deletable_training_session = (this.props.mode == 'training_session' && this.has_delete_permission());
    var is_deletable_race = (this.props.mode == 'race' && this.props.race.has_permanent_id()) || is_deletable_training_session;

    if (this.props.closeTrainingSessionForm && this.props.deleteTrainingSession && (is_deletable_training_session || is_deletable_race)) {
     delete_link = (
        <div id="delete-session" className="delete-button">
          <span onClick={this.deleteSessionClicked}>delete</span>
        </div>
      );
    }

    var discipline_widget;
    var dropdown_widget;
    if (false && hasBetaAccess('new_training_session_form_dropdown') && this.state.discipline_widget_open == true && this.props.columns == 3) {
      // This is an experiment for the new discipline widget. It is completely disabled for now as it won't currently work.
      dropdown_widget = (
        <FadeTransition>
          <DropDown
            training_session_form={this}
            related_training_logs={this.props.related_training_logs}
            training_session={this.props.training_session}
            mode={this.props.mode}
            updateTrainingSession={this.props.updateTrainingSession}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
            file_ids_marked_to_import={this.props.file_ids_marked_to_import} />
        </FadeTransition>
      );
    }
    else if (this.state.discipline_widget_open == true && this.props.columns == 3) {
      discipline_widget = (
        <FadeTransition>
          <DisciplineWidget
            training_session_form={this}
            related_training_logs={this.props.related_training_logs}
            training_session={this.props.training_session}
            mode={this.props.mode}
            updateTrainingSession={this.props.updateTrainingSession}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
            file_ids_marked_to_import={this.props.file_ids_marked_to_import}
            intensity_widget_open={this.state.intensity_widget_open}
          />
        </FadeTransition>
      );
    }

    var keep_session_open_button;
    if (this.props.activate_persistent_sidebar && this.props.perspective == 'coach' && this.props.mode == 'training_session' && this.props.training_session.has_permanent_id() && hasBetaAccess('keep_open_training_forms')) {
      keep_session_open_button = (
        <div className="keep-open" style={{textDecoration: 'underline', cursor: 'pointer'}} onClick={this.keep_session_open_button_pressed}>click to keep open</div>
      );
    }

    var comments;
    if (
      this.props.mode == 'training_session' &&
      typeof(this.props.athlete_data) == 'object' &&
      !this.props.group_data &&
      !this.props.training_plan &&
      !this.props.training_session.template &&
      (this.props.training_session.has_permanent_id() || this.props.perspective == 'athlete')
    ) {
      var comments = (
        <Comments
          perspective={this.props.perspective}
          columns={this.props.columns}
          training_session={this.props.training_session}
          content_attachments={this.props.content_attachments}
          updateTrainingSession={this.props.updateTrainingSession}
          form_errors={this.props.form_errors}
          athlete_data={this.props.athlete_data}
          current_user_id={this.props.current_user_id}
          editing_comment_id={this.state.editing_comment_id}
          setEditingComment={this.setEditingComment}
          openMediaAttachmentSelector={this.props.openMediaAttachmentSelector}
        />
      );
    }

    var error_message;
    if (this.state.error_message) {

      var message_style = {
        background: '#f73f1c',
        color: 'white',
        padding: '5px',
        position: 'absolute',
        bottom: '43px',
        right: '5px',
        left: '5px',
        borderRadius: '4px',
        boxShadow: '0px 0px 12px -5px black'
      }

      var arrow_style = {
        width: '0',
        height: '0',
        borderStyle: 'solid',
        borderWidth: '15px 12px 0 12px',
        borderColor: '#f73f1c transparent transparent transparent',
        position: 'absolute',
      }

      if (this.props.columns == 3) {
        message_style.maxWidth = '500px';
        message_style.margin = 'auto';
        message_style.bottom = '48px';
        arrow_style.left = '50%';
        arrow_style.marginLeft = '-12px';
      }

      error_message = (
        <div className="error-message" style={message_style}>
          {this.state.error_message}
          <div className="error-message-arrow" style={arrow_style}></div>
        </div>
      );
    }

    var form_bottom_padding = '0px';
    var bottom_bar_styles = {};
    if (this.props.columns == 1 && !(this.props.mobile_display == 'inline')) {

      var width = (this.props.mobile_mode) ? '100%' : '377px';

      var bottom_bar_styles = {
        width: width,
        bottom: '0px',
        position: 'fixed',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box',
        minHeight: '34px',
        zIndex: 2,
      }

      form_bottom_padding = '36px';
    }

    let bottom_bar;
    if (!has_edit_permission && edit_permission_reason == 'template_created_by_another_user') {
      bottom_bar_styles.padding = '9px 5px';
      bottom_bar = (
        <div className="button-wrapper no-save-button-message" style={bottom_bar_styles}>This template was shared by {this.props.training_session.creator.creator_name}</div>
      );
    }
    else {
      bottom_bar = (
        <div className="button-wrapper" style={bottom_bar_styles}>
          {error_message}
          <SaveButton
            changes_need_saving={this.props.changes_need_saving}
            currently_saving={this.props.currently_saving}
            currently_loading={this.props.currently_loading}
            error_message={this.state.error_message}
            onClick={this.submitForm}
            mode={this.props.mode}
            saveTrainingSessions={this.props.saveTrainingSessions}
          />
          {delete_link}
        </div>
      );
    }

    var race_map_container;
    if (this.props.mode == 'race' && this.props.include_map) {
      race_map_container = (
        <div id="training-map-container" style={{ display: this.getTrainingMapDisplayCSS() }}>
          <MapComponent id='training-map' existing_activity_data={this.props.training_session.activity_data} mode={this.props.mode} related_training_logs={this.props.related_training_logs} file_ids_marked_to_import={this.props.file_ids_marked_to_import} />
        </div>
      );
    }

    let structured_training_editor;
    if (this.props.perspective == 'athlete' && this.props.open_activity_plan_uuid) {
      structured_training_editor = (
        <StructuredWorkoutEditor
          athlete_data={this.props.athlete_data}
          perspective={this.props.perspective}
          custom_zones={this.props.custom_zones}
          training_session={this.props.training_session}
          discipline_id={this.props.training_session.discipline_id}
          activity_plan={this.props.activity_plans[this.props.open_activity_plan_uuid]}
          workout_sections={this.props.workout_sections}
          workout_sections_loaded={this.props.workout_sections_loaded}
          fetchWorkoutSections={this.props.fetchWorkoutSections}
          setWorkoutSections={this.props.setWorkoutSections}
          updateTrainingSession={this.props.updateTrainingSession}
          closeOverlay={this.props.closeStructuredWorkout}
          formChanged={this.formChanged}
        />
      );
    }

    if (this.props.columns == 1) {

      return (
        <form id="training-session-form" onChange={this.formChanged} className={form_classes} style={{paddingBottom: form_bottom_padding, minHeight: '100%', boxSizing: 'border-box'}}>

          {keep_session_open_button}
          {schedule_widget}

          <TitleSection
            training_session_form={this}
            perspective={this.props.perspective}
            training_session={this.props.training_session}
            athlete_data={this.props.athlete_data}
            coaching_companies={this.props.coaching_companies}
            group_data={this.props.group_data}
            updateRace={this.props.updateRace}
            updateTrainingSession={this.props.updateTrainingSession}
            race={this.props.race}
            mode={this.props.mode}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
          />

          {discipline_widget}
          {intensity_widget}

          <div className="training-session-details">
            {'__all__' in this.props.form_errors && <Errors errors={this.props.form_errors.__all__} />}
            {training_plan_fields}
            {session_fields_middle}
            {session_fields_right}
            {race_map_container}
            {comments}
          </div>

          {bottom_bar}

        </form>
      )

    }
    else {

      var title_section = (
        <TitleSectionV2
          training_session_form={this}
          perspective={this.props.perspective}
          training_session={this.props.training_session}
          coaching_companies={this.props.coaching_companies}
          updateSessionOrder={this.props.updateSessionOrder}
          markers_on_this_date={this.props.markers_on_this_date}
          athlete_data={this.props.athlete_data}
          updateRace={this.props.updateRace}
          updateTrainingSession={this.props.updateTrainingSession}
          race={this.props.race}
          mode={this.props.mode}
          columns={this.props.columns}
          form_errors={this.props.form_errors}
        />
      );

      return (
        <form id="training-session-form" onChange={this.formChanged} className={form_classes}>

          {title_section}

          <TransitionGroup>{dropdown_widget ? dropdown_widget : null}</TransitionGroup>
          <TransitionGroup>{discipline_widget ? discipline_widget : null}</TransitionGroup>
          <TransitionGroup>{intensity_widget ? intensity_widget : null}</TransitionGroup>
          <TransitionGroup>{schedule_widget ? schedule_widget: null}</TransitionGroup>

          <div className="training-session-details">
            {'__all__' in this.props.form_errors && <Errors errors={this.props.form_errors.__all__} />}
            <div className="column-left">{training_plan_fields}</div>
            <div className="column-middle">{session_fields_middle}{comments}</div>
            <div className="column-right">
              {session_fields_right}
              {race_map_container}
            </div>
          </div>
          {structured_training_editor}
          {bottom_bar}
        </form>
      )
    }

  }

  renderRace = () => {

    if (this.state.unexpected_error) {
      return <div style={{margin: '15px', lineHeight: '1.3em'}}>Sorry, there was a problem rendering this section due to a bug. If this problem continues please let us know so we can fix it.</div>;
    }

    var race_fields_left;
    var race_fields_middle;
    var race_fields_right;

    race_fields_left = (<RaceFieldsLeft form_errors={this.props.form_errors} race={this.props.race} updateRace={this.props.updateRace} perspective={this.props.perspective} />);
    race_fields_middle = (<RaceFieldsMiddle form_errors={this.props.form_errors} is_group={this.props.group_data ? true : false} race={this.props.race} updateRace={this.props.updateRace} perspective={this.props.perspective} />);
    if ((this.props.race.has_permanent_id() || this.props.perspective == 'athlete') && !this.props.group_data) {
      race_fields_right = (
        <RaceFieldsRight
          athlete_data={this.props.athlete_data}
          form_errors={this.props.form_errors}
          race={this.props.race}
          updateRace={this.props.updateRace}
          perspective={this.props.perspective}
          columns={this.props.columns}
          display_more_details_link={this.props.display_more_details_link}
        />
      );
    }

    var race_map_display = (this.props.mode == 'race') ? 'block' : 'none';
    var form_classes = 'session-form race intensity-racing';

    if (this.props.columns == 1) {
      form_classes += ' columns-1';
    }
    else {
      form_classes += ' columns-3';
    }

    var race_results;
    if (this.props.columns == 3 || this.props.perspective == 'athlete') {

      var related_training_logs_widget;
      var related_training_logs = [];
      if (this.props.related_training_logs && this.props.related_training_logs.length > 0) {
        for (var i = 0; i < this.props.related_training_logs.length; i++) {
          var related_training_log = this.props.related_training_logs[i];
          var importing = (this.props.file_ids_marked_to_import.indexOf(related_training_log.file_data.id) >= 0) ? true : false;
          related_training_logs.push(
            <ImportingDataMessage
              key={related_training_log.file_data.id}
              training_file={related_training_log}
              toggleImporting={this.props.toggleImporting}
              importing={importing}
              attached_training_logs={this.props.race.attached_training_logs}
            />
          );
        }

        related_training_logs_widget = (
          <div className="related-training-logs-widget">
            <h5>Check the activities you would like to include in this race</h5>
            {related_training_logs}
          </div>
        );

      }

      var race_stages = (
        <RaceStages
          race={this.props.race}
          updateRace={this.props.updateRace}
          columns={this.props.columns}
          form_errors={this.props.form_errors}
          related_training_logs={this.props.related_training_logs}
          file_ids_marked_to_import={this.props.file_ids_marked_to_import}
        />
      );

      let map_component;
      if (this.props.include_map) {
        map_component = (
          <div id="race-map-container" style={{ display: race_map_display }}>
            <MapComponent id='race-map' existing_activity_data={this.props.race.activity_data} related_training_logs={this.props.related_training_logs} mode={this.props.mode} file_ids_marked_to_import={this.props.file_ids_marked_to_import} />
          </div>
        );
      }

      race_results = (
        <div className="race-bottom">
          {race_stages}
          <div className="right-column">
            {related_training_logs_widget}
            {map_component}
          </div>
        </div>
      );
    }

    var schedule_widget;
    // Widget is only shown for training sessions when in mobile mode or in
    // desktop mode when it is marked as open.
    var session_is_template = this.props.mode == 'training_session' && this.props.training_session.template ? true : false;
    if (!session_is_template && (this.props.columns == 1 || this.state.schedule_widget_open)) {
      schedule_widget = (
        <FadeTransition>
          <ScheduleWidget
            markers_on_this_date={this.props.markers_on_this_date}
            updateSessionOrder={this.props.updateSessionOrder}
            mode={this.props.mode}
            prefix={this.props.prefix}
            training_session_form={this}
            training_session={this.props.training_session}
            race={this.props.race}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
            updateTrainingSession={this.props.updateTrainingSession}
            updateRace={this.props.updateRace}
            perspective={this.props.perspective}
          />
        </FadeTransition>
      );
    }

    // Allow all races and athlete and group created training sessions to be deleted by the athlete.
    var delete_link;
    var is_deletable_training_session = (this.props.mode == 'training_session' && this.has_delete_permission());
    var is_deletable_race = (this.props.mode == 'race' && this.props.race.has_permanent_id()) || is_deletable_training_session;

    if (this.props.closeTrainingSessionForm && this.props.deleteTrainingSession && (is_deletable_training_session || is_deletable_race)) {
     delete_link = (
        <div id="delete-session" className="delete-button">
          <span onClick={this.deleteSessionClicked}>delete</span>
        </div>
      );
    }

    var discipline_widget;
    var dropdown_widget;
    if (this.state.discipline_widget_open == true && this.props.columns == 3) {
      discipline_widget = (
        <FadeTransition>
          <DisciplineWidget
            training_session_form={this}
            related_training_logs={this.props.related_training_logs}
            training_session={this.props.training_session}
            mode={this.props.mode}
            updateTrainingSession={this.props.updateTrainingSession}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
            file_ids_marked_to_import={this.props.file_ids_marked_to_import}
            intensity_widget_open={this.state.intensity_widget_open}
          />
        </FadeTransition>
      );
    }

    var keep_session_open_button;

    var error_message;
    if (this.state.error_message) {

      var message_style = {
        background: '#f73f1c',
        color: 'white',
        padding: '5px',
        position: 'absolute',
        bottom: '43px',
        right: '5px',
        left: '5px',
        borderRadius: '4px',
        boxShadow: '0px 0px 12px -5px black'
      }

      var arrow_style = {
        width: '0',
        height: '0',
        borderStyle: 'solid',
        borderWidth: '15px 12px 0 12px',
        borderColor: '#f73f1c transparent transparent transparent',
        position: 'absolute',
      }

      if (this.props.columns == 3) {
        message_style.maxWidth = '500px';
        message_style.margin = 'auto';
        message_style.bottom = '48px';
        arrow_style.left = '50%';
        arrow_style.marginLeft = '-12px';
      }

      error_message = (
        <div className="error-message" style={message_style}>
          {this.state.error_message}
          <div className="error-message-arrow" style={arrow_style}></div>
        </div>
      );
    }

    var form_bottom_padding = '0px';
    var bottom_bar_styles = {};
    if (this.props.columns == 1 && !(this.props.mobile_display == 'inline')) {

      var width = (this.props.mobile_mode) ? '100%' : '377px';

      var bottom_bar_styles = {
        width: width,
        bottom: '0px',
        position: 'fixed',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box',
        minHeight: '34px',
        zIndex: 2,
      }

      form_bottom_padding = '36px';
    }

    let bottom_bar = (
      <div className="button-wrapper" style={bottom_bar_styles}>
        {error_message}
        <SaveButton
          changes_need_saving={this.props.changes_need_saving}
          currently_saving={this.props.currently_saving}
          currently_loading={this.props.currently_loading}
          error_message={this.state.error_message}
          onClick={this.submitForm}
          mode={this.props.mode}
          saveTrainingSessions={this.props.saveTrainingSessions}
        />
        {delete_link}
      </div>
    );

    var race_map_container;
    if (this.props.mode == 'race' && this.props.include_map) {
      race_map_container = (
        <div id="training-map-container" style={{ display: this.getTrainingMapDisplayCSS() }}>
          <MapComponent id='training-map' existing_activity_data={this.props.training_session.activity_data} mode={this.props.mode} related_training_logs={this.props.related_training_logs} file_ids_marked_to_import={this.props.file_ids_marked_to_import} />
        </div>
      );
    }

    let structured_training_editor;
    if (this.props.perspective == 'athlete' && this.props.open_activity_plan_uuid) {
      structured_training_editor = (
        <StructuredWorkoutEditor
          athlete_data={this.props.athlete_data}
          perspective={this.props.perspective}
          custom_zones={this.props.custom_zones}
          training_session={this.props.training_session}
          discipline_id={this.props.training_session.discipline_id}
          activity_plan={this.props.activity_plans[this.props.open_activity_plan_uuid]}
          workout_sections={this.props.workout_sections}
          workout_sections_loaded={this.props.workout_sections_loaded}
          fetchWorkoutSections={this.props.fetchWorkoutSections}
          setWorkoutSections={this.props.setWorkoutSections}
          updateTrainingSession={this.props.updateTrainingSession}
          closeOverlay={this.props.closeStructuredWorkout}
          formChanged={this.formChanged}
        />
      );
    }

    if (this.props.columns == 1) {

      return (
        <form id="training-session-form" onChange={this.formChanged} className={form_classes} style={{paddingBottom: form_bottom_padding, minHeight: '100%', boxSizing: 'border-box'}}>

          {keep_session_open_button}
          {schedule_widget}

          <TitleSection
            training_session_form={this}
            perspective={this.props.perspective}
            training_session={this.props.training_session}
            athlete_data={this.props.athlete_data}
            coaching_companies={this.props.coaching_companies}
            group_data={this.props.group_data}
            updateRace={this.props.updateRace}
            updateTrainingSession={this.props.updateTrainingSession}
            race={this.props.race}
            mode={this.props.mode}
            columns={this.props.columns}
            form_errors={this.props.form_errors}
          />

          {discipline_widget}

          <div className="training-session-details">
            {'__all__' in this.props.form_errors && <Errors errors={this.props.form_errors.__all__} />}
            {race_fields_left}
            {race_fields_middle}
            {race_fields_right}
            {race_map_container}
            {race_results}
          </div>

          {bottom_bar}

        </form>
      )

    }
    else {

      var title_section = (
        <TitleSectionV2
          training_session_form={this}
          perspective={this.props.perspective}
          training_session={this.props.training_session}
          coaching_companies={this.props.coaching_companies}
          updateSessionOrder={this.props.updateSessionOrder}
          markers_on_this_date={this.props.markers_on_this_date}
          athlete_data={this.props.athlete_data}
          updateRace={this.props.updateRace}
          updateTrainingSession={this.props.updateTrainingSession}
          race={this.props.race}
          mode={this.props.mode}
          columns={this.props.columns}
          form_errors={this.props.form_errors}
        />
      );

      return (
        <form id="training-session-form" onChange={this.formChanged} className={form_classes}>

          {title_section}

          <TransitionGroup>{dropdown_widget ? dropdown_widget : null}</TransitionGroup>
          <TransitionGroup>{discipline_widget ? discipline_widget : null}</TransitionGroup>
          <TransitionGroup>{schedule_widget ? schedule_widget: null}</TransitionGroup>

          <div className="training-session-details">
            {'__all__' in this.props.form_errors && <Errors errors={this.props.form_errors.__all__} />}
            <div className="column-left">{race_fields_left}</div>
            <div className="column-middle">{race_fields_middle}</div>
            <div className="column-right">
              {race_fields_right}
              {race_map_container}
            </div>
            {race_results}
          </div>
          {structured_training_editor}
          {bottom_bar}
        </form>
      )
    }

  }

  render = () => {
    if (this.props.mode == 'training_session') {
      return this.renderTrainingSession();
    }
    else if (this.props.mode == 'race') {
      return this.renderRace();
    }
  }
}

const ensureOneOfAthleteGroupOrTrainingPlan = (props, propName, componentName) => {
  let props_found = 0;
  if (props.athlete_data) {
    props_found += 1;
  }
  if (props.group_data) {
    props_found += 1;
  }
  if (props.training_plan) {
    props_found += 1;
  }
  if (props_found != 1) {
    return new Error(`One only of athlete_data, group_data or training_plan must be provided as a prop.`);
  }
}

const validateTemplateFieldsOnly = (props, propName, componentName) => {
  if (props.template && this.props.training_plan) {
    return new Error(`Templates should not set training plan.`);
  }
}

TrainingSessionForm.propTypes = {
  athlete_data: ensureOneOfAthleteGroupOrTrainingPlan,
  group_data: ensureOneOfAthleteGroupOrTrainingPlan,
  training_plan: ensureOneOfAthleteGroupOrTrainingPlan,
  template: validateTemplateFieldsOnly,
  current_user_id: PropTypes.number.isRequired,
};
